import React, { useState, useEffect, useRef } from 'react';
import { Plus, X, Calendar, Clock, Edit2, Trash2, Save, ArrowRight, ThumbsUp, ThumbsDown, Bookmark, Share2, List, Play, Pause, SkipBack, SkipForward, Loader, Sparkles, Settings, BarChart3, Search, Filter, Lock, Unlock, MicOff, Mic, RefreshCw, Grip, Bell, BellOff } from 'lucide-react';
import JournalStorageService from './services/JournalStorageService';
import { 
  sendChatMessage, 
  parseAzureResponse, 
  sendToolResult,
  getStoredUserName,
  convertMessagesToAzureFormat 
} from './Azurechatbot';
import { Capacitor } from '@capacitor/core';
import { useLocation, useNavigate } from 'react-router-dom';
import JournalTabs from './JournalTabs';

import { 
  updateJournalNotifications, 
  requestJournalNotificationPermission,
  areJournalNotificationsEnabled,
  disableJournalNotifications,
  enableJournalNotifications
} from './journalNotifications';
import Confetti from 'react-confetti';


const compressImage = async (file, maxWidth = 1200) => {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) {
      reject(new Error('File is not an image'));
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        
        if (width > maxWidth) {
          height *= maxWidth / width;
          width = maxWidth;
        }
        
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob(
          (blob) => {
            const compressedFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });
            
            const compressedReader = new FileReader();
            compressedReader.onloadend = () => resolve(compressedReader.result);
            compressedReader.onerror = reject;
            compressedReader.readAsDataURL(compressedFile);
          },
          'image/jpeg',
          0.8
        );
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target.result;
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};
const useSpeechRecognition = () => {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [isSupported, setIsSupported] = useState(false);
  const recognitionRef = useRef(null);

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      console.log('Speech recognition not supported');
      setIsSupported(false);
      return;
    }

    setIsSupported(true);
    recognitionRef.current = new SpeechRecognition();
    recognitionRef.current.continuous = true;
    recognitionRef.current.interimResults = true;
    recognitionRef.current.lang = 'en-US';

    recognitionRef.current.onresult = (event) => {
      let interimTranscript = '';
      let finalTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript + ' ';
        } else {
          interimTranscript += transcript;
        }
      }

      setTranscript(prev => prev + finalTranscript + interimTranscript);
    };

    recognitionRef.current.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
    };

    recognitionRef.current.onend = () => {
      setIsListening(false);
    };

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  const startListening = () => {
    if (recognitionRef.current && !isListening && isSupported) {
      try {
        recognitionRef.current.start();
        setIsListening(true);
      } catch (error) {
        console.error('Error starting recognition:', error);
      }
    }
  };

  const stopListening = () => {
    if (recognitionRef.current && isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    }
  };

  const resetTranscript = () => {
    setTranscript('');
  };

  return { isListening, transcript, startListening, stopListening, resetTranscript, isSupported };
};
const CalendarHeatmap = ({ entries }) => {
  const [heatmapData, setHeatmapData] = useState([]);

  useEffect(() => {
    if (!entries || entries.length === 0) return;

    const today = new Date();
    const oneYearAgo = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 364);
    
    const dateMap = {};
    entries.forEach(entry => {
      const date = new Date(entry.createdAt || entry.date);
      const dateKey = date.toISOString().split('T')[0];
      dateMap[dateKey] = (dateMap[dateKey] || 0) + 1;
    });

    const data = [];
    for (let d = new Date(oneYearAgo); d <= today; d.setDate(d.getDate() + 1)) {
      const dateKey = new Date(d).toISOString().split('T')[0];
      data.push({
        date: dateKey,
        count: dateMap[dateKey] || 0
      });
    }

    setHeatmapData(data);
  }, [entries]);

  const getColor = (count) => {
    if (count === 0) return '#e5e7eb';
    if (count === 1) return '#c7d2fe';
    if (count === 2) return '#a5b4fc';
    if (count >= 3) return '#818cf8';
    return '#6366f1';
  };

  const weeks = [];
  for (let i = 0; i < heatmapData.length; i += 7) {
    weeks.push(heatmapData.slice(i, i + 7));
  }

  return (
    <div style={{ display: 'flex', gap: '2px', overflowX: 'auto', paddingBottom: '8px' }}>
      {weeks.map((week, weekIdx) => (
        <div key={weekIdx} style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {week.map((day, dayIdx) => (
            <div
              key={dayIdx}
              title={`${day.date}: ${day.count} ${day.count === 1 ? 'entry' : 'entries'}`}
              style={{
                width: '10px',
                height: '10px',
                backgroundColor: getColor(day.count),
                borderRadius: '2px',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.transform = 'scale(1.3)'}
              onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
            />
          ))}
        </div>
      ))}
    </div>
  );
};

// ============= CUSTOMIZABLE CARD SECTION COMPONENT =============
const CustomizableCardSection = ({ 
  entries, 
  timeStats, 
  moodStats,
  currentStreak  
}) => {
  const availableCards = {
    peakTime: { id: 'peakTime', type: 'stat', title: 'Peak Writing Time' },
    moodTracker: { id: 'moodTracker', type: 'stat', title: 'Mood Tracker' },
    streak: { id: 'streak', type: 'stat', title: 'Writing Streak' }
  };

  const getInitialLayout = () => {
    try {
      const saved = localStorage.getItem('journalCardLayout');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && Array.isArray(parsed.statCards)) {
          return { statCards: parsed.statCards, hiddenCards: parsed.hiddenCards || [] };
        }
      }
    } catch (error) {
      console.error('Error loading card layout:', error);
    }
    
    return {
      statCards: ['streak', 'peakTime'],
      hiddenCards: ['moodTracker']
    };
  };

  const [layout, setLayout] = useState(getInitialLayout());
  const [isCustomizing, setIsCustomizing] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [shakingCard, setShakingCard] = useState(null);
  const [longPressTimer, setLongPressTimer] = useState(null);
  const [draggedCard, setDraggedCard] = useState(null);
  const [dragOverCard, setDragOverCard] = useState(null);

  // Calculate current streak
  const sortedDates = entries.map(e => e.date).sort((a, b) => b - a);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768 || 'ontouchstart' in window);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Save layout to localStorage
  useEffect(() => {
    localStorage.setItem('journalCardLayout', JSON.stringify(layout));
  }, [layout]);

  // Handle long press to enter customization mode
  const handleLongPressStart = (cardId, e) => {
    e.preventDefault();
    
    const timer = setTimeout(() => {
      setShakingCard(cardId);
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
      
      setTimeout(() => {
        setShakingCard(null);
        setIsCustomizing(true);
      }, 300);
    }, 500);
    
    setLongPressTimer(timer);
  };

  const handleLongPressEnd = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
    setShakingCard(null);
  };

  const handleLongPressCancel = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
    setShakingCard(null);
  };

// Hide a card
const hideCard = (cardId) => {
  const newLayout = { ...layout };
  
  if (layout.statCards.includes(cardId)) {
    newLayout.statCards = newLayout.statCards.filter(id => id !== cardId);
  }
  
  if (!newLayout.hiddenCards.includes(cardId)) {
    newLayout.hiddenCards.push(cardId);
  }
  
  setLayout(newLayout);
};

  // Show a hidden card
  const showCard = (cardId) => {
    const newLayout = { ...layout };
    
    newLayout.hiddenCards = newLayout.hiddenCards.filter(id => id !== cardId);
    
    if (newLayout.statCards.length < 2) {
      newLayout.statCards.push(cardId);
    } else {
      const removed = newLayout.statCards.pop();
      newLayout.hiddenCards.push(removed);
      newLayout.statCards.push(cardId);
    }
    
    setLayout(newLayout);
  };

  // Drag and drop handlers
  const handleDragStart = (cardId) => {
    setDraggedCard(cardId);
  };

  const handleDragOver = (e, cardId) => {
    e.preventDefault();
    setDragOverCard(cardId);
  };

  const handleDrop = (e, targetCardId) => {
    e.preventDefault();
    
    if (!draggedCard || draggedCard === targetCardId) {
      setDraggedCard(null);
      setDragOverCard(null);
      return;
    }

    const newLayout = { ...layout };
    const sourceIdx = newLayout.statCards.indexOf(draggedCard);
    const targetIdx = newLayout.statCards.indexOf(targetCardId);
    
    [newLayout.statCards[sourceIdx], newLayout.statCards[targetIdx]] = 
    [newLayout.statCards[targetIdx], newLayout.statCards[sourceIdx]];

    setLayout(newLayout);
    setDraggedCard(null);
    setDragOverCard(null);
  };

  const handleDragEnd = () => {
    setDraggedCard(null);
    setDragOverCard(null);
  };

const exitCustomization = () => {
  // ✅ CHECK: Must have at least 2 cards visible before exiting
  if (layout.statCards.length < 2) {
    alert('You must have at least 2 cards displayed on your dashboard.');
    return;
  }
  
  setIsCustomizing(false);
};
  // Render individual cards
  const renderCard = (cardId, index = null) => {
    const card = availableCards[cardId];
    if (!card) return null;
    
    const isShaking = shakingCard === cardId;
    const isDragging = draggedCard === cardId;
    const isDragOver = dragOverCard === cardId && draggedCard !== cardId;

    const cardStyle = {
      position: 'relative',
      animation: isShaking ? 'cardShakeSingle 0.3s ease' : 'none',
      opacity: isDragging ? 0.5 : 1,
      transform: isDragOver ? 'scale(0.95)' : 'scale(1)',
      transition: 'transform 0.2s ease, opacity 0.2s ease',
      cursor: isCustomizing ? 'move' : 'default'
    };

    const touchHandlers = {
      onTouchStart: (e) => handleLongPressStart(cardId, e),
      onTouchEnd: handleLongPressEnd,
      onTouchMove: handleLongPressCancel,
    };

    const dragHandlers = isCustomizing ? {
      draggable: true,
      onDragStart: () => handleDragStart(cardId),
      onDragOver: (e) => handleDragOver(e, cardId),
      onDrop: (e) => handleDrop(e, cardId),
      onDragEnd: handleDragEnd
    } : {};

    const MinusButton = () => isCustomizing ? (
      <button
        onClick={(e) => {
          e.stopPropagation();
          hideCard(cardId);
        }}
        style={{
          position: 'absolute',
          top: '8px',
          right: '8px',
          zIndex: 10,
          width: '28px',
          height: '28px',
          borderRadius: '50%',
          background: '#ef4444',
          border: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)',
          transition: 'all 0.2s ease'
        }}
        onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round">
          <line x1="5" y1="12" x2="19" y2="12"></line>
        </svg>
      </button>
    ) : null;

    // STREAK CARD
    if (cardId === 'streak') {
      return (
        <div 
          key={`${cardId}-${index}`} 
          className="stat-card"
          style={cardStyle}
          {...touchHandlers}
          {...dragHandlers}
        >
          <MinusButton />
          <div className="stat-card-gradient" style={{ background: 'linear-gradient(135deg, #fecaca 0%, #fca5a5 100%)' }} />
          <div className="stat-card-content">
            <div className="stat-card-inner">
              <div className="stat-title">Writing Streak</div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: '12px' }}>
                <svg width="33" height="33" viewBox="0 0 24 24" fill="#ef4444" stroke="#dc2626" strokeWidth="2">
                  <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/>
                </svg>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '27px', fontWeight: '600', marginTop:'-10px', color: '#dc2626', lineHeight: 1 }}>
                    {currentStreak}
                  </div>
                  <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                    Days in a row
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // PEAK TIME CARD
    if (cardId === 'peakTime') {
      return (
        <div 
          key={`${cardId}-${index}`} 
          className="stat-card"
          style={cardStyle}
          {...touchHandlers}
          {...dragHandlers}
        >
          <MinusButton />
          <div className="stat-card-gradient" style={{ background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)' }} />
          <div className="stat-card-content">
            <div className="stat-card-inner">
              <div className="stat-title">Peak Writing Time</div>
              <div className="time-stat">
                <div className="time-label">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2">
                    <circle cx="12" cy="12" r="5"></circle>
                    <line x1="12" y1="1" x2="12" y2="3"></line>
                    <line x1="12" y1="21" x2="12" y2="23"></line>
                    <line x1="1" y1="12" x2="3" y2="12"></line>
                    <line x1="21" y1="12" x2="23" y2="12"></line>
                    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
                    <line x1="5.64" y1="5.64" x2="4.22" y2="4.22"></line>
                    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
                    <line x1="5.64" y1="18.36" x2="4.22" y2="19.78"></line>
                  </svg>
                  <span>Morning</span>
                </div>
                <span className="time-percentage">{timeStats.morning}%</span>
              </div>
              <div className="time-stat">
                <div className="time-label">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2">
                    <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"></path>
                  </svg>
                  <span>Afternoon</span>
                </div>
                <span className="time-percentage">{timeStats.afternoon}%</span>
              </div>
              <div className="time-stat">
                <div className="time-label">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2">
                    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
                  </svg>
                  <span>Evening</span>
                </div>
                <span className="time-percentage">{timeStats.evening}%</span>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // MOOD TRACKER CARD
    if (cardId === 'moodTracker') {
      return (
        <div 
          key={`${cardId}-${index}`} 
          className="stat-card"
          style={cardStyle}
          {...touchHandlers}
          {...dragHandlers}
        >
          <MinusButton />
          <div className="stat-card-gradient" style={{ background: 'linear-gradient(135deg, #ffffffff 0%, rgba(157, 196, 255, 1) 100%)' }} />
          <div className="stat-card-content">
            <div className="stat-card-inner">
              <div className="stat-title" style={{ marginBottom: '4px' }}>Mood Tracker</div>
              <div className="emotion-chart-container" style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'flex-end', height: '92px', gap: '8px' }}>
                {moodStats.map((mood, idx) => (
                  <div key={idx} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                    <div style={{ fontSize: '10px', fontWeight: '600', color: '#1f2937', marginBottom: '6px' }}>{mood.percentage}%</div>
                    <div style={{ background: mood.color, height: `${Math.max(mood.percentage * 0.6, 10)}px`, width: '50%', borderRadius: '7px 7px 0 0' }}></div>
                    <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '8px' }}>{mood.name}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

// Hidden Cards Panel
const HiddenCardsPanel = () => {
  if (!isCustomizing || layout.hiddenCards.length === 0) return null;

  return (
    <div style={{
      marginBottom: '16px',
      padding: '12px 16px',
      background: 'rgba(249, 250, 251, 0.8)',
      backdropFilter: 'blur(10px)',
      borderRadius: '16px',
      border: '1.5px dashed rgba(209, 213, 219, 0.6)'
    }}>
      <div style={{
        fontSize: '11px',
        fontWeight: '600',
        color: '#6b7280',
        marginBottom: '10px',
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
      }}>
        Hidden Cards
      </div>
      
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
        gap: '8px'
      }}>
        {layout.hiddenCards.map((cardId) => {
          // ✅ FIX: Get card info safely
          const card = availableCards[cardId];
          if (!card) return null;
          
          return (
            <button
              key={cardId}
              onClick={() => showCard(cardId)}
              style={{
                background: '#fff',
                border: '1.5px solid rgba(209, 213, 219, 0.6)',
                borderRadius: '12px',
                padding: '10px 8px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                textAlign: 'center'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#000';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(209, 213, 219, 0.6)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <div style={{
                fontSize: '11px',
                fontWeight: '600',
                color: '#1f2937',
                marginBottom: '4px',
                lineHeight: '1.2'
              }}>
                {card.title}
              </div>
              <div style={{
                fontSize: '9px',
                color: '#9ca3af'
              }}>
                Tap to show
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};
  return (
    <div>
      <style>{`
        @keyframes cardShakeSingle {
          0%, 100% { transform: translateX(0) rotate(0deg); }
          25% { transform: translateX(-4px) rotate(-2deg); }
          50% { transform: translateX(4px) rotate(2deg); }
          75% { transform: translateX(-4px) rotate(-2deg); }
        }

        .stat-card {
          user-select: none;
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
        }
      `}</style>

      {/* Customization Mode Header */}
      {isCustomizing && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          background: 'rgba(0, 0, 0, 0.95)',
          backdropFilter: 'blur(20px)',
          padding: '16px 20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
          animation: 'slideDown 0.3s ease'
        }}>
          <div>
            <div style={{ 
              fontSize: '16px', 
              fontWeight: '600', 
              color: '#fff',
              marginTop: '24px',
              marginBottom: '4px'
            }}>
              Customize Dashboard
            </div>
            <div style={{ 
              fontSize: '12px', 
              marginTop: '4px',
              color: 'rgba(255, 255, 255, 0.7)'
            }}>
              Drag to reorder • Tap minus to hide
            </div>
          </div>
          <button
            onClick={exitCustomization}
            style={{
              background: '#fff',
              color: '#000',
              border: 'none',
              borderRadius: '12px',
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              marginTop: '24px',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            Done
          </button>
        </div>
      )}

      {/* Overlay when customizing */}
      {isCustomizing && (
        <div style={{
          position: 'fixed',
          inset: 0,
          zIndex: 9,
          pointerEvents: 'none',
          animation: 'fadeIn 0.3s ease'
        }} />
      )}

      {/* Stats Grid */}
      <div style={{ 
        position: 'relative', 
        minHeight: '140px', 
        marginBottom: '20px',
        marginTop: isCustomizing ? '130px' : '0',
        transition: 'margin-top 0.3s ease'
      }}>
        {/* Hidden Cards Panel */}
        {isCustomizing && (
          <div style={{ 
            position: 'absolute',
            top: '-120px',
            left: 0,
            right: 0,
            zIndex: 5
          }}>
            <HiddenCardsPanel />
          </div>
        )}

        <div className="stats-grid">
          {layout.statCards.map((cardId, index) => (
            <React.Fragment key={`stat-${cardId}-${index}`}>
              {renderCard(cardId, index)}
            </React.Fragment>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideDown {
          from { transform: translateY(-100%); }
          to { transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};
export default function JournalApp({ firestoreService }) {
const [journalStorage] = useState(() => new JournalStorageService());
const [displayStreak, setDisplayStreak] = useState(0);
const [showSaveSuccess, setShowSaveSuccess] = useState(false);

const tagColors = [
  { bg: '#fef3c7', color: '#92400e' },      // Yellow
  { bg: '#dbeafe', color: '#1e40af' },      // Blue
  { bg: '#d1fae5', color: '#065f46' }       // Green
];
 const [entries, setEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filteredEntries, setFilteredEntries] = useState([]);
const [showSingleEntry, setShowSingleEntry] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  // Search & Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [filterMood, setFilterMood] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
const [activeJournalTab, setActiveJournalTab] = useState('latest'); // 'latest' or 'discover'
const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
const [entryToDelete, setEntryToDelete] = useState(null);
const [showEmergencyReset, setShowEmergencyReset] = useState(false);
const [isSavingChanges, setIsSavingChanges] = useState(false);
const [showSuccessCheck, setShowSuccessCheck] = useState(false);
const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    // PIN lock states
    const [showPinSetup, setShowPinSetup] = useState(false);
const [showResetPin, setShowResetPin] = useState(false);
const [resetPinStep, setResetPinStep] = useState(1); // 1: verify old, 2: enter new, 3: confirm new
const [oldPinInput, setOldPinInput] = useState('');
const [newPinInput, setNewPinInput] = useState('');
const [confirmPinInput, setConfirmPinInput] = useState('');
  const [appPin, setAppPin] = useState(() => localStorage.getItem('journal_app_pin') || '');
  const [lockedEntries, setLockedEntries] = useState(() => {
    const stored = localStorage.getItem('journal_locked_entries');
    return stored ? JSON.parse(stored) : [];
  });
  const [pinInput, setPinInput] = useState('');
  const [showPinModal, setShowPinModal] = useState(false);
  const [currentLockAction, setCurrentLockAction] = useState(null);  
  // Speech recognition
const { isListening, transcript, startListening, stopListening, resetTranscript, isSupported } = useSpeechRecognition();  
  // Reflection audio states
  const [isGeneratingReflection, setIsGeneratingReflection] = useState(false);
  const [isPlayingReflection, setIsPlayingReflection] = useState(false);
  const [reflectionProgress, setReflectionProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [generationStage, setGenerationStage] = useState('');
  const audioRef = useRef(null);
const [userName, setUserName] = useState(null); 
const [showStats, setShowStats] = useState(false);
const [notificationUserName, setNotificationUserName] = useState('there');
const [editTitle, setEditTitle] = useState('');
const [editContent, setEditContent] = useState('');
const [editMood, setEditMood] = useState('neutral');
const [editingEntry, setEditingEntry] = useState(null);
const [selectedImages, setSelectedImages] = useState([]);

  const [reflectionAudio, setReflectionAudio] = useState(null);

  const [currentAffirmationIndex, setCurrentAffirmationIndex] = useState(0);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewAllModal, setShowViewAllModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [showWritingForm, setShowWritingForm] = useState(false);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
const [isExpanded, setIsExpanded] = useState(false);
const [isEntryExpanded, setIsEntryExpanded] = useState(false);
const [filteredStoredEntries, setFilteredStoredEntries] = useState([]);
const [showCongratulationsModal, setShowCongratulationsModal] = useState(false);
  const [wisdomCards, setWisdomCards] = useState([]);
const [isGeneratingWisdom, setIsGeneratingWisdom] = useState(false);
const [currentWisdomIndex, setCurrentWisdomIndex] = useState(0);
const [usedImageIndices, setUsedImageIndices] = useState([]);
const [showWisdomCards, setShowWisdomCards] = useState(() => {
  // Check localStorage on first render
  const saved = localStorage.getItem('showWisdomCards');
  return saved === 'true';
});const [podcastPreferences, setPodcastPreferences] = useState({
  hostPersonality: 'warm', // 'warm', 'energetic', 'calm', 'intellectual'
  pacing: 'medium', // 'slow', 'medium', 'fast'
  focus: 'growth', // 'growth', 'patterns', 'emotions', 'achievements'
  includeQuotes: true
});
const [showConfetti, setShowConfetti] = useState(false);
const [windowSize, setWindowSize] = useState({
  width: window.innerWidth,
  height: window.innerHeight
});
// ✅ Calculate and save streak whenever entries change (including deletes)
useEffect(() => {
  const newStreak = calculateStreak(entries);
  setDisplayStreak(newStreak);
  
  // Save to localStorage immediately
  localStorage.setItem('currentStreak', JSON.stringify({
    value: newStreak,
    lastCalculated: new Date().toISOString()
  }));
  
  console.log('🔥 Streak recalculated:', newStreak);
}, [entries]);
const handleImageSelect = (event) => {
  const files = Array.from(event.target.files);
  
  if (selectedImages.length + files.length > 3) {
    alert('You can only select up to 3 images');
    return;
  }

  setSelectedImages([...selectedImages, ...files]);
};
const [showNotificationText, setShowNotificationText] = useState(false);
useEffect(() => {
  if (editingEntry) {
    // Check if anything changed
    const titleChanged = editTitle !== editingEntry.title;
    const contentChanged = editContent !== editingEntry.content;
    const moodChanged = editMood !== editingEntry.mood;
    const imagesChanged = selectedImages.length !== (editingEntry.images?.length || 0);
    
    setHasUnsavedChanges(titleChanged || contentChanged || moodChanged || imagesChanged);
  }
}, [editTitle, editContent, editMood, selectedImages, editingEntry]);

const [isServiceReady, setIsServiceReady] = useState(false);

useEffect(() => {
  const initializeJournalService = async () => {
    try {
      // Wait for firestoreService to be ready
      if (!firestoreService?.currentUser?.uid) {
        console.log('⏳ Waiting for Firestore service...');
        return;
      }
      
      const userId = firestoreService.currentUser.uid;
      console.log('✅ Initializing journal storage for user:', userId);
      
      await journalStorage.initialize(userId);
      console.log('✅ Journal storage ready');
      
      // Load entries
      await loadAllEntries();
      
    } catch (error) {
      console.error('❌ Initialization failed:', error);
    }
  };
  
  initializeJournalService();
}, [firestoreService?.currentUser?.uid]); // Re-run when user changes

useEffect(() => {
  // Animate on page load
  const timer1 = setTimeout(() => {
    setShowNotificationText(true);
  }, 1500); // Wait 500ms before expanding

  const timer2 = setTimeout(() => {
    setShowNotificationText(false);
  }, 3000); // Collapse after 3 seconds

  return () => {
    clearTimeout(timer1);
    clearTimeout(timer2);
  };
}, []);
// CHANGE IT TO:
useEffect(() => {
  const loadUserName = async () => {
    try {
      const service = firestoreService || window.firestoreService;
      const name = await getStoredUserName(service);
      setUserName(name || 'there');  // ← NEW
    } catch (error) {
      console.error('Failed to load username for notifications:', error);
      setUserName('there');  // ← NEW
    }
  };
  loadUserName();
}, [firestoreService]);
useEffect(() => {
  const handleResize = () => {
    setWindowSize({
      width: window.innerWidth,
      height: window.innerHeight
    });
  };
  
  window.addEventListener('resize', handleResize);
  return () => window.removeEventListener('resize', handleResize);
}, []);
const [notificationsEnabled, setNotificationsEnabled] = useState(
  areJournalNotificationsEnabled()
);
useEffect(() => {
  const checkNotificationPermission = async () => {
    if (Capacitor.isNativePlatform()) {
      const enabled = areJournalNotificationsEnabled();
      setNotificationsEnabled(enabled);
      
      if (enabled) {
        // Request permission if not already granted
        await requestJournalNotificationPermission();
      }
    }
  };
  
  checkNotificationPermission();
}, []);
useEffect(() => {
  if (entries.length > 0 && notificationsEnabled) {
    updateJournalNotifications(entries, userName);
  }
}, [entries, notificationsEnabled, userName]);

// ADD THIS function to toggle notifications
const toggleNotifications = async () => {
  if (notificationsEnabled) {
    await disableJournalNotifications();
    setNotificationsEnabled(false);
  } else {
    const granted = await enableJournalNotifications();
    if (granted) {
      setNotificationsEnabled(true);
      // Update notifications immediately
      await updateJournalNotifications(entries, userName);
    }
  }
};

const [showPodcastSettings, setShowPodcastSettings] = useState(false);

const [uploadedPhotos, setUploadedPhotos] = useState(() => {
  const saved = localStorage.getItem('uploadedBackgrounds');
  return saved ? JSON.parse(saved) : [];
});

const loadEntryImages = (entry) => {
  const images = [];
  
  console.log('📷 Loading images for entry:', entry.id);
  console.log('   selectedImages:', entry.selectedImages?.length || 0);
  console.log('   images:', entry.images?.length || 0);
  
  // Priority 1: Check selectedImages array FIRST
  if (entry.selectedImages && Array.isArray(entry.selectedImages) && entry.selectedImages.length > 0) {
    console.log('✅ Using selectedImages array:', entry.selectedImages.length, 'images');
    entry.selectedImages.forEach((img, idx) => {
      if (typeof img === 'string' && img.length > 0) {
        images.push({
          data: img,
          id: `img_${idx}_${entry.id}`,
          width: 1920,
          height: 1080,
          aspectRatio: 16/9
        });
      } else if (img && img.data) {
        images.push({
          data: img.data,
          id: img.id || `img_${idx}_${entry.id}`,
          width: img.width || 1920,
          height: img.height || 1080,
          aspectRatio: img.aspectRatio || 16/9
        });
      }
    });
  }
  
  // Priority 2: Check images array if selectedImages is empty
  if (images.length === 0 && entry.images && Array.isArray(entry.images) && entry.images.length > 0) {
    console.log('✅ Using images array:', entry.images.length, 'images');
    entry.images.forEach((img, idx) => {
      if (typeof img === 'string' && img.length > 0) {
        images.push({
          data: img,
          id: `img_${idx}_${entry.id}`,
          width: 1920,
          height: 1080,
          aspectRatio: 16/9
        });
      }
    });
  }
  
  // Priority 3: Check background field (fallback for old entries)
  if (images.length === 0 && entry.background && entry.background.length > 0) {
    console.log('✅ Using background field (fallback)');
    images.push({
      data: entry.background,
      id: 'bg_' + entry.id,
      width: 1920,
      height: 1080,
      aspectRatio: 16/9
    });
  }
  
  // Priority 4: Check imageUrl field (fallback)
  if (images.length === 0 && entry.imageUrl && entry.imageUrl.length > 0) {
    console.log('✅ Using imageUrl field (fallback)');
    images.push({
      data: entry.imageUrl,
      id: 'url_' + entry.id,
      width: 1920,
      height: 1080,
      aspectRatio: 16/9
    });
  }
  
  console.log(`✅ LOADED ${images.length} images for entry ${entry.id}`);
  return images;
};

// ============= DELETE CONFIRMATION MODAL COMPONENT =============
// ============= DELETE CONFIRMATION MODAL - FIXED =============
const DeleteConfirmationModal = ({ onConfirm, onCancel }) => {
  return (
    <div 
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(8px)',
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        animation: 'fadeIn 0.3s ease'
      }}
      onClick={onCancel}
    >
      <div 
        style={{
          background: 'white',
          borderRadius: '20px',
          padding: '28px',
          maxWidth: '380px',
          width: '100%',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
          animation: 'scaleIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
          position: 'relative',
          zIndex: 100000
        }}
        onClick={(e) => e.stopPropagation()}
      >
        
        <h3 style={{
          fontSize: '18px',
          fontWeight: '600',
          color: '#1f2937',
          marginBottom: '8px',
          textAlign: 'center'
        }}>
          Delete Entry?
        </h3>

        <p style={{
          fontSize: '14px',
          color: '#6b7280',
          lineHeight: '1.6',
          marginBottom: '24px',
          textAlign: 'center'
        }}>
          This action cannot be undone. Your entry will be permanently deleted.
        </p>

        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={onCancel}
            style={{
              flex: 1,
              padding: '10px',
              background: '#f3f4f6',
              color: '#374151',
              border: 'none',
              borderRadius: '24px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#e5e7eb'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#f3f4f6'}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            style={{
              flex: 1,
              padding: '10px',
              background: '#dc2626',
              color: 'white',
              border: 'none',
              borderRadius: '24px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#b91c1c'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#dc2626'}
          >
            <Trash2 size={16} />
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

// ============= EMERGENCY PIN RESET MODAL - FIXED =============
const EmergencyPINResetModal = ({ onReset, onCancel }) => {
  return (
    <div 
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(8px)',
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
      onClick={onCancel}
    >
      <div 
        style={{
          background: 'white',
          borderRadius: '28px',
          padding: '28px',
          maxWidth: '380px',
          width: '100%',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
          position: 'relative',
          zIndex: 100000
        }}
        onClick={(e) => e.stopPropagation()}
      >
        
        <h3 style={{
          fontSize: '18px',
          fontWeight: '600',
          color: '#1f2937',
          marginBottom: '12px',
          textAlign: 'center',
          margin: '0 0 12px 0'
        }}>
          Reset PIN
        </h3>

        <p style={{
          fontSize: '14px',
          color: '#6b7280',
          lineHeight: '1.6',
          marginBottom: '24px',
          textAlign: 'center'
        }}>
          This will remove PIN protection from all locked entries. Your entries will remain safe but unlocked.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <button
            onClick={onReset}
            style={{
              padding: '10px',
              background: '#000000ff',
              color: 'white',
              border: 'none',
              borderRadius: '28px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            Reset PIN & Unlock All
          </button>
          <button
            onClick={onCancel}
            style={{
              padding: '10px',
              background: '#f3f4f6',
              color: '#374151',
              border: 'none',
              borderRadius: '28px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};
// Add this function to handle photo uploads
const handlePhotoUpload = (event) => {
  const file = event.target.files[0];
  if (!file) return;

  // Check if file is an image
  if (!file.type.startsWith('image/')) {
    alert('Please select an image file');
    return;
  }

  // Check file size (max 5MB)
  if (file.size > 5 * 1024 * 1024) {
    alert('Image size should be less than 5MB');
    return;
  }

  const reader = new FileReader();
  reader.onloadend = () => {
    const base64Image = reader.result;
    const newPhoto = {
      value: base64Image,
      preview: base64Image,
      isCustom: true,
      id: Date.now()
    };
    
    const updatedPhotos = [newPhoto, ...uploadedPhotos];
    setUploadedPhotos(updatedPhotos);
    
    // Save to localStorage
    try {
      localStorage.setItem('uploadedBackgrounds', JSON.stringify(updatedPhotos));
    } catch (e) {
      console.error('Error saving uploaded photo:', e);
      alert('Failed to save photo. It may be too large.');
    }
    
    // Automatically select the uploaded photo
    setNewEntry({ ...newEntry, background: base64Image });
  };
  
  reader.readAsDataURL(file);
};
const CongratulationsModal = ({ onClose }) => {
  return (
    <>
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 9999
      }}>
        {[...Array(50)].map((_, i) => (
          <div
            key={i}
            style={{
              position: 'absolute',
              top: '-10px',
              left: `${Math.random() * 100}%`,
              width: '10px',
              height: '10px',
              background: ['#fbbf24', '#f59e0b', '#ef4444', '#ec4899', '#8b5cf6'][Math.floor(Math.random() * 5)],
              borderRadius: '50%',
              animation: `confettiFall ${2 + Math.random() * 2}s linear forwards`,
              animationDelay: `${Math.random() * 0.5}s`,
              pointerEvents: 'none'
            }}
          />
        ))}
      </div>
      
      <div className="modal-overlay" style={{ zIndex: 9998 }}>
        <div style={{
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderRadius: '24px',
          padding: '32px 24px',
          maxWidth: '340px',
          width: '90%',
          margin: 'auto',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
          border: '1px solid rgba(255, 255, 255, 0.8)',
          animation: 'scaleIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
          textAlign: 'center'
        }}>
          {/* Sparkle Icon */}
          <div style={{
            width: '56px',
            height: '56px',
            background: 'linear-gradient(135deg, #fef3c7 0%, #ffde5bff 100%)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
            animation: 'bounce 1s ease-in-out infinite'
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path stroke="#f59e0b" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 5.464V3.099m0 2.365a5.338 5.338 0 0 1 5.133 5.368v1.8c0 2.386 1.867 2.982 1.867 4.175C19 17.4 19 18 18.462 18H5.538C5 18 5 17.4 5 16.807c0-1.193 1.867-1.789 1.867-4.175v-1.8A5.338 5.338 0 0 1 12 5.464ZM6 5 5 4M4 9H3m15-4 1-1m1 5h1M8.54 18a3.48 3.48 0 0 0 6.92 0H8.54Z"/>
            </svg>
          </div>

          {/* Title */}
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#1f2937',
            marginBottom: '8px'
          }}>
            Great work!
          </h3>

          {/* Message */}
          <p style={{
            fontSize: '13px',
            color: '#6b7280',
            lineHeight: '1.6',
            marginBottom: '24px'
          }}>
Yayy! You created your first journal entry! Keep going and build your story.
          </p>

          {/* Close Button */}
          <button
            onClick={onClose}
            style={{
              width: '40%',
              padding: '10px',
              background: '#272727',
              border: 'none',
              borderRadius: '20px',
              color: 'white',
              fontSize: '12px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s',
              margin: '0 auto',
              display: 'block'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#3f3f3f'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#272727'}
          >
            Close
          </button>
        </div>
      </div>
    </>
  );
};
// ✅ ADD THIS NEW FUNCTION
const checkMilestones = (totalEntries) => {
  const milestones = {
    10: "🎉 10 Entries! You're building a habit!",
    50: "🔥 50 Entries! You're on fire!",
    100: "🏆 100 Entries! You're a journaling master!"
  };
  
  const milestone = milestones[totalEntries];
  if (!milestone) return;
  
  // Check if we already celebrated this milestone
  const celebrated = localStorage.getItem(`milestone_${totalEntries}`);
  if (celebrated) return;
  
  // Mark as celebrated
  localStorage.setItem(`milestone_${totalEntries}`, 'true');
  
  // Show celebration
  showMilestoneCelebration(milestone, totalEntries);
};

// ✅ REPLACE showMilestoneCelebration with this updated version
const showMilestoneCelebration = (message, count) => {
  // Trigger confetti
  setShowConfetti(true);
  
  const overlay = document.createElement('div');
  overlay.style.cssText = `
    position: fixed;
    inset: 0;
    background: rgba(175, 175, 175, 0.3);
    backdrop-filter: blur(2px);
    z-index: 99999;
    display: flex;
    align-items: center;
    justify-content: center;
    animation: fadeIn 0.3s ease;
  `;
  
  const modal = document.createElement('div');
  modal.style.cssText = `
    background: white;
    border-radius: 24px;
    padding: 40px 32px;
    text-align: center;
    max-width: 400px;
    width: 90%;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    animation: scaleIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  `;
  
  modal.innerHTML = `
    <div style="font-size: 64px; margin-bottom: 20px; animation: bounce 1s infinite;">
      ${count === 10 ? '🎉' : count === 50 ? '🔥' : '🏆'}
    </div>
    <h2 style="font-size: 24px; font-weight: 700; color: #1f2937; margin-bottom: 12px;">
      ${count} Entries Milestone!
    </h2>
    <p style="font-size: 16px; color: #6b7280; line-height: 1.6; margin-bottom: 28px;">
      ${message.replace(/🎉|🔥|🏆/g, '').trim()}
    </p>
    <button style="
      background: #272727;
      color: white;
      border: none;
      border-radius: 20px;
      padding: 14px 32px;
      font-size: 15px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s;
    " onmouseover="this.style.background='#1a1a1a'" onmouseout="this.style.background='#272727'">
      Keep Writing!
    </button>
  `;
  
  modal.querySelector('button').onclick = () => {
    document.body.removeChild(overlay);
    setShowConfetti(false); // Stop confetti when closing
  };
  
  overlay.appendChild(modal);
  document.body.appendChild(overlay);
  
  // Auto-stop confetti after 5 seconds
  setTimeout(() => {
    setShowConfetti(false);
  }, 5000);
  
  // Auto-close modal after 5 seconds
  setTimeout(() => {
    if (document.body.contains(overlay)) {
      document.body.removeChild(overlay);
    }
  }, 5000);
};
// Add this function to delete custom photos
const deleteCustomPhoto = (photoId, event) => {
  event.stopPropagation();
  const updatedPhotos = uploadedPhotos.filter(photo => photo.id !== photoId);
  setUploadedPhotos(updatedPhotos);
  localStorage.setItem('uploadedBackgrounds', JSON.stringify(updatedPhotos));
  
  // If the deleted photo was selected, clear selection
  const deletedPhoto = uploadedPhotos.find(p => p.id === photoId);
  if (deletedPhoto && newEntry.background === deletedPhoto.value) {
    setNewEntry({ ...newEntry, background: '' });
  }
};

const calculateWritingTimeStats = () => {
  if (entries.length === 0) {
    return { morning: 0, afternoon: 0, evening: 0 };
  }

  const timeStats = { morning: 0, afternoon: 0, evening: 0 };
  
  entries.forEach(entry => {
    const hour = entry.date.getHours();
    if (hour >= 5 && hour < 12) {
      timeStats.morning++;
    } else if (hour >= 12 && hour < 18) {
      timeStats.afternoon++;
    } else {
      timeStats.evening++;
    }
  });

  const total = entries.length;
  return {
    morning: Math.round((timeStats.morning / total) * 100),
    afternoon: Math.round((timeStats.afternoon / total) * 100),
    evening: Math.round((timeStats.evening / total) * 100)
  };
};

const getRandomUnusedImage = () => {
  const allImages = [
    '/image/session-img13.png', '/image/session-img32.png', '/image/session-img8.png',
    '/image/session-img26.png', '/image/session-img17.png', '/image/session-img10.png',
    '/image/session-img14.png', '/image/session-img42.png', '/image/session-img12.png',
    '/image/session-img11.png', '/image/session-img20.png', '/image/session-img40.png',
    '/image/session-img50.png', '/image/session-img47.png', '/image/session-img24.png',
    '/image/session-img31.png'
  ];

  // If all images used, reset
  if (usedImageIndices.length >= allImages.length) {
    setUsedImageIndices([]);
  }

  // Get available images
  const availableIndices = allImages
    .map((_, index) => index)
    .filter(index => !usedImageIndices.includes(index));

  // Pick random from available
  const randomIndex = availableIndices[Math.floor(Math.random() * availableIndices.length)];
  
  // Mark as used
  setUsedImageIndices([...usedImageIndices, randomIndex]);
  
  return allImages[randomIndex];
};
const extractPowerfulQuotes = (entries) => {
  const quotes = entries.flatMap(entry => {
    const sentences = entry.content
      .split(/[.!?]+/)
      .map(s => s.trim())
      .filter(s => s.length > 30 && s.length < 180);
    
    const emotionalWords = [
      'feel', 'felt', 'realized', 'discovered', 'understand', 'understood',
      'love', 'fear', 'hope', 'dream', 'believe', 'learned', 'changed',
      'grew', 'want', 'need', 'wish', 'grateful', 'proud', 'scared', 'happy'
    ];
    
    const powerfulSentences = sentences.filter(s => 
      emotionalWords.some(word => s.toLowerCase().includes(word))
    );
    
    return powerfulSentences.map(quote => ({
      quote: quote,
      entry: entry.title,
      date: formatDate(entry.date),
      mood: entry.mood
    }));
  });
  
  return quotes.slice(0, 6);
};

const analyzeJourneyDeeper = (entries) => {
  const emotionalJourney = entries.slice(0, 10).map((e) => ({
    date: formatDate(e.date).split(',')[0],
    mood: e.mood,
    title: e.title,
    wordCount: e.content.split(' ').length
  }));
  
  const breakthroughMoments = [...entries]
    .sort((a, b) => b.content.length - a.content.length)
    .slice(0, 2)
    .map(e => ({
      date: formatDate(e.date),
      title: e.title,
      excerpt: e.content.substring(0, 120),
      wordCount: e.content.split(' ').length
    }));
  
  const allWords = entries.flatMap(e => 
    e.content.toLowerCase()
      .split(/\s+/)
      .filter(w => w.length > 5)
  );
  
  const wordFrequency = {};
  allWords.forEach(w => wordFrequency[w] = (wordFrequency[w] || 0) + 1);
  
  const topThemes = Object.entries(wordFrequency)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)
    .map(([word, count]) => `${word} (${count}×)`);
  
  return { emotionalJourney, breakthroughMoments, topThemes };
};

const compareJourneyPhases = (entries) => {
  const firstWeek = entries.slice(-7);
  const lastWeek = entries.slice(0, 7);
  
  const earlyAvgWords = Math.round(
    firstWeek.reduce((sum, e) => sum + e.content.split(' ').length, 0) / firstWeek.length
  );
  const recentAvgWords = Math.round(
    lastWeek.reduce((sum, e) => sum + e.content.split(' ').length, 0) / lastWeek.length
  );
  
  const earlyMoods = [...new Set(firstWeek.map(e => e.mood))];
  const recentMoods = [...new Set(lastWeek.map(e => e.mood))];
  
  const earlyThemes = [...new Set(firstWeek.flatMap(e => e.tags || []))];
  const recentThemes = [...new Set(lastWeek.flatMap(e => e.tags || []))];
  const newThemes = recentThemes.filter(t => !earlyThemes.includes(t));
  
  const growthPercentage = Math.round(((recentAvgWords - earlyAvgWords) / earlyAvgWords) * 100);
  
  return {
    earlyAvgWords,
    recentAvgWords,
    growthPercentage,
    earlyMoods: earlyMoods.join(', '),
    recentMoods: recentMoods.join(', '),
    earlyThemes: earlyThemes.slice(0, 3).join(', ') || 'exploring',
    recentThemes: recentThemes.slice(0, 3).join(', ') || 'discovering',
    newThemes: newThemes.join(', ') || 'none'
  };
};

const getHostPersonalityInstructions = (personality) => {
  const personalities = {
    warm: "Speak like a caring best friend - supportive, gentle, understanding. Use warm phrases like 'I see you' and 'that makes sense'.",
    energetic: "Be enthusiastic and motivating! Use exclamations, celebrate wins big, be upbeat and inspiring!",
    calm: "Maintain a meditative, peaceful tone. Speak slowly, thoughtfully. Create space for reflection.",
    intellectual: "Be analytical and insightful. Draw connections, identify patterns, offer psychological frameworks."
  };
  return personalities[personality] || personalities.warm;
};

const getFocusInstructions = (focus) => {
  const focuses = {
    growth: "Emphasize progress, evolution, and positive changes. Show them how far they've come.",
    patterns: "Identify recurring themes, habits, and cycles. Help them see their patterns clearly.",
    emotions: "Deep dive into their emotional landscape. Validate feelings and emotional journeys.",
    achievements: "Celebrate wins, milestones, and accomplishments. Highlight their strengths."
  };
  return focuses[focus] || focuses.growth;
};

// Update the generatePersonalWisdomCards function
const generatePersonalWisdomCards = async () => {
  if (entries.length === 0) {
    alert('Write some journal entries first to generate your personal wisdom!');
    return;
  }

  // ✅ FIX: Get userName properly and ensure it's a string
  const userNameRaw = getStoredUserName();
  const userName = String(userNameRaw || 'there'); // Convert to string safely
  console.log('✅ Username:', userName, 'Type:', typeof userName);

  // ✅ Filter out locked entries - only analyze unlocked ones
  const unlockedEntries = entries.filter(entry => !isEntryLocked(entry.id));
  
  if (unlockedEntries.length < 5) {
    alert(`You need at least 5 unlocked entries to generate personalized insights. Currently you have ${unlockedEntries.length} unlocked entries.`);
    return;
  }

  setIsGeneratingWisdom(true);
  setUsedImageIndices([]);
  
  try {
    
    // ✅ Use only UNLOCKED entries for analysis
    const totalEntries = unlockedEntries.length;
    const shuffledEntries = [...unlockedEntries].sort(() => Math.random() - 0.5);
    
    // Randomly decide which time period to focus on
    const focusStrategies = [
      () => entries.slice(0, 8), // Recent focus
      () => entries.slice(-8), // Early journey focus
      () => shuffledEntries.slice(0, 8), // Random mix
      () => [...entries.slice(0, 3), ...shuffledEntries.slice(0, 3), ...entries.slice(-2)], // Balanced
      () => entries.filter((_, i) => i % 2 === 0).slice(0, 8) // Every other entry
    ];
    
    const randomStrategy = focusStrategies[Math.floor(Math.random() * focusStrategies.length)];
    const allSelectedEntries = randomStrategy().slice(0, Math.min(10, totalEntries));
    
    // ✅ ADD TIMESTAMP to make each request unique
    const timestamp = new Date().toISOString();
    const sessionId = Math.random().toString(36).substring(7);
    
    // Build richer entry context with MORE personal details
    const entriesText = allSelectedEntries.map((e, idx) => 
      `Entry ${idx + 1} (${formatDate(e.date).split(',')[0]}): "${e.title}"\n${e.content.substring(0, 200)}...\nMood: ${e.mood}\nTags: ${e.tags.slice(0, 3).join(', ')}\n`
    ).join('\n---\n\n');

    // Deep analysis of their journey
    const moodData = calculateMoodStats();
    const timeData = calculateWritingTimeStats();
    
    // Calculate streak
// Calculate streak - REPLACE the old calculation  
const currentStreak = calculateStreak(entries);
    // Extract recurring themes/words
    const allWords = entries.flatMap(e => 
      e.content.toLowerCase().split(/\s+/).filter(w => w.length > 5)
    );
    const wordFreq = {};
    allWords.forEach(w => wordFreq[w] = (wordFreq[w] || 0) + 1);
    const topThemes = Object.entries(wordFreq)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([word]) => word);

    // Find emotional progression
    const moodProgression = entries.slice(0, 7).map(e => e.mood).join(' → ');
    
    // Identify most vulnerable/deep entry
    const longestEntry = [...entries].sort((a, b) => b.content.length - a.content.length)[0];
    
    // Find if they mention goals/desires
    const goalKeywords = ['want', 'wish', 'hope', 'goal', 'dream', 'need', 'trying'];
    const goalsEntries = entries.filter(e => 
      goalKeywords.some(keyword => e.content.toLowerCase().includes(keyword))
    );

    // Find struggles/challenges
    const struggleKeywords = ['hard', 'difficult', 'struggle', 'tired', 'exhausted', 'frustrated', 'stuck'];
    const strugglesEntries = entries.filter(e => 
      struggleKeywords.some(keyword => e.content.toLowerCase().includes(keyword))
    );

    // Find wins/achievements
    const winKeywords = ['achieved', 'accomplished', 'proud', 'success', 'won', 'finished', 'completed'];
    const winsEntries = entries.filter(e => 
      winKeywords.some(keyword => e.content.toLowerCase().includes(keyword))
    );

    // ✅ Analyze recent entries for daily/weekly goals
    const today = new Date();
    const thisWeek = entries.filter(e => {
      const daysDiff = Math.ceil((today - e.date) / (1000 * 60 * 60 * 24));
      return daysDiff <= 7;
    });
    
    const recentMoods = thisWeek.map(e => e.mood);
    const recentThemes = [...new Set(thisWeek.flatMap(e => e.tags || []))];

    // ✅ NEW: Respectful card type selection with actionable recommendations
    const cardFormats = [
      'Gentle Observation',
      'Weekly Recommendation',
      'Daily Practice Suggestion',
      'Pattern Recognition',
      'Strength Highlight',
      'Progress Celebration',
      'Supportive Insight',
      'Actionable Next Step',
      'Encouraging Reflection',
      'Personalized Goal'
    ];
    
    const randomFormats = [...cardFormats]
      .sort(() => Math.random() - 0.5)
      .slice(0, 5);

    // ✅ RANDOM FOCUS AREAS - Different angle each time
    const focusAreas = [
      'emotional growth and self-awareness',
      'actionable next steps and momentum', 
      'recurring patterns and insights',
      'strengths and positive qualities',
      'progress and achievements',
      'self-care and wellbeing',
      'gratitude and appreciation',
      'personal development',
      'meaningful connections',
      'creative expression'
    ];
    const randomFocus = focusAreas[Math.floor(Math.random() * focusAreas.length)];

    // ✅ SUPPORTIVE PERSPECTIVES
    const perspectives = [
      'like a supportive friend celebrating their journey',
      'like a wise mentor offering gentle guidance',
      'like a compassionate coach recognizing their efforts',
      'like someone who deeply appreciates their growth',
      'like a trusted companion on their path',
      'like an encouraging guide highlighting their strengths'
    ];
    const randomPerspective = perspectives[Math.floor(Math.random() * perspectives.length)];

    // ✅ RESPECTFUL & ACTIONABLE PROMPT
    const prompt = `You are ${userName}'s supportive journal companion analyzing their writing with care and respect.

**SESSION ID**: ${sessionId}
**ANALYSIS FOCUS**: ${randomFocus}
**PERSPECTIVE**: Speak ${randomPerspective}
**GENERATED AT**: ${timestamp}

**${userName.toUpperCase()}'S STORY:**

${entriesText}

**JOURNEY INSIGHTS:**

- **Writing Streak**: ${currentStreak} consecutive days
- **Total Entries**: ${entries.length} entries
- **This Week's Entries**: ${thisWeek.length} entries
- **Recent Moods**: ${recentMoods.join(', ')}
- **Recent Themes**: ${recentThemes.slice(0, 5).join(', ')}
- **Peak Writing Time**: ${Object.entries(timeData).sort((a,b) => b[1] - a[1])[0][0]} 
- **Dominant Mood**: ${moodData[0].name} (${moodData[0].percentage}%)
- **Recurring Themes**: ${topThemes.join(', ')}
- **Deepest Entry**: "${longestEntry?.title}" (${longestEntry?.content.length} chars)
- **Goals Mentioned**: ${goalsEntries.length} entries
- **Struggles Expressed**: ${strugglesEntries.length} entries  
- **Wins Celebrated**: ${winsEntries.length} entries

**YOUR MISSION:**
Create 5 SUPPORTIVE, ACTIONABLE wisdom cards focused on ${randomFocus}. Be respectful, encouraging, and helpful.

**CARD FORMAT VARIETY - Use these 5 formats (one per card):**
${randomFormats.map((format, i) => `${i + 1}. ${format}`).join('\n')}

**FORMAT GUIDELINES (RESPECTFUL & ACTIONABLE):**
- **Gentle Observation**: Notice patterns kindly without judgment
- **Weekly Recommendation**: Suggest a specific practice for the week ahead based on their entries
- **Daily Practice Suggestion**: Offer a simple daily action they can take (5-10 minutes)
- **Pattern Recognition**: Highlight positive patterns they're creating
- **Strength Highlight**: Celebrate qualities they're demonstrating
- **Progress Celebration**: Acknowledge growth and wins
- **Supportive Insight**: Share a helpful perspective on their journey
- **Actionable Next Step**: Give ONE clear, doable action for this week
- **Encouraging Reflection**: Affirm their process and progress
- **Personalized Goal**: Based on recent entries, suggest a gentle goal for next week

**IMPORTANT TONE GUIDELINES:**
- Be warm, respectful, and encouraging
- AVOID asking too many questions (max 1 question per 5 cards)
- Focus on STATEMENTS and RECOMMENDATIONS instead
- Never challenge or confront - always support and guide
- Acknowledge their efforts and progress
- Make suggestions gentle, never demanding
- Celebrate small wins genuinely

**STRUCTURE:**
Mix these types across the 5 cards:
- 2 cards with DAILY/WEEKLY ACTION RECOMMENDATIONS
- 1 card celebrating PROGRESS or STRENGTH
- 1 card offering GENTLE INSIGHT about patterns
- 1 card with ENCOURAGEMENT and next steps

**AVOID:**
- Asking multiple questions
- Being confrontational or challenging
- Making them feel guilty or inadequate
- Using therapy-speak or corporate language
- Generic platitudes - be specific to THEIR entries
- Repeating the same phrases across cards

**EXAMPLES OF GOOD CARDS:**

Example 1 (Weekly Recommendation):
"Based on your entries this week, you mentioned [specific theme] three times. This week, try dedicating 10 minutes each morning to [specific action]. This could help deepen what you're already exploring."

Example 2 (Daily Practice):
"Your evening entries show [pattern]. Each day this week, before bed, write down one moment that felt aligned with [their value/goal]. Just one sentence. Watch what happens."

Example 3 (Strength Highlight):
"[Quote from entry]. The way you showed up here demonstrates [specific strength]. You're building something important, and it shows."

Example 4 (Progress Celebration):
"${currentStreak} days of writing. That's ${currentStreak} times you chose to understand yourself better. This consistency is creating real change."

Example 5 (Actionable Next Step):
"You mentioned [specific goal] on [date]. This week, take one 30-minute block to [specific action]. No pressure for perfection - just progress."

**CRITICAL RULES:**
- Use ${userName}'s name 1-2 times ONLY across all cards
- Quote EXACT words verbatim
- Be specific to THEIR entries, not generic
- Give ACTIONABLE recommendations (daily practices, weekly goals)
- 2-3 sentences MAX per message
- Be respectful, warm, and supportive always
- Focus on what they CAN do, not what they should fix
- Celebrate effort, not just outcomes

**CATEGORY MAPPING:**
- Daily Practice Suggestion, Weekly Recommendation, Personalized Goal → "Your Next Move"
- Pattern Recognition, Gentle Observation, Supportive Insight → "Pattern I'm Seeing"
- Strength Highlight → "This Is Your Superpower"
- Encouraging Reflection → "Growth Challenge" (but make it gentle!)
- Progress Celebration → "Celebrate This"

Return ONLY valid JSON array:
[
  {
    "title": "warm, specific 4-6 word title",
    "quote": "their exact words from entries",
    "message": "supportive 2-3 sentence message with clear action or insight",
    "category": "one of the 5 categories above",
    "format": "the format type used"
  }
]

No markdown. Just JSON.`;

    const response = await sendChatMessage([], prompt, null);
    
    // ✅ FIX: Extract content from Azure response object
    const responseContent = typeof response === 'string' 
      ? response 
      : response.content || response.message?.content || JSON.stringify(response);
    
    console.log('✅ Full response received');
    console.log('Response type:', typeof response);
    console.log('Response preview:', responseContent.substring(0, 500));
    
    // Try to extract JSON with better error handling
    let wisdomData;
    try {
      // Remove any markdown code blocks
      let cleanResponse = responseContent.replace(/```json\n?/g, '').replace(/```\n?/g, '');
      
      // Find JSON array
      const jsonMatch = cleanResponse.match(/\[\s*\{[\s\S]*\}\s*\]/);
      
      if (jsonMatch) {
        console.log('✅ JSON found, parsing...');
        wisdomData = JSON.parse(jsonMatch[0]);
        
        if (!Array.isArray(wisdomData) || wisdomData.length === 0) {
          throw new Error('Invalid wisdom data structure');
        }
        
        // Validate and clean cards
        wisdomData = wisdomData.filter(card => 
          card.title && card.message && card.category
        ).slice(0, 5);
        
        if (wisdomData.length === 0) {
          throw new Error('No valid cards after filtering');
        }
        
        console.log('✅ Parsed wisdom cards:', wisdomData.length);
        
      } else {
        console.error('❌ No JSON found in response');
        throw new Error('No JSON found in response');
      }
    } catch (parseError) {
      console.error('❌ Parse error:', parseError);
      console.log('Response type:', typeof response);
      console.log('Full response object:', response);
      console.log('Response content:', responseContent);
      
      // ✅ RESPECTFUL & ACTIONABLE FALLBACK
      const fallbackEntries = allSelectedEntries.slice(0, 5);
      
      // Find most emotional/longest entry this week
      const deepestThisWeek = thisWeek.length > 0 
        ? [...thisWeek].sort((a, b) => b.content.length - a.content.length)[0]
        : fallbackEntries[0];
      
      // Find a goal/desire mentioned
      const goalEntry = entries.find(e => 
        ['want', 'wish', 'hope', 'goal', 'dream', 'trying'].some(word => 
          e.content.toLowerCase().includes(word)
        )
      );
      
      // Find most recent positive entry
      const positiveEntry = entries.find(e => 
        ['grateful', 'peaceful', 'creative'].includes(e.mood.toLowerCase())
      );
      
      // Calculate writing momentum
      const writingMomentum = thisWeek.length >= 3 ? 'strong' : thisWeek.length >= 1 ? 'building' : 'starting';
      
      // Get peak writing time
      const peakTime = Object.entries(timeData).sort((a,b) => b[1] - a[1])[0][0];
      
      // ✅ SUPPORTIVE, ACTIONABLE FALLBACK CARDS
      wisdomData = [
        {
          title: "This Week's Writing Practice",
          quote: deepestThisWeek?.content.split(/[.!?]/)[0] || "Your reflections are creating clarity",
          message: thisWeek.length > 0
            ? `You wrote ${thisWeek.length} ${thisWeek.length === 1 ? 'entry' : 'entries'} this week. Your ${writingMomentum} momentum is building something meaningful. Keep this rhythm going.`
            : `This week, try writing for just 5 minutes each day. Small, consistent steps create lasting habits.`,
          category: "Your Next Move",
          format: "Weekly Recommendation",
          image: getRandomUnusedImage()
        },
        {
          title: goalEntry ? "Your Next Small Step" : "A Gentle Goal for This Week",
          quote: goalEntry 
            ? goalEntry.content.split(/[.!?]/).find(s => 
                ['want', 'wish', 'hope', 'goal', 'dream', 'trying'].some(word => s.toLowerCase().includes(word))
              )?.trim() || goalEntry.content.substring(0, 100)
            : "Every entry brings you closer to understanding yourself",
          message: goalEntry
            ? `You mentioned this in "${goalEntry.title}". This week, dedicate 20 minutes to take one concrete action toward it. Just one step forward.`
            : `Based on your recent entries, try setting one small intention for the week. Write it down today and revisit it in your next entry.`,
          category: "Your Next Move",
          format: "Actionable Next Step",
          image: getRandomUnusedImage()
        },
        {
          title: `Your ${peakTime} Writing Ritual`,
          quote: entries.find(e => {
            const hour = e.date.getHours();
            return (peakTime === 'morning' && hour < 12) || 
                   (peakTime === 'afternoon' && hour >= 12 && hour < 18) ||
                   (peakTime === 'evening' && hour >= 18);
          })?.content.substring(0, 120) || "You've found your rhythm",
          message: `You write most often in the ${peakTime}. This shows you're creating a natural practice. Honor this time and protect it when you can.`,
          category: "Pattern I'm Seeing",
          format: "Pattern Recognition",
          image: getRandomUnusedImage()
        },
        {
          title: "The Consistency You're Building",
          quote: positiveEntry?.content.split(/[.!?]/)[0] || `${currentStreak} consecutive days of self-reflection`,
          message: currentStreak > 1
            ? `${currentStreak} days in a row. You're not just writing—you're honoring yourself with presence and attention. This matters deeply.`
            : `Every entry you write is an act of self-care. You're choosing to understand yourself better, and that choice compounds over time.`,
          category: "This Is Your Superpower",
          format: "Strength Highlight",
          image: getRandomUnusedImage()
        },
        {
          title: `${entries.length} Entries of Growth`,
          quote: longestEntry?.content.split(/[.!?]/)[0] || "Each word captures a moment of your journey",
          message: entries.length > 10
            ? `${entries.length} entries. That's ${entries.length} times you've shown up for yourself. Look at how much ground you've covered.`
            : currentStreak > 7 
              ? `Over a week of consistent reflection. You're building a practice that will serve you for years. Keep going—you're creating something beautiful.`
              : `You're building a meaningful practice. Each entry is a gift to your future self. The consistency you're creating will compound in ways you can't yet see.`,
          category: "Celebrate This",
          format: "Progress Celebration",
          image: getRandomUnusedImage()
        }
      ];
    }

    // Assign images
    const cardsWithImages = wisdomData.map(card => ({
      ...card,
      image: card.image || getRandomUnusedImage()
    }));

    setWisdomCards(cardsWithImages);
    setShowWisdomCards(true);
    
    console.log('✅ Wisdom cards generated successfully');
    
  } catch (error) {
    console.error('❌ Error generating wisdom:', error);
    alert('Failed to generate wisdom cards. Please try again.');
  } finally {
    setIsGeneratingWisdom(false);
  }
};
useEffect(() => {
  const loadSavedWisdom = async () => {
    try {
      const saved = localStorage.getItem('personalWisdomCards');
      const savedState = localStorage.getItem('showWisdomCards'); 
      if (saved) {
        setWisdomCards(JSON.parse(saved));
        setShowWisdomCards(savedState === 'true'); 
      }
    } catch (error) {
      console.error('Error loading saved wisdom:', error);
    }
  };
  loadSavedWisdom();
}, []);
useEffect(() => {
  if (wisdomCards.length > 0) {
    localStorage.setItem('personalWisdomCards', JSON.stringify(wisdomCards));
  }
  localStorage.setItem('showWisdomCards', showWisdomCards.toString());
}, [wisdomCards, showWisdomCards]); 

useEffect(() => {
  if (wisdomCards.length === 0 || !showWisdomCards) return; 
  
  const interval = setInterval(() => {
    setCurrentWisdomIndex((prev) => (prev + 1) % wisdomCards.length);
  }, 6000);

  return () => clearInterval(interval);
}, [wisdomCards.length, showWisdomCards]); 

// Calculate mood statistics - TOP 3 MOODS ONLY
const calculateMoodStats = () => {
  if (entries.length === 0) {
    return [
      { name: 'Grateful', count: 0, percentage: 0, color: '#fbbf24' },
      { name: 'Peaceful', count: 0, percentage: 0, color: '#34d399' },
      { name: 'Creative', count: 0, percentage: 0, color: '#60a5fa' }
    ];
  }

  const moodCounts = { grateful: 0, peaceful: 0, creative: 0, thoughtful: 0 };
  
  entries.forEach(entry => {
    const mood = entry.mood.toLowerCase();
    if (moodCounts.hasOwnProperty(mood)) {
      moodCounts[mood]++;
    }
  });

  const total = entries.length;
  
  // Convert to array and sort by count
  const moodArray = [
    { name: 'Grateful', count: moodCounts.grateful, percentage: Math.round((moodCounts.grateful / total) * 100), color: '#fbbf24' },
    { name: 'Peaceful', count: moodCounts.peaceful, percentage: Math.round((moodCounts.peaceful / total) * 100), color: '#34d399' },
    { name: 'Creative', count: moodCounts.creative, percentage: Math.round((moodCounts.creative / total) * 100), color: '#60a5fa' },
    { name: 'Thoughtful', count: moodCounts.thoughtful, percentage: Math.round((moodCounts.thoughtful / total) * 100), color: '#a78bfa' }
  ];
  
  // Sort by count (descending) and return top 3
  return moodArray.sort((a, b) => b.count - a.count).slice(0, 3);
};
// Call these functions to get the stats
const timeStats = calculateWritingTimeStats();
const moodStats = calculateMoodStats(); // Returns array of top 3 moods


  // Entry form state
  const [newEntry, setNewEntry] = useState({
    title: '',
    content: '',
    tags: [],
    mood: '',
    tagInput: '',
    background: '',
  selectedImages: [], // Array of up to 3 images for entry
    writingStyle: '',
    journalPurpose: ''
  });
  
  // Load entries
  useEffect(() => {
    loadAllEntries();
  }, []);

  // Apply filters when search or filter changes
  useEffect(() => {
    applyFilters();
  }, [searchQuery, filterMood, entries]);

const handleResetPin = () => {
  if (resetPinStep === 1) {
    // ✅ STEP 1: VERIFY OLD PIN (SECURITY)
    if (oldPinInput !== appPin) {
      alert('❌ Incorrect PIN. Access Denied.');
      setOldPinInput('');
      return;
    }
    console.log('✅ PIN verified');
    setResetPinStep(2);
    setOldPinInput('');
    
  } else if (resetPinStep === 2) {
    // STEP 2: Enter new PIN
    if (newPinInput.length !== 4 || !/^\d{4}$/.test(newPinInput)) {
      alert('❌ PIN must be exactly 4 digits');
      setNewPinInput('');
      return;
    }
    console.log('✅ New PIN entered');
    setResetPinStep(3);
    
  } else if (resetPinStep === 3) {
    // STEP 3: Confirm new PIN matches
    if (newPinInput !== confirmPinInput) {
      alert('❌ PINs do not match');
      setConfirmPinInput('');
      return;
    }
    
    // ✅ ONLY NOW save the new PIN
    console.log('✅ PIN updated successfully');
    localStorage.setItem('journal_app_pin', newPinInput);
    setAppPin(newPinInput);
    
    // Reset all state
    setShowResetPin(false);
    setResetPinStep(1);
    setOldPinInput('');
    setNewPinInput('');
    setConfirmPinInput('');
    
    alert('✅ PIN has been reset successfully');
  }
};


  const cancelResetPin = () => {
    setShowResetPin(false);
    setResetPinStep(1);
    setOldPinInput('');
    setNewPinInput('');
    setConfirmPinInput('');
  };
const clearAllPinData = () => {
  const confirm = window.confirm('Are you sure you want to remove PIN protection? All entries will be unlocked.');
  if (confirm) {
    localStorage.removeItem('journal_app_pin');
    localStorage.removeItem('journal_locked_entries');
    setAppPin('');
    setLockedEntries([]);
    alert(' PIN protection removed');
  }
};
// ✅ NEW FIXED STREAK CALCULATION
const calculateStreak = (entries) => {
  if (!entries || entries.length === 0) {
    return 0;
  }

  // Get today at midnight (local timezone)
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayTime = today.getTime();

  // Convert all entry dates to midnight timestamps
  const entryDates = entries
    .map(entry => {
      const date = new Date(entry.date);
      date.setHours(0, 0, 0, 0);
      return date.getTime();
    })
    .sort((a, b) => b - a); // Sort descending (newest first)

  // Remove duplicates (multiple entries on same day count as one day)
  const uniqueDates = [...new Set(entryDates)];

  // Get most recent entry date
  const mostRecentDate = uniqueDates[0];
  const daysSinceLastEntry = Math.floor((todayTime - mostRecentDate) / (1000 * 60 * 60 * 24));

  // ✅ CRITICAL: If last entry was more than 1 day ago, streak is BROKEN
  if (daysSinceLastEntry > 1) {
    console.log('❌ Streak broken: Last entry was', daysSinceLastEntry, 'days ago');
    return 0;
  }

  // ✅ Count consecutive days working backwards from most recent
  let streak = 0;
  let expectedDate = mostRecentDate;

  for (const entryDate of uniqueDates) {
    const daysDiff = Math.floor((expectedDate - entryDate) / (1000 * 60 * 60 * 24));
    
    if (daysDiff === 0) {
      // Entry matches expected date
      streak++;
      expectedDate -= 1000 * 60 * 60 * 24; // Move to previous day
    } else {
      // Gap found, streak ends
      break;
    }
  }

  console.log('🔥 Current streak:', streak, 'days');
  return streak;
};
// ============================================
// STREAK SYSTEM - 3 useEffects (OPTIMIZED - no duplicates)
// ============================================

// 1️⃣ CLEANUP OLD STREAK DATA FOR EXISTING USERS (runs once)
useEffect(() => {
  const cleanupOldStreakData = () => {
    const hasRunCleanup = localStorage.getItem('streakCleanupV2Done');
    
    if (hasRunCleanup) {
      return; // Already done, skip
    }
    
    console.log('🧹 Cleaning up old streak data...');
    
    // Remove ALL old streak-related keys
    const oldKeys = [
      'streak',
      'streakCount', 
      'lastJournalDate',
      'oldStreakMigrated',
      'currentStreak'
    ];
    
    oldKeys.forEach(key => {
      if (localStorage.getItem(key)) {
        localStorage.removeItem(key);
      }
    });
    
    // Mark cleanup as done FIRST (prevent re-runs)
    localStorage.setItem('streakCleanupV2Done', 'true');
    console.log('✅ Streak cleanup complete');
  };
  
  // Run cleanup only once when entries first load
  if (!isLoading && entries.length >= 0) {
    cleanupOldStreakData();
  }
}, [isLoading]); // Only depend on isLoading, run once

// 2️⃣ CHECK STREAK ON APP LOAD - Reset if needed based on timezone/date
useEffect(() => {
  // Skip if still loading or no entries loaded yet
  if (isLoading) return;
  
  const checkStreakOnLoad = () => {
    const stored = localStorage.getItem('currentStreak');
    
    // If no stored streak, calculate fresh
    if (!stored) {
      const newStreak = calculateStreak(entries);
      setDisplayStreak(newStreak);
      
      localStorage.setItem('currentStreak', JSON.stringify({
        value: newStreak,
        lastCalculated: new Date().toISOString()
      }));
      console.log('🔥 Initial streak calculated:', newStreak);
      return;
    }
    
    try {
      const streakData = JSON.parse(stored);
      const lastChecked = new Date(streakData.lastCalculated);
      const now = new Date();
      
      // Set both to midnight for comparison
      lastChecked.setHours(0, 0, 0, 0);
      now.setHours(0, 0, 0, 0);
      
      // Calculate days difference
      const daysDiff = Math.floor((now - lastChecked) / (1000 * 60 * 60 * 24));
      
      // Only recalculate if it's a different day
      if (daysDiff > 0) {
        console.log('🔄 New day detected - recalculating streak...');
        const newStreak = calculateStreak(entries);
        setDisplayStreak(newStreak);
        
        localStorage.setItem('currentStreak', JSON.stringify({
          value: newStreak,
          lastCalculated: new Date().toISOString()
        }));
        
        console.log('🔥 Streak updated for new day:', newStreak);
      } else {
        // Same day, use cached streak (no recalculation)
        setDisplayStreak(streakData.value);
        console.log('✅ Using cached streak:', streakData.value);
      }
    } catch (error) {
      console.error('❌ Error reading streak, recalculating:', error);
      const newStreak = calculateStreak(entries);
      setDisplayStreak(newStreak);
      
      localStorage.setItem('currentStreak', JSON.stringify({
        value: newStreak,
        lastCalculated: new Date().toISOString()
      }));
    }
  };
  
  checkStreakOnLoad();
}, [isLoading]); // Only run when loading completes

// 3️⃣ UPDATE STREAK WHEN ENTRIES CHANGE (add/delete)
useEffect(() => {
  // Skip during initial load
  if (isLoading) return;
  
  // Skip if entries haven't actually changed (prevent duplicate calculations)
  const stored = localStorage.getItem('currentStreak');
  if (stored) {
    try {
      const streakData = JSON.parse(stored);
      const now = new Date();
      const lastChecked = new Date(streakData.lastCalculated);
      
      // If calculated within last 5 seconds, skip (prevents duplicates)
      const secondsDiff = (now - lastChecked) / 1000;
      if (secondsDiff < 5) {
        return;
      }
    } catch (error) {
      // Continue with recalculation if error
    }
  }
  
  const newStreak = calculateStreak(entries);
  setDisplayStreak(newStreak);
  
  localStorage.setItem('currentStreak', JSON.stringify({
    value: newStreak,
    lastCalculated: new Date().toISOString()
  }));
  
  console.log('🔥 Streak recalculated (entries changed):', newStreak);
}, [entries]); // Run when entries array changes
// Sync speech transcript with content
useEffect(() => {
  if (transcript && (showAddModal || showEditModal)) {
    if (showAddModal && showWritingForm) {
      setNewEntry(prev => ({
        ...prev,
        content: prev.content + transcript
      }));
    } else if (showEditModal && selectedEntry) {
      setSelectedEntry(prev => ({
        ...prev,
        content: prev.content + transcript
      }));
    }
    resetTranscript();
  }
}, [transcript]);
  useEffect(() => {
  if (showEditModal && selectedEntry) {
    // Track if user made changes
    setHasUnsavedChanges(false);
  }
}, [showEditModal, selectedEntry]);

// Add a separate effect to detect changes
useEffect(() => {
  if (showEditModal && selectedEntry) {
    setHasUnsavedChanges(true);
  }
}, [selectedEntry?.title, selectedEntry?.content, selectedEntry?.mood, selectedEntry?.selectedImages]);
const loadAllEntries = async () => {
  setIsLoading(true);
  try {
    // ✅ CRITICAL: Check if service is ready
    if (!journalStorage.userId) {
      console.warn('⚠️ Journal storage not ready, waiting...');
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      if (!journalStorage.userId) {
        console.error('❌ Cannot load entries: Service not initialized');
        setIsLoading(false);
        return;
      }
    }
    
    const startTime = Date.now();
    const minLoadTime = 800;
    
    const journals = await journalStorage.getAllJournals();
    
    console.log('📚 Loading journals from database:', journals.length);
    
const convertedEntries = journals.map(journal => {
  const isLocked = lockedEntries.includes(journal.id);
  
  return {
    id: journal.id,
    date: new Date(journal.createdAt || journal.sessionStartTime || Date.now()),
    title: isLocked ? 'Locked Entry' : (journal.title || journal.header || 'Untitled Entry'),
    content: isLocked ? 'This entry is protected. Enter PIN to view.' : (journal.summary || journal.insights || 'No content'),
    tags: journal.tags && Array.isArray(journal.tags) ? journal.tags : [],
    mood: journal.mood || 'thoughtful',  // ✅ Use actual mood field from Firestore!
    background: journal.imageUrl || journal.background || '/image/session-img13.png',
    selectedImages: journal.selectedImages || [],
    images: journal.images || [],
    createdAt: journal.createdAt,
    isLocked: isLocked
  };
});

    const elapsedTime = Date.now() - startTime;
    if (elapsedTime < minLoadTime) {
      await new Promise(resolve => setTimeout(resolve, minLoadTime - elapsedTime));
    }

    setEntries(convertedEntries);
    setFilteredEntries(convertedEntries);
    
    console.log('✅ Entries loaded:', convertedEntries.length);
  } catch (error) {
    console.error('❌ Error loading journals:', error);
    setEntries([]);
    setFilteredEntries([]);
  } finally {
    setIsLoading(false);
  }
};

 const applyFilters = () => {
  let filtered = [...entries];

  // Apply search filter on title, content, AND tags
  if (searchQuery.trim()) {
    const query = searchQuery.toLowerCase();
    filtered = filtered.filter(entry => {
      const titleMatch = entry.title?.toLowerCase().includes(query);
      const contentMatch = entry.content?.toLowerCase().includes(query);
      const tagsMatch = entry.tags?.some(tag => tag.toLowerCase().includes(query));
      
      return titleMatch || contentMatch || tagsMatch;
    });
  }

  // Apply mood filter - check the actual mood field, not tags[0]
  if (filterMood !== 'all') {
    filtered = filtered.filter(entry => {
      // Check both mood field and if tag includes the mood value
      return entry.mood?.toLowerCase() === filterMood.toLowerCase() || 
             entry.tags?.some(tag => tag.toLowerCase() === filterMood.toLowerCase());
    });
  }

  setFilteredEntries(filtered);
};



  const verifyPin = (entryId, action) => {
    if (!appPin) {
      setShowPinSetup(true);
      return;
    }
    setCurrentLockAction({ entryId, action });
    setShowPinModal(true);
  };

  // Image compression handler
  const handleImageUpload = async (event, isMultiple = false) => {
    const files = Array.from(event.target.files);
    
    if (files.length === 0) return;

    try {
      const compressedImages = await Promise.all(
        files.map(async (file) => {
          try {
            const compressed = await compressImage(file, 1200);
            return {
              data: compressed,
              id: Date.now() + Math.random(),
              width: 1200,
              height: 800,
              aspectRatio: 3/2
            };
          } catch (error) {
            console.error('Error compressing image:', error);
            return null;
          }
        })
      );

      const validImages = compressedImages.filter(img => img !== null);
      
      if (isMultiple) {
        setNewEntry(prev => ({
          ...prev,
          selectedImages: [...prev.selectedImages, ...validImages].slice(0, 3)
        }));
      } else {
        setNewEntry(prev => ({
          ...prev,
          background: validImages[0]?.data || ''
        }));
      }
    } catch (error) {
      console.error('Error handling image upload:', error);
      alert('Failed to process image. Please try again.');
    }
  };

const handleBackgroundSelect = (bgValue) => {
  const currentImages = newEntry.selectedImages || [];
  
  // Check if already selected
  const isAlreadySelected = currentImages.some(img => img.data === bgValue);
  
  if (isAlreadySelected) {
    // Remove if already selected
    setNewEntry({
      ...newEntry,
      selectedImages: currentImages.filter(img => img.data !== bgValue)
    });
  } else {
    // Add if under 3 images
    if (currentImages.length < 3) {
      const newImage = {
        data: bgValue,
        id: Date.now() + Math.random(),
        width: 1920,
        height: 1080,
        aspectRatio: 16/9
      };
      setNewEntry({
        ...newEntry,
        selectedImages: [...currentImages, newImage]
      });
    } else {
      alert('You can only select up to 3 background images');
    }
  }
};

// Handle multiple image selection for entries
const handleEntryImageUpload = (event) => {
  const files = Array.from(event.target.files);
  
  if (newEntry.selectedImages.length + files.length > 3) {
    alert('You can only select up to 3 images per entry');
    return;
  }

  const validFiles = files.filter(file => {
    if (!file.type.startsWith('image/')) {
      alert(`${file.name} is not an image file`);
      return false;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert(`${file.name} is too large (max 5MB)`);
      return false;
    }
    return true;
  });

  const readPromises = validFiles.map(file => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new Image();
        img.onload = () => {
          resolve({
            data: reader.result,
            id: Date.now() + Math.random(),
            width: img.width,
            height: img.height,
            aspectRatio: img.width / img.height
          });
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    });
  });

  Promise.all(readPromises).then(images => {
    setNewEntry({
      ...newEntry,
      selectedImages: [...newEntry.selectedImages, ...images]
    });
  });
};

// Remove selected image
const removeEntryImage = (imageId) => {
  setNewEntry({
    ...newEntry,
    selectedImages: newEntry.selectedImages.filter(img => img.id !== imageId)
  });
};

// Get grid layout class based on image count
const getImageGridClass = (count) => {
  switch(count) {
    case 1: return 'single-image';
    case 2: return 'two-images';
    case 3: return 'three-images';
    default: return '';
  }
};
  const affirmations = [
    {
      title: "Positive Energy",
      message: "Each entry is a gift to your future self. Keep going and stay consistent!",
      image: "/image/session-img8.png",
      tag: "Motivational Quote"
    },
    {
      title: "Inner Peace",
      message: "Writing is not just remembering, it's a way of discovering yourself.",
      image: "/image/session-img31.png",
      tag: "Affirmation"
    },
    {
      title: "Daily Wisdom",
      message: "In the quiet moments of writing, you'll find clarity and calm.",
      image: "/image/session-img32.png",
      tag: "Motivational Quote"
    },
    {
      title: "Mindful Moment",
      message: "Your thoughts and feelings are valid. Take time to honor them.",
      image: "/image/session-img33.png",
      tag: "Affirmation"
    },
    {
      title: "Today's Reflection",
      message: "Every word you write is a step toward understanding yourself better.",
      image: "/image/session-img24.png",
      tag: "Affirmation"
    },
  ];

  const currentAffirmation = affirmations[currentAffirmationIndex];

  // Load all stored entries from JournalStorageService
  useEffect(() => {
    loadAllEntries();
  }, []);

  // Auto-slide affirmations every 8 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentAffirmationIndex((prev) => (prev + 1) % affirmations.length);
    }, 8000);

    return () => clearInterval(interval);
  }, []);

  const moodOptions = [
    { 
      value: 'grateful', 
      label: 'Feeling grateful',
      svg:<svg className="w-6 h-6" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
        <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M17 15.5V5s3 1 3 4m-7-3H4m9 4H4m4 4H4m13 2.4c0 1.326-1.343 2.4-3 2.4s-3-1.075-3-2.4 1.343-2.4 3-2.4 3 1.075 3 2.4Z"/>
      </svg>
    },
    { 
      value: 'peaceful', 
      label: 'Peaceful and calm',
      svg: <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M14.7649 6.07596C14.9991 6.22231 15.0703 6.53079 14.9239 6.76495C14.4849 7.46743 13.9632 8.10645 13.3702 8.66305L14.5712 9.86406C14.7664 10.0593 14.7664 10.3759 14.5712 10.5712C14.3759 10.7664 14.0593 10.7664 13.8641 10.5712L12.6011 9.30817C11.805 9.90283 10.9089 10.3621 9.93375 10.651L10.383 12.3277C10.4544 12.5944 10.2961 12.8685 10.0294 12.94C9.76267 13.0115 9.4885 12.8532 9.41704 12.5865L8.95917 10.8775C8.48743 10.958 8.00036 10.9999 7.50001 10.9999C6.99965 10.9999 6.51257 10.958 6.04082 10.8775L5.58299 12.5864C5.51153 12.8532 5.23737 13.0115 4.97064 12.94C4.7039 12.8686 4.5456 12.5944 4.61706 12.3277L5.06625 10.651C4.09111 10.3621 3.19503 9.90282 2.3989 9.30815L1.1359 10.5712C0.940638 10.7664 0.624058 10.7664 0.428798 10.5712C0.233537 10.3759 0.233537 10.0593 0.428798 9.86405L1.62982 8.66303C1.03682 8.10643 0.515113 7.46742 0.0760677 6.76495C-0.0702867 6.53079 0.000898544 6.22231 0.235065 6.07596C0.469231 5.9296 0.777703 6.00079 0.924058 6.23496C1.40354 7.00213 1.989 7.68057 2.66233 8.2427C2.67315 8.25096 2.6837 8.25972 2.69397 8.26898C4.00897 9.35527 5.65537 9.99991 7.50001 9.99991C10.3078 9.99991 12.6564 8.5063 14.076 6.23495C14.2223 6.00079 14.5308 5.9296 14.7649 6.07596Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"/>
      </svg>
    },
    { 
      value: 'creative', 
      label: 'Creatively inspired',
      svg: <svg className="w-6 h-6" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
        <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M15 13h2c1.1046 0 2 .8954 2 2s-.8954 2-2 2h-2.5M10 3c0 2.4-3 1.6-3 4m8-4c0 2.4-3 1.6-3 4m-7 4 .6398 6.398C5.84428 19.4428 7.56494 21 9.61995 21H10.38c2.0551 0 3.7757-1.5572 3.9802-3.602L15 11H5Z"/>
      </svg>
    },
    { 
      value: 'thoughtful', 
      label: 'Deep in thought',
      svg:<svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M2.89998 0.499976C2.89998 0.279062 2.72089 0.0999756 2.49998 0.0999756C2.27906 0.0999756 2.09998 0.279062 2.09998 0.499976V1.09998H1.49998C1.27906 1.09998 1.09998 1.27906 1.09998 1.49998C1.09998 1.72089 1.27906 1.89998 1.49998 1.89998H2.09998V2.49998C2.09998 2.72089 2.27906 2.89998 2.49998 2.89998C2.72089 2.89998 2.89998 2.72089 2.89998 2.49998V1.89998H3.49998C3.72089 1.89998 3.89998 1.72089 3.89998 1.49998C3.89998 1.27906 3.72089 1.09998 3.49998 1.09998H2.89998V0.499976ZM5.89998 3.49998C5.89998 3.27906 5.72089 3.09998 5.49998 3.09998C5.27906 3.09998 5.09998 3.27906 5.09998 3.49998V4.09998H4.49998C4.27906 4.09998 4.09998 4.27906 4.09998 4.49998C4.09998 4.72089 4.27906 4.89998 4.49998 4.89998H5.09998V5.49998C5.09998 5.72089 5.27906 5.89998 5.49998 5.89998C5.72089 5.89998 5.89998 5.72089 5.89998 5.49998V4.89998H6.49998C6.72089 4.89998 6.89998 4.72089 6.89998 4.49998C6.89998 4.27906 6.72089 4.09998 6.49998 4.09998H5.89998V3.49998ZM1.89998 6.49998C1.89998 6.27906 1.72089 6.09998 1.49998 6.09998C1.27906 6.09998 1.09998 6.27906 1.09998 6.49998V7.09998H0.499976C0.279062 7.09998 0.0999756 7.27906 0.0999756 7.49998C0.0999756 7.72089 0.279062 7.89998 0.499976 7.89998H1.09998V8.49998C1.09998 8.72089 1.27906 8.89997 1.49998 8.89997C1.72089 8.89997 1.89998 8.72089 1.89998 8.49998V7.89998H2.49998C2.72089 7.89998 2.89998 7.72089 2.89998 7.49998C2.89998 7.27906 2.72089 7.09998 2.49998 7.09998H1.89998V6.49998ZM8.54406 0.98184L8.24618 0.941586C8.03275 0.917676 7.90692 1.1655 8.02936 1.34194C8.17013 1.54479 8.29981 1.75592 8.41754 1.97445C8.91878 2.90485 9.20322 3.96932 9.20322 5.10022C9.20322 8.37201 6.82247 11.0878 3.69887 11.6097C3.45736 11.65 3.20988 11.6772 2.96008 11.6906C2.74563 11.702 2.62729 11.9535 2.77721 12.1072C2.84551 12.1773 2.91535 12.2458 2.98667 12.3128L3.05883 12.3795L3.31883 12.6045L3.50684 12.7532L3.62796 12.8433L3.81491 12.9742L3.99079 13.089C4.11175 13.1651 4.23536 13.2375 4.36157 13.3059L4.62496 13.4412L4.88553 13.5607L5.18837 13.6828L5.43169 13.7686C5.56564 13.8128 5.70149 13.8529 5.83857 13.8885C5.94262 13.9155 6.04767 13.9401 6.15405 13.9622C6.27993 13.9883 6.40713 14.0109 6.53544 14.0298L6.85241 14.0685L7.11934 14.0892C7.24637 14.0965 7.37436 14.1002 7.50322 14.1002C11.1483 14.1002 14.1032 11.1453 14.1032 7.50023C14.1032 7.25044 14.0893 7.00389 14.0623 6.76131L14.0255 6.48407C13.991 6.26083 13.9453 6.04129 13.8891 5.82642C13.8213 5.56709 13.7382 5.31398 13.6409 5.06881L13.5279 4.80132L13.4507 4.63542L13.3766 4.48666C13.2178 4.17773 13.0353 3.88295 12.8312 3.60423L12.6782 3.40352L12.4793 3.16432L12.3157 2.98361L12.1961 2.85951L12.0355 2.70246L11.8134 2.50184L11.4925 2.24191L11.2483 2.06498L10.9562 1.87446L10.6346 1.68894L10.3073 1.52378L10.1938 1.47176L9.95488 1.3706L9.67791 1.2669L9.42566 1.1846L9.10075 1.09489L8.83599 1.03486L8.54406 0.98184ZM10.4032 5.30023C10.4032 4.27588 10.2002 3.29829 9.83244 2.40604C11.7623 3.28995 13.1032 5.23862 13.1032 7.50023C13.1032 10.593 10.596 13.1002 7.50322 13.1002C6.63646 13.1002 5.81597 12.9036 5.08355 12.5522C6.5419 12.0941 7.81081 11.2082 8.74322 10.0416C8.87963 10.2284 9.10028 10.3497 9.34928 10.3497C9.76349 10.3497 10.0993 10.0139 10.0993 9.59971C10.0993 9.24256 9.84965 8.94373 9.51535 8.86816C9.57741 8.75165 9.63653 8.63334 9.6926 8.51332C9.88358 8.63163 10.1088 8.69993 10.35 8.69993C11.0403 8.69993 11.6 8.14028 11.6 7.44993C11.6 6.75976 11.0406 6.20024 10.3505 6.19993C10.3853 5.90487 10.4032 5.60464 10.4032 5.30023Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"/>
      </svg>
    }
  ];

  const moodColors = {
    grateful: { bg: '#fef3c7', border: '#fcd34d', text: '#78350f' },
    reflective: { bg: '#e9d5ff', border: '#c084fc', text: '#581c87' },
    peaceful: { bg: '#d1fae5', border: '#86efac', text: '#065f46' },
    creative: { bg: '#dbeafe', border: '#93c5fd', text: '#1e3a8a' },
    thoughtful: { bg: '#e9d5ff', border: '#c084fc', text: '#581c87' }
  };

  const backgroundImages = [
    { value: '/image/session-img13.png', preview: '/image/session-img13.png' },
    { value: '/image/session-img32.png', preview: '/image/session-img32.png' },
    { value: '/image/session-img8.png', preview: '/image/session-img8.png' },
    { value: '/image/session-img26.png', preview: '/image/session-img26.png' },
    { value: '/image/session-img17.png', preview: '/image/session-img17.png' },
    { value: '/image/session-img10.png', preview: '/image/session-img10.png' },
    { value: '/image/session-img14.png', preview: '/image/session-img14.png' },
    { value: '/image/session-img16.png', preview: '/image/session-img16.png' },
    { value: '/image/session-img12.png', preview: '/image/session-img12.png' },
    { value: '/image/session-img11.png', preview: '/image/session-img11.png' },
    { value: '/image/session-img20.png', preview: '/image/session-img20.png' },
    { value: '/image/session-img21.png', preview: '/image/session-img21.png' },
    { value: '/image/session-img22.png', preview: '/image/session-img22.png' },
    { value: '/image/session-img49.png', preview: '/image/session-img49.png' },
    { value: '/image/session-img24.png', preview: '/image/session-img24.png' },
    { value: '/image/session-img31.png', preview: '/image/session-img31.png' },
  ];

  const blobUrlToBase64 = async (blobUrl) => {
  try {
    const response = await fetch(blobUrl);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Error converting blob to base64:', error);
    return null;
  }
};

const base64ToObjectUrl = (base64) => {
  try {
    // Extract the base64 data and mime type
    const parts = base64.split(',');
    const mimeMatch = parts[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : 'audio/mpeg';
    const bstr = atob(parts[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    const blob = new Blob([u8arr], { type: mime });
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error('Error converting base64 to object URL:', error);
    return null;
  }
};

  const writingStyles = ['Quick thoughts', 'Detailed reflection', 'Creative writing', 'Gratitude list'];
  const journalPurposes = ['Personal growth', 'Mental wellness', 'Daily memories', 'Goal tracking'];

  const questions = [
    { id: 1, question: 'How are you feeling today?', type: 'mood', options: moodOptions },
    { id: 2, question: "What's your writing style today?", type: 'writingStyle', options: writingStyles },
    { id: 3, question: "What's the purpose of this entry?", type: 'journalPurpose', options: journalPurposes },
    { id: 4, question: 'Choose a background for your entry', type: 'background', options: backgroundImages }
  ];

  const handleNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      setShowWritingForm(true);
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

const handleAddEntry = async () => {
  if (!newEntry.title.trim() || !newEntry.content.trim()) {
    alert('Please add a title and content for your journal entry');
    return;
  }

  try {
    const skipLockPrompt = localStorage.getItem('skipLockPrompt') === 'true';
    let shouldLock = false;

    if (!skipLockPrompt) {
      const response = await new Promise((resolve) => {
        const overlay = document.createElement('div');
        overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.5);backdrop-filter:blur(8px);z-index:99999;display:flex;align-items:center;justify-content:center;padding:20px';
        
        const modal = document.createElement('div');
        modal.style.cssText = 'background:white;border-radius:20px;padding:24px;max-width:400px;width:100%;box-shadow:0 20px 40px rgba(0,0,0,0.2)';
        
        modal.innerHTML = `
          <h3 style="font-size:18px;font-weight:600;color:#1f2937;margin:0 0 12px 0">Lock this entry?</h3>
          <p style="font-size:14px;color:#6b7280;margin-bottom:20px;line-height:1.6">Do you want to protect this entry with your PIN?</p>
          <label style="display:flex;align-items:center;gap:8px;margin-bottom:20px;font-size:13px;color:#374151;cursor:pointer">
            <input type="checkbox" id="dontAskAgain" style="width:16px;height:16px;cursor:pointer">
            <span>Don't ask me again</span>
          </label>
          <div style="display:flex;gap:12px">
            <button id="noBtn" style="flex:1;padding:10px;background:#f3f4f6;color:black;border:none;border-radius:12px;font-size:13px;font-weight:500;cursor:pointer">No</button>
            <button id="yesBtn" style="flex:1;padding:10px;background:#272727;color:white;border:none;border-radius:12px;font-size:13px;font-weight:500;cursor:pointer">Yes, Lock It</button>
          </div>
        `;
        
        overlay.appendChild(modal);
        document.body.appendChild(overlay);
        
        const dontAskCheckbox = modal.querySelector('#dontAskAgain');
        
        modal.querySelector('#yesBtn').onclick = () => {
          if (dontAskCheckbox.checked) {
            localStorage.setItem('skipLockPrompt', 'true');
            localStorage.setItem('autoLockEntries', 'true');
          }
          document.body.removeChild(overlay);
          resolve(true);
        };
        
        modal.querySelector('#noBtn').onclick = () => {
          if (dontAskCheckbox.checked) {
            localStorage.setItem('skipLockPrompt', 'true');
            localStorage.setItem('autoLockEntries', 'false');
          }
          document.body.removeChild(overlay);
          resolve(false);
        };
      });
      
      shouldLock = response;
    } else {
      shouldLock = localStorage.getItem('autoLockEntries') === 'true';
    }

    if (shouldLock && !appPin) {
      alert('Please set up a PIN first');
      setShowPinSetup(true);
      return;
    }

    const contentToSave = newEntry.content;
    const titleToSave = newEntry.title;

    const imagesToSave = await Promise.all(
      (newEntry.selectedImages || []).map(async (img, index) => {
        try {
          const base64Data = img.data || img;
          
          console.log(`🗜️ Compressing image ${index + 1}...`);
          
          return new Promise((resolve) => {
            const image = new Image();
            image.onload = () => {
              const canvas = document.createElement('canvas');
              let width = image.width;
              let height = image.height;
              
              const maxDimension = 800;
              if (width > maxDimension || height > maxDimension) {
                if (width > height) {
                  height = (height / width) * maxDimension;
                  width = maxDimension;
                } else {
                  width = (width / height) * maxDimension;
                  height = maxDimension;
                }
              }
              
              canvas.width = width;
              canvas.height = height;
              const ctx = canvas.getContext('2d');
              ctx.drawImage(image, 0, 0, width, height);
              
              let quality = 0.6;
              let compressedData = canvas.toDataURL('image/jpeg', quality);
              let sizeInBytes = (compressedData.length * 3) / 4;
              
              while (sizeInBytes > 300 * 1024 && quality > 0.1) {
                quality -= 0.1;
                compressedData = canvas.toDataURL('image/jpeg', quality);
                sizeInBytes = (compressedData.length * 3) / 4;
              }
              
              console.log(`✅ Compressed to ${(sizeInBytes / 1024).toFixed(0)}KB`);
              resolve(compressedData);
            };
            image.src = base64Data;
          });
        } catch (error) {
          console.error('Error compressing image:', error);
          return img.data || img;
        }
      })
    );

    const journalData = {
      title: titleToSave,
      header: titleToSave,
      summary: contentToSave,
      insights: `Mood: ${newEntry.mood || 'Not specified'}\nStyle: ${newEntry.writingStyle || 'Not specified'}`,
      conversation: [{
        type: 'user',
        content: contentToSave,
        timestamp: new Date().toISOString()
      }],
      tags: newEntry.tags,
      mood: newEntry.mood || 'thoughtful',
      background: imagesToSave[0] || '',
      selectedImages: imagesToSave,
      writingStyle: newEntry.writingStyle,
      journalPurpose: newEntry.journalPurpose,
      sessionStartTime: new Date().toISOString(),
      question: `Journal Purpose: ${newEntry.journalPurpose || 'Not specified'}`,
      isLocked: shouldLock
    };

    console.log('💾 Saving journal entry with images:', journalData);

    const savedJournal = await journalStorage.saveJournal(journalData);

    if (savedJournal) {
      console.log('✅ Journal saved successfully:', savedJournal);

      const entry = {
        id: savedJournal.id,
        date: new Date(savedJournal.createdAt),
        title: shouldLock ? '🔒 Locked Entry' : newEntry.title,
        content: shouldLock ? 'This entry is protected. Enter PIN to view.' : newEntry.content,
        tags: savedJournal.tags,
        mood: newEntry.mood,
        background: imagesToSave[0] || '',
        selectedImages: imagesToSave,
        writingStyle: newEntry.writingStyle,
        journalPurpose: newEntry.journalPurpose,
        isLocked: shouldLock
      };

      if (shouldLock) {
        const newLockedEntries = [...lockedEntries, savedJournal.id];
        setLockedEntries(newLockedEntries);
        localStorage.setItem('journal_locked_entries', JSON.stringify(newLockedEntries));
      }

      const updatedEntries = [entry, ...entries];
      setEntries(updatedEntries);
      
      if (notificationsEnabled) {
        console.log('📬 Updating journal notifications after new entry...');
        await updateJournalNotifications(updatedEntries, userName);
      }
      
      // ✅ SHOW SUCCESS ANIMATION
      setShowSaveSuccess(true);
      
      // Wait for animation (1.2 seconds)
      await new Promise(resolve => setTimeout(resolve, 1200));
      
      setShowSaveSuccess(false);

      // ✅ THEN close modal and reload
      resetModal();
      await loadAllEntries();
      
      const isFirstEntry = entries.length === 0;
      
      if (isFirstEntry) {
        setShowCongratulationsModal(true);
      } else {
        checkMilestones(updatedEntries.length);
      }      
    }
  } catch (error) {
    console.error('❌ Error saving journal entry:', error);
    alert('Failed to save journal entry. Please try again.');
  }
};

const handleUpdateEntry = async () => {
  if (!selectedEntry || isSavingChanges) return;

  try {
    setIsSavingChanges(true);
    
    if (!journalStorage.userId) {
      const service = firestoreService || window.firestoreService;
      if (!service?.currentUser?.uid) {
        throw new Error('Cannot update: User not authenticated.');
      }
      await journalStorage.initialize(service.currentUser.uid);
    }
    
    // ✅ FIX: Get ALL images from selectedEntry
    const imagesToSave = selectedEntry.selectedImages && selectedEntry.selectedImages.length > 0
      ? selectedEntry.selectedImages
      : [];

    console.log('📸 BEFORE compression:', imagesToSave.length, 'images');

    // Compress each image
    const compressedImages = await Promise.all(
      imagesToSave.map(async (img, index) => {
        try {
          const imgData = typeof img === 'string' ? img : (img.data || img);
          
          return new Promise((resolve) => {
            const image = new Image();
            image.onload = () => {
              const canvas = document.createElement('canvas');
              let width = image.width;
              let height = image.height;
              const maxDimension = 800;
              
              if (width > maxDimension || height > maxDimension) {
                if (width > height) {
                  height = (height / width) * maxDimension;
                  width = maxDimension;
                } else {
                  width = (width / height) * maxDimension;
                  height = maxDimension;
                }
              }
              
              canvas.width = width;
              canvas.height = height;
              const ctx = canvas.getContext('2d');
              ctx.drawImage(image, 0, 0, width, height);
              
              let quality = 0.6;
              let compressedData = canvas.toDataURL('image/jpeg', quality);
              let sizeInBytes = (compressedData.length * 3) / 4;
              
              while (sizeInBytes > 300 * 1024 && quality > 0.1) {
                quality -= 0.1;
                compressedData = canvas.toDataURL('image/jpeg', quality);
                sizeInBytes = (compressedData.length * 3) / 4;
              }
              
              console.log(`✅ Image ${index + 1} compressed to ${(sizeInBytes / 1024).toFixed(0)}KB`);
              resolve(compressedData);
            };
            image.onerror = () => {
              console.warn(`⚠️ Failed to compress image ${index + 1}, using original`);
              resolve(imgData);
            };
            image.src = imgData;
          });
        } catch (error) {
          console.error(`Error compressing image ${index}:`, error);
          return img.data || img;
        }
      })
    );

    console.log('📸 AFTER compression:', compressedImages.length, 'images');

    // ✅ CRITICAL: Save to BOTH images AND selectedImages
    const updatedEntry = {
      title: selectedEntry.title || 'Untitled Entry',
      content: selectedEntry.content || '',
      mood: selectedEntry.mood || 'thoughtful',
      tags: selectedEntry.tags || [],
      images: compressedImages,           // ✅ ALL IMAGES
      selectedImages: compressedImages,   // ✅ ALL IMAGES (same array)
      background: compressedImages[0] || '',
      imageUrl: compressedImages[0] || '',
      lastModified: new Date().toISOString()
    };

    console.log('💾 SAVING to database:', updatedEntry.images.length, 'images in both fields');

    await journalStorage.updateJournal(selectedEntry.id, updatedEntry);

    setIsSavingChanges(false);
    setShowSuccessCheck(true);
    await new Promise(resolve => setTimeout(resolve, 1200));
    
    setShowSuccessCheck(false);
    setShowEditModal(false);
    setSelectedEntry(null);
    setHasUnsavedChanges(false);
    await loadAllEntries();

  } catch (error) {
    console.error('❌ Error updating journal:', error);
    alert(`Failed to update: ${error.message}`);
    setIsSavingChanges(false);
  }
};


// The handleDeleteEntry function is already fixed, here it is again:
const handleDeleteEntry = async (id) => {
  // Removed confirmation dialog from here
  try {
    // Delete from storage
    await journalStorage.deleteJournal(id);
    
    // Update local state
    setEntries(entries.filter(e => e.id !== id));
    
    // Remove from locked entries if it was locked
    if (lockedEntries.includes(id)) {
      const newLockedEntries = lockedEntries.filter(entryId => entryId !== id);
      setLockedEntries(newLockedEntries);
      localStorage.setItem('journal_locked_entries', JSON.stringify(newLockedEntries));
    }
    
    setShowEditModal(false);
    setSelectedEntry(null);
    alert('Entry deleted successfully');
  } catch (error) {
    console.error('Error deleting entry:', error);
    alert('❌ Failed to delete entry. Please try again.');
  }
};

  const addTag = (tagInput, isEdit = false) => {
    const tag = tagInput.trim().toLowerCase();
    if (!tag) return;
    if (isEdit && selectedEntry) {
      if (!selectedEntry.tags.includes(tag)) {
        setSelectedEntry({ ...selectedEntry, tags: [...selectedEntry.tags, tag] });
      }
    } else {
      if (!newEntry.tags.includes(tag)) {
        setNewEntry({ ...newEntry, tags: [...newEntry.tags, tag], tagInput: '' });
      }
    }
  };

  const removeTag = (tag, isEdit = false) => {
    if (isEdit && selectedEntry) {
      setSelectedEntry({ ...selectedEntry, tags: selectedEntry.tags.filter(t => t !== tag) });
    } else {
      setNewEntry({ ...newEntry, tags: newEntry.tags.filter(t => t !== tag) });
    }
  };

  const formatDate = (date) => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

const resetModal = () => {
  setShowAddModal(false);
  setShowWritingForm(false);
  setCurrentQuestionIndex(0);
  setNewEntry({ 
    title: '', 
    content: '', 
    tags: [], 
    mood: '', 
    tagInput: '', 
    background: '', 
    selectedImages: [],
    writingStyle: '', 
    journalPurpose: '' 
  });
  if (isListening) {
    stopListening();
  }
  resetTranscript();
};
  const currentQuestion = questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / questions.length) * 100;

  const nextAffirmation = () => {
    setCurrentAffirmationIndex((prev) => (prev + 1) % affirmations.length);
  };

  const previousAffirmation = () => {
    setCurrentAffirmationIndex((prev) => (prev - 1 + affirmations.length) % affirmations.length);
  };

  const handleThumbsUp = () => {
    console.log('Thumbs up for:', currentAffirmation.title);
  };

  const handleThumbsDown = () => {
    console.log('Thumbs down for:', currentAffirmation.title);
  };

  // Generate reflection using EXISTING podcast system from ChatMode
const handleGenerateReflection = async () => {
  setIsGeneratingReflection(true);
  setGenerationStage('Analyzing your journey deeply...');
    setShowPodcastSettings(false);
  
  try {
    const recentEntries = entries.slice(0, 10);
    
    // Calculate stats
    const journeyStartDate = entries[entries.length - 1]?.date || new Date();
    const journeyEndDate = entries[0]?.date || new Date();
    const journeyDays = Math.ceil((journeyEndDate - journeyStartDate) / (1000 * 60 * 60 * 24));
    
    const sortedDates = entries.map(e => e.date).sort((a, b) => b - a);
 // Calculate streak - REPLACE the old calculation  
const currentStreak = calculateStreak(entries);
    
    const moodData = calculateMoodStats();
    const timeData = calculateWritingTimeStats();
    
    setGenerationStage('Extracting your most powerful moments...');
    
 const wisdomQuotes = wisdomCards.length > 0 
  ? wisdomCards.slice(0, 2).map(w => `"${w.quote}"`).join('\n')
  : '';

const peakTime = Object.entries(timeData).sort((a, b) => b[1] - a[1])[0][0];

const hostInstructions = getHostPersonalityInstructions(podcastPreferences.hostPersonality);
const focusInstructions = getFocusInstructions(podcastPreferences.focus);
    const powerfulQuotes = extractPowerfulQuotes(entries);

// ✅ Use deep analysis data
const deepAnalysis = analyzeJourneyDeeper(entries);
const comparison = compareJourneyPhases(entries);

// ✅ Determine current day and suggest appropriate goals
const today = new Date();
const dayOfWeek = today.getDay(); // 0 = Sunday, 6 = Saturday
const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const currentDay = dayNames[dayOfWeek];

const getTimeOfDay = () => {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 11) return 'morning';
  if (hour >= 11 && hour < 17) return 'afternoon';
  if (hour >= 17 && hour < 21) return 'evening';
  return 'night';
};

// Then use it in your prompt:
const timeOfDay = getTimeOfDay();
let goalTimeframe = '';
let goalContext = '';

if (dayOfWeek === 0) { // Sunday
  goalTimeframe = 'week ahead';
  goalContext = "It's Sunday - perfect for setting fresh intentions for the upcoming week";
} else if (dayOfWeek === 5) { // Friday
  goalTimeframe = 'weekend';
  goalContext = "It's Friday - time to plan a meaningful weekend";
} else if (dayOfWeek === 6) { // Saturday
  goalTimeframe = 'weekend';
  goalContext = "It's Saturday - make the most of your weekend";
} else { // Monday-Thursday
  goalTimeframe = 'rest of the week';
  goalContext = `It's ${currentDay} - let's set an intention for the days ahead`;
}

// ✅ Identify growth areas and patterns
const growthOpportunities = [];

// Check for consistency patterns
if (currentStreak < 3 && entries.length > 5) {
  growthOpportunities.push("building more consistent journaling habits");
}

// Check for emotional diversity
const uniqueMoods = [...new Set(entries.slice(0, 10).map(e => e.mood))];
if (uniqueMoods.length <= 2) {
  growthOpportunities.push("exploring and expressing a wider range of emotions");
}

// Check for depth of reflection
const averageWordCount = entries.slice(0, 10).reduce((sum, e) => 
  sum + e.content.split(' ').length, 0) / Math.min(entries.length, 10);
if (averageWordCount < 50) {
  growthOpportunities.push("writing more detailed reflections to deepen self-awareness");
}

// Check for goal-oriented content
const goalKeywords = ['goal', 'want', 'will', 'plan', 'hope', 'try', 'improve'];
const goalEntries = entries.filter(e => 
  goalKeywords.some(word => e.content.toLowerCase().includes(word))
);
if (goalEntries.length < entries.length * 0.3) {
  growthOpportunities.push("setting more specific personal goals and intentions");
}

// ✅ Use comparison data for growth insights
const wordGrowth = comparison.growthPercentage > 0 
  ? `${userName} has grown their reflection depth by ${comparison.growthPercentage}% since starting` 
  : `${userName}'s entries have maintained consistent depth`;

// ✅ Enhanced prompt with personalized advice
const reflectionTopic = `Create a personal reflection podcast for ${userName}. 

**PODCAST NAME:** The Inner Loop

**OPENING REQUIREMENT:**
Start with: "Hey ${userName}, welcome to The Inner Loop. This ${timeOfDay}..."
- Use appropriate greeting based on time: morning (5am-11am), afternoon (11am-5pm), evening (5pm-9pm), night (9pm-5am)
- Make it warm and welcoming
- Transition naturally into discussing their journey

**TODAY IS ${currentDay.toUpperCase()}** - ${goalContext}

**JOURNAL CONTEXT (${journeyDays} days of journaling):**
${recentEntries.slice(0, 5).map((e, idx) => 
  `Entry ${idx + 1}: "${e.title}"\n${e.content.substring(0, 200)}...\nMood: ${e.mood}`
).join('\n\n')}

**STYLE & PERSONALITY:**
- Host Style: ${podcastPreferences.hostPersonality}
- Primary Focus: ${podcastPreferences.focus}
- Pacing: ${podcastPreferences.pacing}
- Tone: ${hostInstructions}
- Approach: ${focusInstructions}

**STATS:**
- ${entries.length} entries over ${journeyDays} days
- ${currentStreak}-day current streak
- Peak writing time: ${peakTime}
- Dominant mood: ${moodData[0].name} (${moodData[0].percentage}%)
- Average entry length: ${Math.round(averageWordCount)} words
- ${wordGrowth}

**POWERFUL QUOTES FROM ${userName.toUpperCase()}:**
${powerfulQuotes.slice(0, 3).map(q => `"${q.quote}" (from "${q.entry}")`).join('\n')}

${wisdomQuotes ? `**WISDOM INSIGHTS:**\n${wisdomQuotes}\n` : ''}

**JOURNEY EVOLUTION:**
- Top themes: ${deepAnalysis.topThemes.slice(0, 5).join(', ')}
- Breakthrough moments: ${deepAnalysis.breakthroughMoments.length} significant entries
- Early focus: ${comparison.earlyThemes}
- Recent focus: ${comparison.recentThemes}
- New themes emerging: ${comparison.newThemes}

**GROWTH OPPORTUNITIES FOR ${userName}:**
${growthOpportunities.length > 0 ? growthOpportunities.map((opp, i) => `${i + 1}. ${opp}`).join('\n') : 'Continue deepening self-reflection and awareness'}

**YOUR MISSION:**
Create a 12-18 conversational exchange between 2 supportive hosts discussing ${userName}'s journey on The Inner Loop podcast.

**LENGTH REQUIREMENT:**
- Total exchanges: 12-18 (NOT 30-40)
- Each speaker line: 1-3 sentences maximum
- Total podcast length: 2-3 minutes when spoken
- Keep it concise, focused, and impactful

**CRITICAL REQUIREMENTS:**
1. **MUST START WITH:** "Hey ${userName}, welcome to The Inner Loop. This ${timeOfDay}..."
2. Quote ${userName}'s EXACT words 4-6 times throughout (not 8-10)
3. Use ${userName}'s name only 2-3 times total (not every message)
4. Celebrate their progress genuinely (mention the ${wordGrowth})
5. Offer 1-2 SPECIFIC, ACTIONABLE pieces of advice based on their entries
6. Reference their ${peakTime} writing time naturally if relevant
7. Discuss their growth opportunities with empathy and encouragement
8. Sound like supportive friends, NOT therapists or analysts
9. Use natural language - NO percentages or statistics in speech
10. Focus on: ${podcastPreferences.focus}
11. **END WITH A ${goalTimeframe.toUpperCase()} GOAL** - Give ONE specific, achievable goal for the ${goalTimeframe} based on their patterns

**ADDRESSING ${userName.toUpperCase()} - CRITICAL:**
- When referencing journal entries, speak DIRECTLY to ${userName} in second person
- Say "you wrote" or "you mentioned" NOT "the user said" or "${userName} said"
- Example: "You wrote 'I had an amazing day today'" NOT "User said they had an amazing day"
- These are THEIR words from THEIR journal - own that intimacy
- Make it feel like you're talking WITH them about THEIR journey, not analyzing someone else
- Natural phrasing: "In your entry about...", "You captured this beautifully when you wrote...", "You've been exploring..."

**ADVICE GUIDELINES:**
- Make advice SPECIFIC to their entries (not generic)
- Frame suggestions positively ("Consider..." not "You should...")
- Tie advice to patterns you notice in their writing
- Keep it actionable and achievable
- Be warm and encouraging, never critical

**${goalTimeframe.toUpperCase()} GOAL GUIDELINES:**
- Since it's ${currentDay}, create a goal for the ${goalTimeframe}
- Make it specific and actionable (not vague like "be better")
- Tie it directly to patterns in their entries
- Should take 10-30 minutes to complete
- Examples: "This weekend, write about one fear you've been avoiding" or "This week, journal 3 times about moments of gratitude"

Generate authentic dialogue now.`;
    setGenerationStage('Give me one sec...');
    
    // ✅ FIX: Better error handling for podcast generation
    const response = await sendChatMessage([], `Create a podcast: ${reflectionTopic}`, null);
    const parsed = parseAzureResponse(response);
    
    if (parsed.type === 'tool_call' && parsed.functionName === 'generate_podcast_dialogue') {
      setGenerationStage('Generating audio with personality...');
      
      if (window.generatePodcastDialogue && window.combineAudioSegments) {
        try {
          const dialogue = await window.generatePodcastDialogue(
            reflectionTopic,
            null,
            true,
            ["Host 1", "Host 2"],
            userName
          );
          
          setGenerationStage('Finalizing your reflection...');
          
          const audioUrl = await window.combineAudioSegments(
            dialogue,
            (text) => setGenerationStage(text)
          );
          
          const reflectionData = {
            url: audioUrl,
            title: `${userName}'s ${podcastPreferences.focus === 'growth' ? 'Growth' : podcastPreferences.focus === 'emotions' ? 'Emotional' : podcastPreferences.focus === 'achievements' ? 'Achievement' : 'Pattern'} Reflection`,
            userName,
            createdAt: new Date().toISOString(),
            personality: podcastPreferences.hostPersonality,
            focus: podcastPreferences.focus
          };

          setReflectionAudio(reflectionData);
          
          // Save to localStorage
          blobUrlToBase64(audioUrl).then(base64 => {
            if (base64) {
              try {
                localStorage.setItem('journalReflectionAudio', JSON.stringify({
                  ...reflectionData,
                  audioBase64: base64,
                  url: undefined
                }));
              } catch (error) {
                console.error('Error saving audio:', error);
              }
            }
          });
        } catch (dialogueError) {
          console.error('❌ Dialogue generation failed:', dialogueError);
          throw new Error('Failed to generate podcast dialogue. The response may have been too long. Try again.');
        }
      } else {
        throw new Error('Podcast functions not available');
      }
    } else {
      throw new Error('AI did not return podcast dialogue function');
    }
    
  } catch (error) {
    console.error('❌ Reflection error:', error);
    alert(`Failed to generate reflection: ${error.message}`);
        setShowPodcastSettings(false);
  } finally {
    setIsGeneratingReflection(false);
    setGenerationStage('');
  }
};


const verifyPinForAction = (entryId, action) => {
  if (!appPin) {
    // No PIN set yet, show setup modal
    setShowPinSetup(true);
    // Store the action to perform after PIN is set
    setCurrentLockAction({ entryId, action });
    return;
  }
  
  // PIN exists, ask for verification
  setCurrentLockAction({ entryId, action });
  setShowPinModal(true);
  setPinInput('');
};
useEffect(() => {
  localStorage.setItem('journal_locked_entries', JSON.stringify(lockedEntries));
}, [lockedEntries]);
// Add this near your other useEffects, after the lockedEntries useEffect
useEffect(() => {
  const savedPin = localStorage.getItem('journal_app_pin');
  const savedLocked = localStorage.getItem('journal_locked_entries');
  
  if (savedPin) {
    setAppPin(savedPin);
  }
  
  if (savedLocked) {
    try {
      const parsed = JSON.parse(savedLocked);
      if (Array.isArray(parsed)) {
        setLockedEntries(parsed);
      }
    } catch (error) {
      console.error('Error loading locked entries:', error);
    }
  }
}, []); // Run only on mount
// 3. PIN SETUP FUNCTION - FIXED
const setupPin = (pin) => {
  if (pin.length !== 4) {
    alert('❌ PIN must be exactly 4 digits');
    return;
  }
  
  if (!/^\d{4}$/.test(pin)) {
    alert('❌ PIN must contain only numbers');
    return;
  }
  
  localStorage.setItem('journal_app_pin', pin);
  setAppPin(pin);
  setShowPinSetup(false);
  setPinInput('');
  
  // ✅ FIX: If there's a queued action, perform it now and save to localStorage
  if (currentLockAction && currentLockAction.action === 'lock') {
    const newLocked = [...lockedEntries, currentLockAction.entryId];
    setLockedEntries(newLocked);
    localStorage.setItem('journal_locked_entries', JSON.stringify(newLocked)); // Save immediately
    setCurrentLockAction(null);
    setTimeout(() => alert('PIN set and entry locked successfully!'), 100);
  } else {
    alert('PIN set successfully!');
  }
};

// 5. HANDLE PIN SUBMIT - FIXED

const handlePinSubmit = async () => {
  // Verify PIN
  if (pinInput !== appPin) {
    alert('❌ Incorrect PIN. Please try again.');
    setPinInput('');
    return;
  }

  // Check if there's an action to perform
  if (!currentLockAction) {
    setPinInput('');
    setShowPinModal(false);
    return;
  }

  const { entryId, action } = currentLockAction;
  
  try {
    if (action === 'lock') {
      // Lock the entry
      const newLocked = [...lockedEntries, entryId];
      setLockedEntries(newLocked);
      localStorage.setItem('journal_locked_entries', JSON.stringify(newLocked));
      
      // Update the entry in state to show as locked
      setEntries(entries.map(e => 
        e.id === entryId 
          ? { ...e, title: 'Locked Entry', content: 'This entry is protected. Enter PIN to view.', isLocked: true }
          : e
      ));
      
      setShowPinModal(false);
      setPinInput('');
      setCurrentLockAction(null);
      setTimeout(() => alert('Entry locked'), 100);
      
    } else if (action === 'unlock') {
      // Unlock the entry
      const newLocked = lockedEntries.filter(id => id !== entryId);
      setLockedEntries(newLocked);
      localStorage.setItem('journal_locked_entries', JSON.stringify(newLocked));
      
      // Fetch real content from database and update state
      const realEntry = await journalStorage.getJournal(entryId);
      
      if (realEntry) {
        setEntries(entries.map(e => 
          e.id === entryId 
            ? {
                ...e,
                title: realEntry.title || realEntry.header || 'Untitled Entry',
                content: realEntry.summary || realEntry.insights || 'No content',
                isLocked: false
              }
            : e
        ));
      }
      
      setShowPinModal(false);
      setPinInput('');
      setCurrentLockAction(null);
      setTimeout(() => alert(' Entry unlocked'), 100);
      
    } else if (action === 'view') {
      // Fetch the REAL entry from database (not the masked version)
      const realEntry = await journalStorage.getJournal(entryId);
      
      if (realEntry) {
        const fullEntry = {
          id: realEntry.id,
          date: new Date(realEntry.createdAt || realEntry.sessionStartTime || Date.now()),
          title: realEntry.title || realEntry.header || 'Untitled Entry',
          content: realEntry.summary || realEntry.insights || 'No content',
          tags: realEntry.tags || [],
          mood: realEntry.tags?.[0] || 'thoughtful',
          background: realEntry.imageUrl || '/image/session-img13.png',
          writingStyle: realEntry.writingStyle,
          journalPurpose: realEntry.journalPurpose,
          isLocked: true
        };
        
        setSelectedEntry(fullEntry);
        setShowSingleEntry(true);
        setShowViewAllModal(false); // Close the all entries modal if open
        setShowPinModal(false);
        setPinInput('');
        setCurrentLockAction(null);
      } else {
        alert('❌ Could not load entry. Please try again.');
        setPinInput('');
      }
    }
  } catch (error) {
    console.error('❌ Error in handlePinSubmit:', error);
    alert('❌ Something went wrong. Please try again.');
    setPinInput('');
  }
};

// 6. TOGGLE LOCK FUNCTION - FIXED
const toggleLock = (entryId) => {
  const isLocked = isEntryLocked(entryId);
  
  if (!appPin) {
    // No PIN set - must set one first
    setCurrentLockAction({ entryId, action: 'lock' });
    setShowPinSetup(true);
    setPinInput('');
    return;
  }
  
  if (isLocked) {
    // Entry is locked, need PIN to unlock
    setCurrentLockAction({ entryId, action: 'unlock' });
    setShowPinModal(true);
    setPinInput('');
  } else {
    // Entry is unlocked, need PIN to lock
    setCurrentLockAction({ entryId, action: 'lock' });
    setShowPinModal(true);
    setPinInput('');
  }
};
// 7. CHECK IF ENTRY IS LOCKED
const isEntryLocked = (entryId) => {
  return lockedEntries.includes(entryId);
};
const handleEntryClick = (entry) => {
  if (isEntryLocked(entry.id)) {
    setCurrentLockAction({ entryId: entry.id, action: 'view' });
    setShowPinModal(true);
    setPinInput('');
  } else {
    const fullEntry = {
      ...entry,
      tags: entry.tags || [],
      selectedImages: entry.selectedImages || [],
      images: entry.images || [],
      tagInput: '' // ✅ ADD this line
    };
    
    setSelectedEntry(fullEntry);
    setShowViewAllModal(false);
    setShowSingleEntry(true);
  }
};

// ✅ DELETE this entire function (not used):
const openEditModal = (entry) => {
  setEditingEntry(entry);
  setEditTitle(entry.title || '');
  setEditContent(entry.content || '');
  setEditMood(entry.mood || 'neutral');
  setSelectedImages([]);
  setHasUnsavedChanges(false);
};

  const skipBackward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime - 15);
    }
  };

  const skipForward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.min(audioRef.current.duration, audioRef.current.currentTime + 15);
    }
  };

    // Audio controls

 const togglePlayPause = () => {
    if (audioRef.current) {
      if (isPlayingReflection) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlayingReflection(!isPlayingReflection);
    }
  };

  // Update close function to destroy controls
  const closeReflectionPlayer = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }

    
    if (reflectionAudio?.url) {
      URL.revokeObjectURL(reflectionAudio.url);
    }
    
    setReflectionAudio(null);
    setIsPlayingReflection(false);
    localStorage.removeItem('journalReflectionAudio');
  };

   // Handle audio events
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handlePlay = () => setIsPlayingReflection(true);
    const handlePause = () => setIsPlayingReflection(false);
    const handleEnded = () => {
      setIsPlayingReflection(false);
    };

    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);
useEffect(() => {
  // Cleanup: revoke object URL when component unmounts
  return () => {
    if (reflectionAudio?.url) {
      URL.revokeObjectURL(reflectionAudio.url);
    }
  };
}, [reflectionAudio]);

  // Handle audio time updates
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => {
      setCurrentTime(audio.currentTime);
      setDuration(audio.duration || 0);
      setReflectionProgress((audio.currentTime / audio.duration) * 100);
    };

    const handleEnded = () => {
      setIsPlayingReflection(false);
      setReflectionProgress(0);
      setCurrentTime(0);
    };

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('loadedmetadata', updateTime);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('loadedmetadata', updateTime);
    };
  }, [reflectionAudio]);

  const formatAudioTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle touch start
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  // Handle touch move
  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  // Handle touch end
  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      setCurrentAffirmationIndex((prev) => (prev + 1) % affirmations.length);
    }
    
    if (isRightSwipe) {
      setCurrentAffirmationIndex((prev) => (prev - 1 + affirmations.length) % affirmations.length);
    }

    setTouchStart(0);
    setTouchEnd(0);
 
  };
// Show skeleton loading screen during initial load
if (isLoading && entries.length === 0) {
  return (
    <>
      <style>{`
        @keyframes shimmer {
          0% { background-position: -1000px 0; }
          100% { background-position: 1000px 0; }
        }

        .skeleton {
          background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
          background-size: 1000px 100%;
          animation: shimmer 1s infinite linear;
          border-radius: 8px;
        }

        .skeleton-journal-container {
          height: 100vh;
          background: linear-gradient(170deg, #e3e3e3d8 0%, #fefefecf 60%, #e0e7fcb2 100%);
          padding: 16px;
          padding-top: 80px;
          overflow-y: auto;
        }

        .skeleton-journal-inner {
          max-width: 680px;
          margin: 0 auto;
        }
      `}</style>

      <div className="skeleton-journal-container">
        <div className="skeleton-journal-inner">
          {/* Header Skeleton */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <div className="skeleton" style={{ width: '120px', height: '24px' }}></div>
          </div>

          {/* Stats Grid Skeleton - Streak + Peak Time Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px', marginBottom: '20px' }}>
            {/* Peak Writing Time Card Skeleton */}
            <div style={{
              position: 'relative',
              borderRadius: '20px',
              overflow: 'hidden',
              boxShadow: '0 3px 10px rgba(0, 0, 0, 0.08)'
            }}>
              {/* Gradient Background */}
              <div style={{
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                opacity: 0.8
              }}></div>
              
              {/* Content */}
              <div style={{
                position: 'relative',
                backdropFilter: 'blur(10px)',
                padding: '5px',
                height: '100%'
              }}>
                <div style={{
                  background: 'rgba(255, 255, 255, 0.85)',
                  backdropFilter: 'blur(4px)',
                  borderRadius: '16px',
                  padding: '20px',
                  height: '140px',
                  border: '1px solid rgba(138, 138, 138, 0.3)'
                }}>
                  <div className="skeleton" style={{ width: '140px', height: '13px', marginBottom: '16px' }}></div>
                  <div className="skeleton" style={{ width: '100%', height: '16px', marginBottom: '12px' }}></div>
                  <div className="skeleton" style={{ width: '100%', height: '16px', marginBottom: '12px' }}></div>
                  <div className="skeleton" style={{ width: '100%', height: '16px' }}></div>
                </div>
              </div>
            </div>

            {/* Streak Card Skeleton */}
            <div style={{
              position: 'relative',
              borderRadius: '20px',
              overflow: 'hidden',
              boxShadow: '0 3px 10px rgba(0, 0, 0, 0.08)'
            }}>
              {/* Gradient Background */}
              <div style={{
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(135deg, #fecaca 0%, #fca5a5 100%)',
                opacity: 0.8
              }}></div>
              
              {/* Content */}
              <div style={{
                position: 'relative',
                backdropFilter: 'blur(10px)',
                padding: '5px',
                height: '100%'
              }}>
                <div style={{
                  background: 'rgba(255, 255, 255, 0.85)',
                  backdropFilter: 'blur(4px)',
                  borderRadius: '16px',
                  padding: '20px',
                  height: '140px',
                  border: '1px solid rgba(138, 138, 138, 0.3)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <div className="skeleton" style={{ width: '100px', height: '13px', marginBottom: '16px' }}></div>
                  <div className="skeleton" style={{ width: '48px', height: '48px', borderRadius: '50%', marginBottom: '12px' }}></div>
                  <div className="skeleton" style={{ width: '60px', height: '32px', marginBottom: '8px', borderRadius: '4px' }}></div>
                  <div className="skeleton" style={{ width: '80px', height: '11px' }}></div>
                </div>
              </div>
            </div>
          </div>

          {/* View Entries Section Skeleton */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.31)',
            backdropFilter: 'blur(20px)',
            borderRadius: '12px',
            padding: '5px',
            border: '1px solid rgba(255, 255, 255, 0.68)',
            marginBottom: '10px'
          }}>
            <div style={{ background: '#f3f4f6', borderRadius: '8px', padding: '14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div className="skeleton" style={{ width: '100px', height: '16px' }}></div>
                <div className="skeleton" style={{ width: '80px', height: '32px', borderRadius: '20px' }}></div>
              </div>
            </div>
          </div>

          {/* Latest/Discover Tabs Skeleton */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.31)',
            backdropFilter: 'blur(20px)',
            borderRadius: '20px',
            padding: '7px',
            border: '1px solid rgba(255, 255, 255, 0.93)'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '14px',
              border: '1px solid #d5d5d5ed',
              padding: '20px'
            }}>
              {/* Tabs */}
              <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
                <div className="skeleton" style={{ width: '80px', height: '36px', borderRadius: '20px' }}></div>
                <div className="skeleton" style={{ width: '80px', height: '36px', borderRadius: '20px' }}></div>
              </div>

              {/* No entries placeholder (white style) */}
              <div style={{
                textAlign: 'center',
                padding: '40px 20px'
              }}>
                <div className="skeleton" style={{ 
                  width: '80px', 
                  height: '80px', 
                  borderRadius: '50%', 
                  margin: '0 auto 16px' 
                }}></div>
                <div className="skeleton" style={{ 
                  width: '200px', 
                  height: '20px', 
                  margin: '0 auto 12px',
                  borderRadius: '4px'
                }}></div>
                <div className="skeleton" style={{ 
                  width: '280px', 
                  height: '16px', 
                  margin: '0 auto',
                  borderRadius: '4px'
                }}></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
  return (
    <>
      <style>{`
        * { box-sizing: border-box; }
/* ✅ Pinterest-style Masonry Layout for Edit Modal */
.edit-modal-images {
  margin-bottom: 24px;
}

.edit-modal-images-grid {
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(2, 1fr);
  grid-auto-rows: 120px;
}

/* Single image - full width */
.edit-modal-images-grid.single-image {
  grid-template-columns: 1fr;
}

.edit-modal-images-grid.single-image .edit-image-wrapper {
  grid-column: 1;
  grid-row: span 2;
}

/* Two images - side by side */
.edit-modal-images-grid.two-images {
  grid-template-columns: repeat(2, 1fr);
}

.edit-modal-images-grid.two-images .edit-image-wrapper {
  grid-row: span 2;
}

/* Three images - Pinterest style */
.edit-modal-images-grid.three-images {
  grid-template-columns: repeat(2, 1fr);
}

.edit-modal-images-grid.three-images .edit-image-wrapper:first-child {
  grid-column: 1;
  grid-row: span 2;
}

.edit-modal-images-grid.three-images .edit-image-wrapper:nth-child(2),
.edit-modal-images-grid.three-images .edit-image-wrapper:nth-child(3) {
  grid-column: 2;
  grid-row: span 1;
}

.edit-image-wrapper {
  position: relative;
  width: 100%;
  overflow: hidden;
  border-radius: 12px;
  background: #f3f4f6;
  border: 2px solid #e5e7eb;
}

.edit-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.edit-remove-btn {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 26px;
  height: 26px;
  background: rgba(0, 0, 0, 0.27);
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 2;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}



@media (max-width: 768px) {
  .edit-modal-images-grid {
    grid-auto-rows: 100px;
  }
}
@keyframes scaleIn {
  from { transform: scale(0.8); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}

@keyframes bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-20px); }
}

body { 
  margin: 0; 
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  background: linear-gradient(170deg, #e3e3e3d8 0%, #fefefecf 60%, #e0e7fcb2 100%);
}

/* Search Bar Styles */
.journal-search-container {
  position: relative;
  margin-bottom: 0px;
}

.journal-search-input {
  width: 100%;
  padding: 14px 48px 14px 44px;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(173, 173, 173, 0.9);
  border-radius: 16px;
  font-size: 14px;
  transition: all 0.3s ease;
  color: #1f2937;
}

.journal-search-input::placeholder {
  color: #9ca3af;
}

.journal-search-input:focus {
  outline: none;
  background: rgba(255, 255, 255, 0.9);
  border-color: #747474ff;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.journal-filter-button {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 0, 0, 0.24);
  padding: 8px;
  border-radius: 12px;
  cursor: pointer;
  color: #6b7280;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.journal-filter-button.active {
  background: #272727;
  color: white;
  border-color: #272727;
}

/* Lock Badge */
.entry-lock-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(10px);
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 11px;
  display: flex;
  align-items: center;
  gap: 6px;
  z-index: 2;
  font-weight: 500;
  letter-spacing: 0.3px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.entry-lock-badge svg {
  color: white !important;
  fill: none !important;
}

/* Lock Toggle Button on Cards */
.entry-lock-toggle {
  position: absolute;
  top: 12px;
  left: 12px;
  background: #272727;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 0, 0, 0.08);
  padding: 8px;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.entry-lock-toggle:hover {
  background: #272727;
  color: white;
  transform: scale(1.1);
}

.entry-lock-toggle.locked {
  background: #ffffff77;
  color: #000000ff;
}

/* Mic Button */
.journal-mic-button {
  position: absolute;
  right: 16px;
  bottom: 16px;
  background: #272727;
  border: none;
  color: white;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 16px rgba(39, 39, 39, 0.3);
  z-index: 10;
}

.journal-mic-button:hover {
  transform: scale(1.05) translateY(-2px);
  box-shadow: 0 6px 24px rgba(39, 39, 39, 0.4);
}

.journal-mic-button:active {
  transform: scale(0.95);
}

.journal-mic-button.recording {
  background: #ef4444;
  animation: pulse-recording 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  box-shadow: 0 4px 16px rgba(239, 68, 68, 0.4), 0 0 0 0 rgba(239, 68, 68, 0.4);
}

@keyframes pulse-recording {
  0%, 100% { 
    box-shadow: 0 4px 16px rgba(239, 68, 68, 0.4), 0 0 0 0 rgba(239, 68, 68, 0.4);
  }
  50% { 
    box-shadow: 0 4px 24px rgba(239, 68, 68, 0.6), 0 0 0 8px rgba(239, 68, 68, 0);
  }
}

.recording-indicator {
  position: absolute;
  bottom: 24px;
  left: 16px;
  background: rgba(239, 68, 68, 0.1);
  backdrop-filter: blur(10px);
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 12px;
  color: #ef4444;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid rgba(239, 68, 68, 0.2);
}

.recording-dot {
  width: 8px;
  height: 8px;
  background: #ef4444;
  border-radius: 50%;
  animation: pulse-dot 1.5s ease-in-out infinite;
}

@keyframes pulse-dot {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(0.8); }
}

/* Stats Button - Black Background */
.add-button1 {
  background: #141414ff;
  border: none;
  color: white;
  padding: 10px 16px;
  border-radius: 20px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 2px 8px rgba(39, 39, 39, 0.2);
}

/* PIN Modal Input Styles */
.pin-modal-input {
  width: 100%;
  padding: 16px;
  border: 2px solid rgba(0, 0, 0, 0.08);
  border-radius: 16px;
  font-size: 24px;
  letter-spacing: 12px;
  text-align: center;
  margin-bottom: 24px;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(10px);
  transition: all 0.2s ease;
  font-family: monospace;
}

.pin-modal-input:focus {
  outline: none;
  border-color: #272727;
  box-shadow: 0 0 0 3px rgba(39, 39, 39, 0.1);
}

/* Icon in Wisdom Card Tag */
.wisdom-card-category {
  display: flex;
  align-items: center;
  gap: 6px;
}

/* Remove emoji styling, use SVG icons */
.icon-fire {
  width: 14px;
  height: 14px;
  color: #ef4444;
}

.icon-lock {
  width: 14px;
  height: 14px;
}

/* Update streak display */
.streak-display {
  display: flex;
  align-items: center;
  gap: 6px;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99000;
  padding: 20px;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.modal {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  padding: 28px;
  max-width: 900px;
  width: 100%;
  max-height: 86vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.9);
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

        .entry-images-section {
    margin-bottom: 16px;
  }

  .entry-images-grid {
    display: grid;
    gap: 8px;
    border-radius: 12px;
    overflow: hidden;
    margin-bottom: 12px;
  }

  .entry-images-grid.single-image {
    grid-template-columns: 1fr;
  }

  .entry-images-grid.two-images {
    grid-template-columns: 1fr 1fr;
  }

  .entry-images-grid.three-images {
    grid-template-columns: repeat(3, 1fr);
  }

  .entry-image-wrapper {
    position: relative;
    width: 100%;
    overflow: hidden;
    border-radius: 8px;
    background: #f3f4f6;
  }

  .entry-images-grid.single-image .entry-image-wrapper {
    aspect-ratio: 16/9;
  }

  .entry-images-grid.two-images .entry-image-wrapper {
    aspect-ratio: 1;
  }

  .entry-images-grid.three-images .entry-image-wrapper:first-child {
    grid-column: 1 / -1;
    aspect-ratio: 16/9;
  }

  .entry-images-grid.three-images .entry-image-wrapper:not(:first-child) {
    aspect-ratio: 1;
  }

  .entry-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .remove-image-btn {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 28px;
    height: 28px;
    background: rgba(118, 118, 118, 0.46);
    border: none;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    z-index: 2;
  }

  .upload-images-area {
    border: 2px dashed #d1d5db;
    border-radius: 12px;
    padding: 24px;
    text-align: center;
    cursor: pointer;
    transition: all 0.2s ease;
    background: #f9fafb;
  }

  .upload-images-area.has-images {
    padding: 16px;
  }

  .background-option.selected-multi {
    border-color: #272727;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
    opacity: 0.8;
  }

  .background-selection-count {
    position: absolute;
    top: 8px;
    left: 8px;
    width: 28px;
    height: 28px;
    background: #272727;
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 600;
    z-index: 2;
  }

  @media (max-width: 768px) {
    .entry-images-grid.three-images {
      grid-template-columns: 1fr;
    }
    
    .entry-images-grid.three-images .entry-image-wrapper:first-child {
      grid-column: 1;
    }
  }
        .podcast-settings-modal {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
}

.podcast-settings-card {
  background: white;
  border-radius: 20px;
  padding: 28px;
  max-width: 480px;
  width: 100%;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  max-height: 90vh;
  overflow-y: auto;
}

.settings-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
}

.settings-title {
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.setting-group {
  margin-bottom: 20px;
}

.setting-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 10px;
}

.setting-options {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

.setting-option {
  padding: 14px 8px;
  border: 1px solid #e5e7eb;
  border-radius: 20px;
  background: #f9fafb;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
}

.setting-option.selected {
  border-color: #272727;
  background: #f0f0f0;
  color: #1f2937;
}

.settings-actions {
  display: flex;
  gap: 12px;
  margin-top: 28px;
}

.settings-action-btn {
  flex: 1;
  padding: 14px;
  border: none;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 400;
  cursor: pointer;
  transition: all 0.2s ease;
}

.settings-cancel {
  background: rgba(0, 0, 0, 0.05);
  color: #374151;
}

.settings-generate {
  background: #272727;
  color: white;
    font-size: 13px;
  font-weight: 400;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

@media (max-width: 768px) {
  .podcast-settings-modal {
    padding: 12px;
    align-items: flex-end;
  }

  .podcast-settings-card {
    padding: 20px 16px;
    border-radius: 20px 20px 0 0;
    max-height: 80vh;
    width: 100%;
    max-width: 100%;
  }

  .settings-header {
    gap: 10px;
    margin-bottom: 18px;
  }

  .settings-title {
    font-size: 16px;
  }

  .setting-group {
    margin-bottom: 16px;
  }

  .setting-label {
    font-size: 12px;
    margin-bottom: 8px;
  }

  .setting-options {
    gap: 8px;
  }

  .setting-option {
    padding: 12px 6px;
    font-size: 11.5px;
    border-radius: 20px;
    white-space: nowrap;
    overflow: hidden;
        margin-bottom: 14px;
    text-overflow: ellipsis;
  }

  .settings-actions {
    gap: 10px;
    margin-top: 20px;
  }

  .settings-action-btn {
    padding: 13px;
    font-size: 12px;
    border-radius: 20px;
  }
}

@media (max-width: 480px) {
  .podcast-settings-modal {
    padding: 0;
  }

  .podcast-settings-card {
    padding: 18px 14px 20px;
    max-height: 75vh;
  }

  .settings-header {
    margin-bottom: 20px;
  }

  .settings-title {
    font-size: 15px;
  }

  .setting-label {
    font-size: 11.5px;
  }

  .setting-options {
    gap: 7px;
  }

  .setting-option {
    padding: 11px 5px;
    font-size: 11px;
  }

  .settings-actions {
    gap: 8px;
    margin-top: 18px;
  }

  .settings-action-btn {
    padding: 12px;
    font-size: 12.5px;
        margin-bottom: 26px;
  }
}

@media (max-width: 380px) {
  .setting-options {
    gap: 6px;
  }

  .setting-option {
    padding: 10px 4px;
    font-size: 10.5px;
  }
}
 .journal-container {
          height: 100vh;
          background: linear-gradient(170deg, #e3e3e3d8 0%, #fefefecf 60%, #e0e7fcb2 100%);
          padding: 16px;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          padding-top: 80px;
          overflow-y:auto;
        }

.journal-inner {
  max-width: 680px;
  margin: 0 auto;
}

        .header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 10px;
          flex-wrap: wrap;
          gap: 12px;
        }

        .header h1 {
          font-size: 18px;
          font-weight: 500;
          color: #1f2937;
          margin: 0;
        }

        .add-button {
          background: #141414ff;
          border: none;
          color: white;
          padding: 10px 16px;
          border-radius: 20px;
          cursor: pointer;
          font-size: 13px;
          font-weight: 500;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 8px;
        }
.add-button1 {
  background: #141414ff;
  border: none;
  color: white;
  padding: 10px 16px;
  border-radius: 20px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 2px 8px rgba(39, 39, 39, 0.2);
}

        .stats-grid {
          display: grid;
          grid-template-columns: 2fr 1fr;
          gap: 16px;
          margin-bottom: 20px;
        }

        .stat-card {
          position: relative;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 3px 10px rgba(0, 0, 0, 0.08);
        }

        .stat-card-gradient {
          position: absolute;
          inset: 0;
          opacity: 0.8;
        }

        .stat-card-content {
          position: relative;
          backdrop-filter: blur(10px);
          padding: 3.5px;
          height: 100%;
        }

        .stat-card-inner {
          background: rgba(255, 255, 255, 0.85);
          backdrop-filter: blur(4px);
          border-radius: 16px;
          padding: 20px;
          height: 100%;
          display: flex;
         border: 1px solid rgba(138, 138, 138, 0.3);
          flex-direction: column;
        }

        .stat-title {
          font-size: 13px;
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 16px;
        }

        .time-stat {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 12px;
        }

        .time-stat:last-child {
          margin-bottom: 0;
        }
        .time-label {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 14px;
          color: #1f2937;
        }

        .time-percentage {
          font-size: 14px;
          font-weight: 600;
          color: #1f2937;
        }

        .affirmation-card {
          position: relative;
          border-radius: 20px;
          overflow: hidden;
          margin-bottom: 20px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
          background: rgba(255, 255, 255, 0.31);
          backdrop-filter: blur(20px);
          padding: 7px;
          border: 1px solid rgba(255, 255, 255, 0.93);
        }

        .affirmation-inner {
          background: white;
          border-radius: 14px;
          overflow: hidden;
          border: 1px solid #d5d5d5ed;
          position: relative;
          min-height: 300px;
          display: flex;
          flex-direction: column;
        }

        .affirmation-image-container {
          width: 100%;
          height: 200px;
          overflow: hidden;
          position: relative;
        }

        .affirmation-tag {
          position: absolute;
          top: 12px;
          left: 12px;
          background: rgba(255, 255, 255, 0.75);
          backdrop-filter: blur(8px);
          padding: 6px 12px;
          border-radius: 8px;
          font-size: 11px;
          font-weight: 600;
          color: #1f2937;
          letter-spacing: 0.5px;
          z-index: 2;
        }
.close-wisdom-button {
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(255, 255, 255, 0.75);
  backdrop-filter: blur(8px);
  border: none;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #29313dff;
  z-index: 2;
  transition: all 0.2s ease;
}

        .affirmation-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .affirmation-content {
          padding: 24px;
          color: #1f2937;
          display: flex;
          flex-direction: column;
          flex: 1;
        }

        .affirmation-title {
          font-size: 12px;
          font-weight: 600;
          margin-bottom: 12px;
          color: #161616ff;
          text-transform: uppercase;
          letter-spacing: 1px;
          text-align: left;
        }

        .affirmation-message {
          font-size: 15px;
          line-height: 1.6;
          font-weight: 400;
          margin-bottom: 20px;
          flex: 1;
          color: #6d7072ff;
          text-align: left;
        }

        .affirmation-actions {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
          padding-top: 16px;
          border-top: 1px solid rgba(0, 0, 0, 0.08);
        }

        .action-button-circle {
          background: rgba(0, 0, 0, 0.04);
          border: none;
          color: #6b7280;
          padding: 0;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .action-button-podcast {
          background: #272727;
          border: none;
          color: white;
          padding: 8px 16px;
          border-radius: 20px;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          font-size: 13px;
          font-weight: 500;
          flex: 1;
        }

        .action-button-podcast:disabled {
          background: #9ca3af;
          cursor: not-allowed;
        }

        /* REFLECTION AUDIO GENERATION - BLACK BUTTON */
        .reflection-generating {
          background: #272727;
          padding: 8px 16px;
          border-radius: 20px;
          color: white;
          font-size: 13px;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 8px;
          flex: 1;
          justify-content: center;
        }

        .spinner {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        /* AUDIO PLAYER IN ACTION SECTION - RIGHT ALIGNED */
        .audio-player-inline {
          flex: 1;
          background: rgba(0, 0, 0, 0.02);
          border-radius: 16px;
          padding: 10px 12px;
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 12px;
          margin-left: auto;
        }

        .player-controls {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .player-control-btn {
          background: rgba(0, 0, 0, 0.04);
          border: none;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: #374151;
          transition: all 0.2s ease;
        }

        .player-control-btn.play-pause {
          width: 40px;
          height: 40px;
          background: #272727;
          color: white;
        }

        .player-control-btn.play-pause:hover {
          background: #1a1a1a;
        }

        .close-audio-btn {
          background: none;
          border: none;
          color: #6b7280;
          cursor: pointer;
          padding: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: color 0.2s ease;
        }
  
        .view-all-container {
          background: rgba(255, 255, 255, 0.31);
          backdrop-filter: blur(20px);
          border-radius: 12px;
          padding: 5px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
          border: 1px solid rgba(255, 255, 255, 0.68);
          transition: all 0.3s ease;
          cursor: pointer;
          margin-bottom: 20px;
        }

        .view-all-inner {
          background: #f3f4f6;
          border-radius: 8px;
          padding: 16px 20px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .view-all-content {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .view-all-icon {
          width: 40px;
          height: 40px;
          background: white;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #6b7280;
        }

        .view-all-text h3 {
          font-size: 14px;
          font-weight: 600;
          color: #1f2937;
          margin: 0 0 4px 0;
        }

        .view-all-text p {
          font-size: 12px;
          color: #6b7280;
          margin: 0;
        }

        .view-all-arrow {
          color: #9ca3af;
          transition: transform 0.2s ease;
        }

        .view-all-container:hover .view-all-arrow {
          transform: translateX(4px);
        }

        .glass-container {
          background: rgba(255, 255, 255, 0.31);
          backdrop-filter: blur(20px);
          border-radius: 20px;
          padding: 5px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
          border: 1px solid rgba(255, 255, 255, 0.93);
          overflow: hidden;
          margin-bottom: 10px;
        }

        .glass-inner {
          background: white;
          border-radius: 14px;
          overflow: hidden;
          border: 1px solid rgba(213, 213, 213, 0.93);
        }

        .viewall-button {
          background: #f1f1f1fe;
          border: none;
          color: white;
          padding: 10px 16px;
          border-radius: 20px;
          cursor: pointer;
          font-size: 12px;
          font-weight: 500;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .section-title1 {
          font-size: 11.5px;
          font-weight: 500;
          color: #080c11c3;
          margin: 0;
        }

        .entries-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 16px;
          padding: 20px;
        }

        .entry-card {
          background: rgba(255, 255, 255, 0.35);
          backdrop-filter: blur(10px);
          border-radius: 12px;
          padding: 3px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
          border: 1px solid rgba(255, 255, 255, 0.68);
          transition: all 0.3s ease;
          cursor: pointer;
        }
        .entry-card-inner {
          border-radius: 10px;
          padding: 16px;
          height: 100%;
          display: flex;
          flex-direction: column;
          background: white;
        }

        .entry-header {
          display: flex;
          justify-content: space-between;
          align-items: start;
          margin-bottom: 12px;
          gap: 8px;
        }

        .entry-title {
          font-size: 16px;
          font-weight: 600;
          color: #1f2937;
          margin: 0 0 4px 0;
          line-height: 1.4;
        }

        .entry-meta {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 11px;
          color: #6b7280;
          margin-bottom: 12px;
        }

        .mood-badge {
          padding: 4px 8px;
          border-radius: 6px;
          font-size: 11px;
          font-weight: 500;
          display: inline-flex;
          align-items: center;
          gap: 4px;
          border: 1px solid;
        }

        .entry-content {
          font-size: 13px;
          color: #374151;
          line-height: 1.6;
          margin-bottom: 12px;
          flex: 1;
          overflow: hidden;
          display: -webkit-box;
          -webkit-line-clamp: 4;
          -webkit-box-orient: vertical;
        }

        .entry-tags {
          display: flex;
          gap: 6px;
          flex-wrap: wrap;
        }

        .entry-tag {
          padding: 4px 8px;
          background: rgba(0, 0, 0, 0.06);
          border-radius: 6px;
          font-size: 10px;
          font-weight: 500;
          color: #4b5563;
        }

        .empty-state {
          text-align: center;
          padding: 60px 20px;
          color: #9ca3af;
        }

        .empty-icon {
          margin: 0 auto 12px;
          opacity: 0.5;
        }

        .empty-text {
          margin: 0;
          font-size: 14px;
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 99000;
          padding: 20px;
          backdrop-filter: blur(4px);
        }

        .modal {
          background: white;
          border-radius: 20px;
          padding: 24px;
          max-width: 600px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
          border: 1px solid rgba(0, 0, 0, 0.06);
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .modal-title {
          font-size: 14px;
          font-weight: 600;
          color: #1f2937;
          margin: 0;
        }

        .close-button {
          background: rgba(0, 0, 0, 0);
          border: none;
          cursor: pointer;
          color: #6b7280;
          padding: 8px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          transition: all 0.2s ease;
        }


        .form-group {
          margin-bottom: 16px;
        }

        .form-label {
          display: block;
          font-size: 12px;
          font-weight: 600;
          color: #374151;
          text-align:left;
          margin-bottom: 8px;
        }

        .form-input:focus {
          border-color: #272727;
          outline: none;
          box-shadow: 0 0 0 3px rgba(39, 39, 39, 0.1);
        }

        .form-textarea {
          min-height: 200px;
          resize: vertical;
        }

        .tag-input-container {
          display: flex;
          gap: 8px;
          margin-bottom: 8px;
        }

        .tag-input {
          flex: 1;
        }

        .tag-add-button {
          padding: 8px 16px;
          background: #141414ff;
          color: white;
          border: none;
          border-radius: 50px;
          cursor: pointer;
          font-size: 12px;
          font-weight: 500;
          transition: all 0.2s ease;
        }

        .tag-add-button:hover {
          background: #1a1a1a;
        }

        .selected-tags {
          display: flex;
          gap: 6px;
          flex-wrap: wrap;
        }

        .selected-tag {
          padding: 6px 10px;
          background: #f3f4f6;
          border-radius: 24px;
          font-size: 12px;
          font-weight: 500;
          color: #374151;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .tag-remove {
          background: none;
          border: none;
          cursor: pointer;
          color: #9ca3af;
          padding: 0;
          display: flex;
          align-items: center;
          transition: color 0.2s ease;
        }
        .form-actions {
          display: flex;
          gap: 12px;
          margin-top: 20px;
        }

.cancel-button1 {
  flex: 1;
  padding: 0 14px;              /* ✅ Only horizontal padding */
  background: rgba(0, 0, 0, 0.04);
  color: #374151;
  border: none;
  border-radius: 20px;          /* ✅ More rounded (matches submit) */
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  min-height: 44px;             /* ✅ Ensures minimum height */
}

.cancel-button1:hover {
  background: rgba(0, 0, 0, 0.08);
}

.submit-button1 {
  flex: 1;
  padding: 0 14px;              /* ✅ Only horizontal padding */
  background: #272727;
  color: white;
  border: none;
  border-radius: 20px;          /* ✅ More rounded (matches cancel) */
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 44px;
  min-height: 44px;             /* ✅ Ensures minimum height */
}

.submit-button:hover {
  background: #1a1a1a;
}


        .delete-button1 {
          padding: 12px 16px;
          background: #fee2e2;
          color: #dc2626;
          border: none;
          border-radius: 20px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .delete-button1:hover {
          background: #fecaca;
        }

/* Filter panel styling */
.filter-panel {
  background: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 20px;
  padding: 16px;
  margin-bottom: 16px;
}

.filter-title {
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.filter-options {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.filter-option-btn {
  padding: 8px 14px;
  border: 2px solid #e5e7eb;
  border-radius: 20px;
  background: white;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #374151;
}


.filter-option-btn.selected {
  transform: scale(1.02);
}

/* PIN Modal wrapper for mobile */
.pin-modal-wrapper {
  max-width: 420px;
  width: 100%;
}
@keyframes scaleIn {
  from { transform: scale(0.8); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}

@keyframes bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-20px); }
}

@keyframes confettiFall {
  0% { 
    transform: translateY(0) rotate(0deg);
    opacity: 1;
  }
  100% { 
    transform: translateY(100vh) rotate(720deg);
    opacity: 0;
  }
}
@media (max-width: 768px) {
  .pin-modal-wrapper {
    max-width: 90vw !important;
  }
  
  .pin-modal-wrapper .modal {
    padding: 20px !important;
  }

  .pin-modal-input {
    font-size: 20px;
    padding: 14px;
    letter-spacing: 8px;
  }
}
        @media (max-width: 768px) {
          .journal-container {
            padding: 10px;
            padding-top: 70px;
             padding-bottom: 120px;
          }

          .reflection-player {
            width: calc(100% - 20px);
            bottom: 10px;
          }
        }

        .all-entries-modal.expanded .modal {
          max-width: 100%;
          max-height: 100vh;
          height: 100vh;
          width: 100vw;
          margin: 0;
          border-radius: 0;
          padding: 32px;
        }

        @media (max-width: 768px) {
          .all-entries-modal.expanded .modal {
            padding: 30px;
            padding-top:35px;
            overflow-y:hidden;
          }
          
  
  .all-entries-modal.expanded .all-entries-grid {
    grid-template-columns: 1fr;
    gap: 16px;
    padding-bottom: 160px;            /* ✅ Added 120px bottom padding */
  }

  .all-entries-grid {
    padding-bottom: 140px;            /* ✅ Added 100px bottom padding */
  }
}
          .all-entries-modal.expanded .all-entries-grid {
            grid-template-columns: 1fr;
            gap: 16px;
          }
        }

        .expand-button {
          background: rgba(0, 0, 0, 0.04);
          border: none;
          cursor: pointer;
          color: #6b7280;
          padding: 8px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          transition: all 0.2s ease;
        }

        .expand-button:hover {
          background: rgba(0, 0, 0, 0.08);
          color: #1f2937;
        }

.all-entries-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
  max-height: 500px;
  overflow-y: auto;
  padding: 4px 4px 120px 4px;     
}

.all-entries-modal.expanded .all-entries-grid {
  max-height: calc(100vh - 120px);
  grid-template-columns: repeat(auto-fill, minmax(310px, 1fr));
  gap: 15px;
  padding: 4px 4px 140px 4px;      
}
        .all-entries-modal .modal {
          max-width: 900px;
        }

        .expand-button {
          background: rgba(0, 0, 0, 0.04);
          border: none;
          cursor: pointer;
          color: #6b7280;
          padding: 8px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          transition: all 0.2s ease;
        }

        .expand-button:hover {
          background: rgba(0, 0, 0, 0.08);
          color: #1f2937;
        }

        .all-entries-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 16px;
          max-height: 500px;
          overflow-y: auto;
          padding: 4px;
        }

        .stored-entry-card {
          background: rgba(255, 255, 255, 0.35);
          backdrop-filter: blur(10px);
          border-radius: 12px;
          padding: 6px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
          border: 1px solid rgba(255, 255, 255, 0.68);
          transition: all 0.3s ease;
          cursor: pointer;
        }

        .stored-entry-card:hover {
          transform: translateY(-2px);
        }

        .stored-entry-inner {
          border-radius: 10px;
          padding: 16px;
          height: 100%;
          display: flex;
          flex-direction: column;
          background: white;
        }

        .stored-entry-image {
          width: 100%;
          height: 100px;
          border-radius: 8px;
          object-fit: cover;
          margin-bottom: 12px;
        }

        .stored-entry-header {
          margin-bottom: 12px;
        }

  .stored-entry-title {
  font-size: 15px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 8px 0;
  line-height: 1.4;
  text-align: left;
  word-wrap: break-word;              /* ✅ Wraps long words */
  overflow-wrap: break-word;          /* ✅ Breaks long words properly */
}

        .stored-entry-date {
          font-size: 11px;
          color: #9ca3af;
          display: flex;
          align-items: center;
          gap: 4px;
        }

.stored-entry-summary {
  font-size: 13px;
  color: #6b7280;
  line-height: 1.5;
  margin-bottom: 12px;
  flex: 1;
  text-align: left;
  word-wrap: break-word;              /* ✅ Wraps long words */
  overflow-wrap: break-word;          /* ✅ Breaks long words properly */
}

        .stored-entry-tags {
          display: flex;
          gap: 6px;
          flex-wrap: wrap;
        }

        .stored-entry-tag {
          padding: 4px 8px;
          background: rgba(0, 0, 0, 0.06);
          border-radius: 6px;
          font-size: 10px;
          font-weight: 500;
          color: #4b5563;
        }

        .entry-preview-image {
          width: 100%;
          height: 150px;
          border-radius: 12px;
          object-fit: cover;
          margin-bottom: 16px;
        }

        .empty-journals-state {
          text-align: center;
          padding: 60px 20px;
          color: #9ca3af;
        }

        .empty-journals-icon {
          margin: 0 auto 16px;
          opacity: 0.3;
        }

        .empty-journals-text {
          font-size: 14px;
          color: #6b7280;
        }
        .modal {
          background: white;
          border-radius: 20px;
          padding: 24px;
          max-width: 600px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
          border: 1px solid rgba(0, 0, 0, 0.06);
        }
.entry-modal.expanded .modal {
  max-width: 100%;
  max-height: 100vh;
  height: 100vh;
  width: 100vw;
  margin: 0;
  border-radius: 0;
  padding: 32px;
}
.swipe-card-modal {
  max-width: 450px;
  margin-bottom: 60px;
}
@media (max-width: 768px) {
  .swipe-card-modal {
    margin-bottom: 80px;
  }
  
  .entry-modal.expanded .modal {
    padding: 20px;
    padding-top: 50px;
  }
}

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .modal-title {
          font-size: 14px;
          font-weight: 600;
          color: #1f2937;
          margin: 0;
        }

        .close-button {
          background: rgba(0, 0, 0, 0.04);
          border: none;
          cursor: pointer;
          color: #6b7280;
          padding: 8px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          transition: all 0.2s ease;
        }


        .progress-bar-container {
          width: 100%;
          height: 3px;
          background: rgba(200, 200, 200, 0.3);
          border-radius: 2px;
          margin-bottom: 20px;
          overflow: hidden;
        }

        .progress-bar {
          height: 100%;
          background: #3b82f6;
          transition: width 0.3s ease;
          border-radius: 2px;
        }


        .swipe-card {
          background: linear-gradient(135deg, #ffe2dbff 0%, #e1efffff 50%, #eadeffff 100%);
          border-radius: 16px;
          padding: 8px;
          margin-bottom: 20px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          border: 1px solid rgba(224, 231, 252, 0.5);
        }
        .swipe-card-inner {
          background: white;
          border-radius: 10px;
          padding: 24px;
          min-height: 300px;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }

        .question-label {
          font-size: 11px;
          font-weight: 600;
          color: #9ca3af;
          margin-bottom: 12px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .question-text {
          font-size: 15.5px;
          line-height: 1.5;
          color: #1f2937;
          font-weight: 600;
          margin-bottom: 24px;
        }

        .question-options {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .option-button {
          padding: 16px 16px;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          background: #f9fafb;
          cursor: pointer;
          transition: all 0.2s ease;
          text-align: left;
          font-size: 12px;
          font-weight: 400;
          color: #374151;
          display: flex;
          align-items: center;
          gap: 16px;
          position: relative;
        }

        .option-checkbox {
          width: 15px;
          height: 15px;
          border: 2px solid #d1d5db;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: all 0.2s ease;
          background: white;
        }

        .option-button:hover .option-checkbox {
          border-color: #9ca3af;
        }

        .option-button.selected {
          border-color: #272727;
          background: #f0f0f0;
        }

        .option-button.selected .option-checkbox {
          border-color: #272727;
          background: #272727;
        }

        .option-button.selected .option-checkbox::after {
          content: '✓';
          color: white;
          font-size: 10px;
        }

        .option-text {
          flex: 1;
        }

        .background-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
          max-height: 200px;
          overflow-y: auto;
          padding: 4px;
        }

        .background-option {
          aspect-ratio: 16/9;
          border: 3px solid transparent;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.2s ease;
          position: relative;
          overflow: hidden;
        }

        .background-option img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .background-option.selected {
          border-color: #272727;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
        }

        .background-check {
          position: absolute;
          top: 8px;
          right: 8px;
          width: 28px;
          height: 28px;
          background: rgba(0, 0, 0, 0.6);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

    

        .form-group {
          margin-bottom: 16px;
        }

        .form-label {
          display: block;
          font-size: 12px;
          font-weight: 600;
          color: #374151;
          margin-bottom: 8px;
        }

        .form-input {
          width: 100%;
          padding: 12px 14px;
          border: 1px solid #e5e7eb;
          border-radius: 24px;
          font-size: 14px;
          font-family: inherit;
          transition: all 0.2s ease;
        }

        .form-input:focus {
          border-color: #272727;
          outline: none;
          box-shadow: 0 0 0 3px rgba(39, 39, 39, 0.1);
        }

        .form-textarea {
          min-height: 200px;
          resize: vertical;
        }

        .tag-input-container {
          display: flex;
          gap: 8px;
          margin-bottom: 8px;
        }

        .tag-input {
          flex: 1;
        }

        .tag-remove {
          background: none;
          border: none;
          cursor: pointer;
          color: #9ca3af;
          padding: 0;
          display: flex;
          align-items: center;
          transition: color 0.2s ease;
        }

        .tag-remove:hover {
          color: #6b7280;
        }

        .form-actions {
          display: flex;
          gap: 12px;
          margin-top: 20px;
        }

        .submit-button:hover {
          background: #1a1a1a;
        }

        .delete-button1 {
          padding: 12px 16px;
          background: #fee2e2;
          color: #dc2626;
          border: none;
          border-radius: 10px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .delete-button:hover {
          background: #fecaca;
        }

        .emotion-chart-container {
          display: flex;
          justify-content: space-around;
          align-items: flex-end;
          height: 100px;
          gap: 12px;
        }

        .emotion-bar-wrapper {
          display: flex;
          flex-direction: column;
          align-items: center;
          flex: 1;
          min-width: 0;
        }

        .emotion-bar-label {
          font-size: 9px;
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 6px;
          flex-shrink: 0;
        }

        .emotion-bar {
          width: 100%;
          display: flex;
          align-items: flex-end;
          justify-content: center;
          padding-bottom: 8px;
          border-radius: 4px 4px 0 0;
        }

        .emotion-bar-name {
          font-size: 10px;
          color: #6b7280;
          margin-top: 8px;
          text-align: center;
          flex-shrink: 0;
        }

          .header h1 {
            font-size: 14px;
          }

          .add-button, .add-button1 {
            padding: 8px 12px;
            font-size: 11px;
            gap: 6px;
          }

          .stats-grid {
            grid-template-columns: 1fr 1fr;
            gap: 10px;
            margin-bottom: 13px;
          }

          .stat-card-inner {
            padding: 10px;
          }

          .stat-title {
            font-size: 11px;
            margin-bottom: 10px;
          }

          .time-label {
            font-size: 12.5px;
          }

          .time-percentage {
            font-size: 12.5px;
          }

          .emotion-chart-container {
            height: 70px;
          }

          .emotion-bar-label {
            font-size: 8px;
          }

          .emotion-bar-name {
            font-size: 9px;
          }

          .affirmation-card {
            margin-bottom: 12px;
          }

          .affirmation-inner {
            min-height: 260px;
          }

          .affirmation-image-container {
            height: 150px;
          }

          .affirmation-content {
            padding: 16px;
          }

          .affirmation-title {
            font-size: 11px;
          }

          .affirmation-message {
            font-size: 13px;
          }

          .action-button-podcast {
            font-size: 11px;
            padding: 6px 12px;
          }

          .entries-grid, .all-entries-grid {
            grid-template-columns: 1fr;
            gap: 10px;
            padding: 16px;
          }

          .entry-card-inner {
            padding: 12px;
          }

          .entry-title {
            font-size: 14px;
          }

          .entry-meta {
            font-size: 10px;
          }

          .entry-content {
            font-size: 12px;
            -webkit-line-clamp: 3;
          }

          .background-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 8px;
          }

          .modal {
            padding: 18px;
          }

          .swipe-card-inner {
            padding: 18px;
            min-height: 240px;
          }

          .question-text {
            font-size: 15px;
          }

          .option-button {
            padding: 12px 14px;
            font-size: 12px;
          }

          .form-input {
            padding: 10px 12px;
            font-size: 13px;
          }

          .form-textarea {
            min-height: 150px;
          }
        }
        @keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes slideInRight {
  from {
    opacity: 0;
    transform: translateX(20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}
  @keyframes confettiFall {
  0% { 
    transform: translateY(0) rotate(0deg);
    opacity: 1;
  }
  100% { 
    transform: translateY(100vh) rotate(720deg);
    opacity: 0;
  }
}
  .floating-write-button {
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 46px;
  height: 46px;
  background: #000000ff;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
  z-index: 9998;
  transition: all 0.3s ease;
}

.floating-write-button:active {
  transform: scale(0.95);
}
  .custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: #f3f4f6;
  border-radius: 10px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 10px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}
  /* Success Check Animation */
.success-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.3s ease;
}

.success-check-circle {
  width: 100px;
  height: 100px;
  animation: scaleIn 0.3s ease;
}

.success-check {
  width: 100%;
  height: 100%;
}

.success-check-circle-bg {
  stroke: #4CAF50;
  stroke-width: 2;
  fill: none;
  animation: drawCircle 0.6s ease forwards;
  stroke-dasharray: 166;
  stroke-dashoffset: 166;
}

.success-check-mark {
  stroke: #4CAF50;
  stroke-width: 3;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-dasharray: 48;
  stroke-dashoffset: 48;
  animation: drawCheck 0.4s 0.4s ease forwards;
}

@keyframes drawCircle {
  to {
    stroke-dashoffset: 0;
  }
}

@keyframes drawCheck {
  to {
    stroke-dashoffset: 0;
  }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes scaleIn {
  from { transform: scale(0.5); }
  to { transform: scale(1); }
}

.modal-saving {
  opacity: 0.3;
  pointer-events: none;
  transition: opacity 0.3s ease;
}

.modal-inner {
  transition: opacity 0.3s ease;
}

/* Disabled button style */
.btn-disabled {
  background: #ccc !important;
  color: #666 !important;
  cursor: not-allowed !important;
  opacity: 0.6;
}

.btn-primary:disabled {
  background: #ccc;
  color: #666;
  cursor: not-allowed;
  opacity: 0.6;
}

/* Image preview grid */
.image-preview-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 10px;
  margin-top: 10px;
}

.image-preview-item {
  position: relative;
  aspect-ratio: 1;
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid #ddd;
}

.image-preview-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}


      `}</style>

      <div className="journal-container">
        <div className="journal-inner">
{/* HEADER */}
<div 
  className="header"
  style={{ 
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%"
  }}
>
  {/* LEFT SIDE — Title */}
  <h1>My Space</h1>

  {/* RIGHT SIDE — Buttons */}
  <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
{/* Notification Toggle Button */}
{/*
<button
  onClick={toggleNotifications}
  className="add-button1"
  style={{
    background: notificationsEnabled ? '#f1f1f1ff' : '#f1f1f1ff',
    color: '#858585ff',
    display: "flex",
    alignItems: "center",
    gap: "6px",
    overflow: "hidden",
    position: "relative",
    width: showNotificationText ? "auto" : "40px",
    transition: "width 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
    justifyContent: "flex-end",
    paddingRight: showNotificationText ? "12px" : "10px",
    paddingLeft: showNotificationText ? "12px" : "10px"
  }}
  title={notificationsEnabled ? 'Notifications enabled' : 'Notifications disabled'}
>
  <span style={{
    opacity: showNotificationText ? 1 : 0,
    transform: showNotificationText ? "translateX(0)" : "translateX(20px)",
    transition: "opacity 0.5s ease, transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    whiteSpace: "nowrap",
    fontSize: "12px",
    fontWeight: "500"
  }}>
    {notificationsEnabled ? 'Journal Reminder On' : 'Journal Reminder Off'}
  </span>
  
  <svg 
    width="18" 
    height="18" 
    viewBox="0 0 24 24" 
    fill="none"
    style={{ flexShrink: 0 }}
    aria-hidden="true"
  >
    {notificationsEnabled ? (
      // Bell without slash (enabled) - BLACK
      <path 
        d="M17.133 12.632v-1.8a5.407 5.407 0 0 0-4.154-5.262.955.955 0 0 0 .021-.106V3.1a1 1 0 0 0-2 0v2.364a.933.933 0 0 0 .021.106 5.406 5.406 0 0 0-4.154 5.262v1.8C6.867 15.018 5 15.614 5 16.807 5 17.4 5 18 5.538 18h12.924C19 18 19 17.4 19 16.807c0-1.193-1.867-1.789-1.867-4.175Zm-13.267-.8a1 1 0 0 1-1-1 9.424 9.424 0 0 1 2.517-6.391A1.001 1.001 0 1 1 6.854 5.8a7.43 7.43 0 0 0-1.988 5.037 1 1 0 0 1-1 .995Zm16.268 0a1 1 0 0 1-1-1A7.431 7.431 0 0 0 17.146 5.8a1 1 0 0 1 1.471-1.354 9.424 9.424 0 0 1 2.517 6.391 1 1 0 0 1-1 .995ZM8.823 19a3.453 3.453 0 0 0 6.354 0H8.823Z" 
        fill="#000000"
      />
    ) : (
      // Bell with slash (disabled) - BLACK
      <>
        <path 
          d="M17.133 12.632v-1.8a5.407 5.407 0 0 0-4.154-5.262.955.955 0 0 0 .021-.106V3.1a1 1 0 0 0-2 0v2.364a.933.933 0 0 0 .021.106 5.406 5.406 0 0 0-4.154 5.262v1.8C6.867 15.018 5 15.614 5 16.807 5 17.4 5 18 5.538 18h12.924C19 18 19 17.4 19 16.807c0-1.193-1.867-1.789-1.867-4.175Zm-13.267-.8a1 1 0 0 1-1-1 9.424 9.424 0 0 1 2.517-6.391A1.001 1.001 0 1 1 6.854 5.8a7.43 7.43 0 0 0-1.988 5.037 1 1 0 0 1-1 .995Zm16.268 0a1 1 0 0 1-1-1A7.431 7.431 0 0 0 17.146 5.8a1 1 0 0 1 1.471-1.354 9.424 9.424 0 0 1 2.517 6.391 1 1 0 0 1-1 .995ZM8.823 19a3.453 3.453 0 0 0 6.354 0H8.823Z" 
          fill="#000000"
        />
        <line 
          x1="4" 
          y1="20" 
          x2="20" 
          y2="4" 
          stroke="#000000" 
          strokeWidth="2" 
          strokeLinecap="round"
        />
      </>
    )}
  </svg>
</button>
 */}
    {/* Settings Button */}
    {localStorage.getItem('skipLockPrompt') === 'true' && (
      <button
        className="add-button1"
        onClick={() => {
          if (window.confirm('Reset lock prompt settings? You will be asked again when creating entries.')) {
            localStorage.removeItem('skipLockPrompt');
            localStorage.removeItem('autoLockEntries');
            alert('Settings reset successfully');
          }
        }}
        title="Reset lock settings"
        style={{
          background: '#f3f4f6',
          color: '#374151'
        }}
      >
        <Settings size={16} />
      </button>
    )}
  </div>
</div>
<CustomizableCardSection
  entries={entries}
  timeStats={timeStats}
  moodStats={moodStats}
  currentStreak={displayStreak}  // ✅ PASS THE CORRECT STREAK
/>

{/* VIEW ALL ENTRIES SECTION - ADD THIS BACK */}
<div className="glass-container" style={{ marginBottom: '5px', marginTop:'-15px'}}>
  <div className="glass-inner" style={{ padding: '12px' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div className="viewall-button">
        <h2 className="section-title1" style={{ margin: 0 }} onClick={() => setShowViewAllModal(true)}>
          View Entries
        </h2>
      </div>
      <button className="add-button" onClick={() => setShowAddModal(true)}>
        <svg width="17" height="17" fill="none" viewBox="0 0 24 24">
          <path stroke="white" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="m14.304 4.844 2.852 2.852M7 7H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1v-4.5m2.409-9.91a2.017 2.017 0 0 1 0 2.853l-6.844 6.844L8 14l.713-3.565 6.844-6.844a2.015 2.015 0 0 1 2.852 0Z"/>
        </svg>
        Create
      </button>
    </div>
  </div>
</div>

{/* JOURNAL TABS SECTION - ADD THIS BACK */}
<JournalTabs
  activeJournalTab={activeJournalTab}
  setActiveJournalTab={setActiveJournalTab}
  entries={entries}
  isEntryLocked={isEntryLocked}
  setCurrentLockAction={setCurrentLockAction}
  setShowPinModal={setShowPinModal}
  setPinInput={setPinInput}
  setSelectedEntry={setSelectedEntry}
  setShowSingleEntry={setShowSingleEntry}
  toggleLock={toggleLock}
  formatDate={formatDate}
  showWisdomCards={showWisdomCards}
  setShowWisdomCards={setShowWisdomCards}
  wisdomCards={wisdomCards}
  currentWisdomIndex={currentWisdomIndex}
  setCurrentWisdomIndex={setCurrentWisdomIndex}
  generatePersonalWisdomCards={generatePersonalWisdomCards}
  isGeneratingWisdom={isGeneratingWisdom}
  isGeneratingReflection={isGeneratingReflection}
  reflectionAudio={reflectionAudio}
  setShowPodcastSettings={setShowPodcastSettings}
  calculateMoodStats={calculateMoodStats}
  calculateStreak={calculateStreak}
  userName={userName}
  loadEntryImages={loadEntryImages}  // ✅ ADD THIS
/>
{showPodcastSettings && (
  <div className="podcast-settings-modal" onClick={() => setShowPodcastSettings(false)}>
    <div className="podcast-settings-card" onClick={(e) => e.stopPropagation()}>
      <div className="settings-header">
        <Settings size={16} color="#272727" />
        <h3 className="settings-title">Personalize Your Reflection</h3>
      </div>
      
      <div className="setting-group">
        <label className="setting-label">Host Personality</label>
        <div className="setting-options">
          {['warm', 'energetic', 'calm', 'intellectual'].map(type => (
            <button
              key={type}
              className={`setting-option ${podcastPreferences.hostPersonality === type ? 'selected' : ''}`}
              onClick={() => setPodcastPreferences({...podcastPreferences, hostPersonality: type})}
            >
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </button>
          ))}
        </div>
      </div>
      
      <div className="setting-group">
        <label className="setting-label">Focus On</label>
        <div className="setting-options">
          {['growth', 'patterns', 'emotions', 'achievements'].map(type => (
            <button
              key={type}
              className={`setting-option ${podcastPreferences.focus === type ? 'selected' : ''}`}
              onClick={() => setPodcastPreferences({...podcastPreferences, focus: type})}
            >
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </button>
          ))}
        </div>
      </div>
      
      <div className="setting-group">
        <label className="setting-label">Pacing</label>
        <div className="setting-options" style={{gridTemplateColumns: 'repeat(3, 1fr)'}}>
          {['slow', 'medium', 'fast'].map(type => (
            <button
              key={type}
              className={`setting-option ${podcastPreferences.pacing === type ? 'selected' : ''}`}
              onClick={() => setPodcastPreferences({...podcastPreferences, pacing: type})}
            >
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </button>
          ))}
        </div>
      </div>
      
      <div className="settings-actions">
        <button className="settings-action-btn settings-cancel" onClick={() => setShowPodcastSettings(false)}>
          Cancel
        </button>
        <button className="settings-action-btn settings-generate" onClick={handleGenerateReflection}>
          <Sparkles size={12} />
          Generate
        </button>
      </div>
    </div>
  </div>
)}
{/* CREATE ENTRY MODAL - QUESTIONS - NEW STYLED */}
{/* CREATE ENTRY MODAL - QUESTIONS - NEW STYLED */}
{showAddModal && !showWritingForm && (
  <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(8px)',
    zIndex: 9999,
    display: 'flex',
    alignItems: 'flex-end',
    animation: 'fadeIn 0.3s ease-out'
  }}>
    <style>{`
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      
      @keyframes slideUp {
        from {
          transform: translateY(100%);
          opacity: 0;
        }
        to {
          transform: translateY(0);
          opacity: 1;
        }
      }
      
      @keyframes scaleIn {
        from {
          transform: scale(0.95);
          opacity: 0;
        }
        to {
          transform: scale(1);
          opacity: 1;
        }
      }
      
      @keyframes fadeSlideIn {
        from {
          transform: translateY(20px);
          opacity: 0;
        }
        to {
          transform: translateY(0);
          opacity: 1;
        }
      }
      
      @keyframes fadeInUp {
        from {
          opacity: 0;
          transform: translateY(20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      
      .question-card-enter {
        animation: scaleIn 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      }
    `}</style>
    
    <div style={{
      width: '100%',
      height: '88vh',
      background: 'white',
      borderRadius: '24px 24px 0 0',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      animation: 'slideUp 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
    }}>
      {/* Header */}
      <div style={{
        padding: '20px 20px 16px 20px',
        borderBottom: '1px solid #f0f0f0',
        background: '#fafafa',
        animation: 'fadeSlideIn 0.5s ease-out 0.2s backwards'
      }}>
        <button
          onClick={resetModal}
          style={{
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '50%',
            width: '30px',
            height: '30px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            marginBottom: '16px',
            transition: 'all 0.2s ease'
          }}
        >
          <X size={17} color="#807f7e" />
        </button>

        <div style={{ textAlign: 'left' }}>
          <h2 style={{
            fontSize: '16px',
            fontWeight: '500',
            color: '#1f2937',
            marginBottom: '6px',
            margin: 0,
          }}>
            New Journal Entry
          </h2>
          <p style={{
            fontSize: '14px',
            color: '#9ca3af',
            margin: '4px 0 0 0',
            lineHeight: '1.5'
          }}>
             Capture your moments here
          </p>
        </div>
      </div>

      {/* Scrollable Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>

        {/* STYLED QUESTION CARD WITH IMAGE & GLASSY EFFECT */}
        <div className="question-card-enter" style={{
          position: 'relative',
          borderRadius: '28px',
          overflow: 'hidden',
          marginBottom: '20px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          background: 'linear-gradient(135deg, rgba(255, 226, 219, 0.4) 0%, rgba(225, 239, 255, 0.4) 50%, rgba(234, 222, 255, 0.4) 100%)',
          border: '1px solid rgba(255, 255, 255, 1)',
          backdropFilter: 'blur(20px)',
          minHeight: currentQuestion.type === 'background' ? '500px' : '420px',
        }}>
          {/* Background Image with Gradient Overlay */}
          {currentQuestionIndex === 0 && (
            <>
              <img
                src="/image/studycard7.png"
                alt="How are you feeling"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  filter: 'blur(9px)',
                  transform: 'scale(1.2)',
                  opacity: 0.5,
                  zIndex: 0,
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(180deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                  zIndex: 1
                }}
              />
            </>
          )}

          {currentQuestionIndex === 1 && (
            <>
              <img
                src="/image/studycard6.png"
                alt="Writing style"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  filter: 'blur(8px)',
                  transform: 'scale(1.2)',
                  opacity: 0.6,
                  zIndex: 0,
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(180deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                  zIndex: 1
                }}
              />
            </>
          )}

          {currentQuestionIndex === 2 && (
            <>
              <img
                src="/image/studycard13.png"
                alt="Journal purpose"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  filter: 'blur(3px)',
                  transform: 'scale(1.1)',
                  opacity: 0.5,
                  zIndex: 0,
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(180deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                  zIndex: 1
                }}
              />
            </>
          )}

          {currentQuestionIndex === 3 && (
            <>
              <img
                src="/image/session-img50.png"
                alt="Background selection"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  filter: 'blur(14px)',
                  transform: 'scale(1.3)',
                  opacity: 0.6,
                  zIndex: 0,
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(180deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)',
                  zIndex: 1
                }}
              />
            </>
          )}

          {/* Content Container */}
          <div
            style={{
              position: 'relative',
              zIndex: 2,
              padding: '32px 24px',
              minHeight: currentQuestion.type === 'background' ? '500px' : '420px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: currentQuestion.type === 'background' ? 'flex-start' : 'space-between'
            }}
          >
            {/* Question Header */}
            <div style={{ animation: 'fadeSlideIn 0.5s ease-out 0.3s backwards' }}>
              <div style={{
                fontSize: '10px',
                fontWeight: '600',
                color: 'rgba(0, 0, 0, 0.5)',
                marginBottom: '12px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Question {currentQuestionIndex + 1} of {questions.length}
              </div>

              <h3 style={{
                fontSize: '16px',
                lineHeight: '1.5',
                color: '#1f2937',
                fontWeight: '600',
                marginBottom: '28px',
                margin: '0 0 28px 0'
              }}>
                {currentQuestion.question}
              </h3>
            </div>

            {/* Options Container */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {currentQuestion.type === 'background' ? (
                // Background Selection Grid - SMALLER IMAGES
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '28px',
                  padding: '24px',
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                  animation: 'fadeSlideIn 0.5s ease-out 0.4s backwards'
                }}>
                  <div style={{
                    fontSize: '12px',
                    color: '#6b7280',
                    marginBottom: '20px',
                    textAlign: 'center',
                    fontWeight: '500'
                  }}>
                    Select up to 3 images ({newEntry.selectedImages.length}/3 selected)
                  </div>

                  <div style={{
                    maxHeight: '280px',
                    overflowY: 'auto',
                    padding: '2px',
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#d1d5db #f9fafb'
                  }}>
                    {/* Upload Button - Smaller */}
                    <label
                      style={{
                        width: '100%',
                        height: '32px',
                        border: '1.5px dashed #d1d5db',
                        borderRadius: '24px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        background: 'linear-gradient(135deg, #f9fafb 0%, #e5e7eb 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        marginBottom: '12px',
                        position: 'relative',
                        zIndex: 1
                      }}
                    >
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (!file) return;

                          if (newEntry.selectedImages.length >= 3) {
                            alert('You can only select up to 3 images');
                            return;
                          }

                          const reader = new FileReader();
                          reader.onloadend = () => {
                            const img = new Image();
                            img.onload = () => {
                              const newImage = {
                                data: reader.result,
                                id: Date.now() + Math.random(),
                                width: img.width,
                                height: img.height,
                                aspectRatio: img.width / img.height,
                                isCustom: true,
                                value: reader.result,
                                preview: reader.result
                              };
                              
                              setUploadedPhotos([newImage, ...uploadedPhotos]);
                              setNewEntry({
                                ...newEntry,
                                selectedImages: [...newEntry.selectedImages, newImage]
                              });
                            };
                            img.src = reader.result;
                          };
                          reader.readAsDataURL(file);
                        }}
                        style={{ display: 'none' }}
                      />
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                        <circle cx="8.5" cy="8.5" r="1.5"></circle>
                        <polyline points="21 15 16 10 5 21"></polyline>
                      </svg>
                      <span
                        style={{
                          fontSize: '11px',
                          color: '#6b7280',
                          fontWeight: '500',
                          lineHeight: 1
                        }}
                      >
                        Click to Upload Image
                      </span>
                    </label>

                    {/* Image Grid - 3 COLUMNS WITH SMALLER IMAGES */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, 1fr)',
                      gap: '10px',
                    }}>
                      {[...uploadedPhotos, ...backgroundImages].map((bg) => {
                        const isSelected = newEntry.selectedImages.some(img => 
                          img.data === bg.value || img.value === bg.value || img.data === bg.data
                        );
                        const selectionIndex = newEntry.selectedImages.findIndex(img => 
                          img.data === bg.value || img.value === bg.value || img.data === bg.data
                        );

                        return (
                          <div
                            key={bg.id || bg.value}
                            style={{
                              aspectRatio: '1/1',
                              border: isSelected ? '2.5px solid #272727' : '2px solid #e5e7eb',
                              borderRadius: '12px',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              position: 'relative',
                              overflow: 'hidden',
                              opacity: isSelected ? 0.9 : 1,
                              boxShadow: isSelected 
                                ? '0 4px 12px rgba(0, 0, 0, 0.12)' 
                                : '0 2px 6px rgba(0, 0, 0, 0.05)',
                              background: 'white'
                            }}
                            onClick={() => handleBackgroundSelect(bg.value || bg.data)}
                          >
                            <img 
                              src={bg.preview || bg.value || bg.data} 
                              alt="Background" 
                              style={{ 
                                width: '100%', 
                                height: '100%', 
                                objectFit: 'cover' 
                              }} 
                            />

                            {bg.isCustom && (
                              <button
                                onClick={(e) => deleteCustomPhoto(bg.id, e)}
                                style={{
                                  position: 'absolute',
                                  top: '6px',
                                  left: '6px',
                                  width: '22px',
                                  height: '22px',
                                  background: 'rgba(239, 68, 68, 0.95)',
                                  border: 'none',
                                  borderRadius: '50%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: 'pointer',
                                  zIndex: 2,
                                  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.2)',
                                  transition: 'all 0.2s ease'
                                }}
                              >
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                                  <line x1="18" y1="6" x2="6" y2="18"></line>
                                  <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                              </button>
                            )}

                            {isSelected && (
                              <div style={{
                                position: 'absolute',
                                top: '6px',
                                right: '6px',
                                width: '24px',
                                height: '24px',
                                background: '#272727',
                                color: 'white',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '11px',
                                fontWeight: '600',
                                zIndex: 2,
                                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3)',
                                animation: 'scaleIn 0.3s ease-out'
                              }}>
                                {selectionIndex + 1}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : (
                // Mood/Style/Purpose Options - WITH STAGGERED ANIMATION
                <>
                  {currentQuestion.options.map((option, idx) => {
                    const isSelected = newEntry[currentQuestion.type] === (option.value || option);
                    return (
                      <button
                        key={option.value || option}
                        style={{
                          padding: '14px 16px',
                          border: isSelected ? '2px solid #272727' : '1px solid rgba(255, 255, 255, 0.4)',
                          borderRadius: '24px',
                          background: isSelected
                            ? 'rgba(255, 255, 255, 0.95)'
                            : 'rgba(255, 255, 255, 0.6)',
                          backdropFilter: 'blur(10px)',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          textAlign: 'left',
                          fontSize: '13px',
                          fontWeight: isSelected ? '600' : '500',
                          color: isSelected ? '#1f2937' : '#374151',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          boxShadow: isSelected ? '0 4px 12px rgba(0, 0, 0, 0.08)' : 'none',
                          animation: 'fadeInUp 0.4s ease-out',
                          animationDelay: `${0.25 + idx * 0.08}s`,
                          animationFillMode: 'both'
                        }}
                        onClick={() => setNewEntry({ ...newEntry, [currentQuestion.type]: option.value || option })}
                        onMouseEnter={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.6)';
                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.75)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.6)';
                          }
                        }}
                      >
                        {option.svg && (
                          <div style={{
                            color: isSelected ? '#272727' : '#6b7280',
                            display: 'flex',
                            alignItems: 'center',
                            flexShrink: 0,
                            width: '20px',
                            height: '20px'
                          }}>
                            {option.svg}
                          </div>
                        )}
                        <span style={{ flex: 1 }}>{option.label || option}</span>
                        {isSelected && (
                          <div style={{
                            width: '16px',
                            height: '16px',
                            background: '#272727',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            animation: 'scaleIn 0.2s ease-out'
                          }}>
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Navigation Buttons */}
        <div style={{ 
          display: 'flex', 
          gap: '8px',
          animation: 'fadeSlideIn 0.5s ease-out 0.5s backwards'
        }}>
          <button
            style={{
              flex: 1,
              padding: '12px',
              background: 'white',
              color: '#374151',
              border: '1px solid #e5e7eb',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: '500',
              cursor: currentQuestionIndex === 0 ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              opacity: currentQuestionIndex === 0 ? 0.5 : 1
            }}
            onClick={handlePreviousQuestion}
            disabled={currentQuestionIndex === 0}
          >
            Previous
          </button>
          <button
            style={{
              flex: 1,
              padding: '12px',
              background: (currentQuestion.type === 'background' && newEntry.selectedImages.length === 0) ||
                (currentQuestion.type !== 'background' && !newEntry[currentQuestion.type])
                ? 'rgba(243, 244, 246, 0.6)'
                : '#272727',
              color: (currentQuestion.type === 'background' && newEntry.selectedImages.length === 0) ||
                (currentQuestion.type !== 'background' && !newEntry[currentQuestion.type])
                ? '#9ca3af'
                : 'white',
              border: 'none',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: '500',
              cursor: (currentQuestion.type === 'background' && newEntry.selectedImages.length === 0) ||
                (currentQuestion.type !== 'background' && !newEntry[currentQuestion.type])
                ? 'not-allowed'
                : 'pointer',
              transition: 'all 0.2s ease'
            }}
            onClick={handleNextQuestion}
            disabled={(currentQuestion.type === 'background' && newEntry.selectedImages.length === 0) ||
              (currentQuestion.type !== 'background' && !newEntry[currentQuestion.type])}
          >
            {currentQuestionIndex === questions.length - 1 ? 'Start Writing' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  </div>
)}

{/* WRITING FORM MODAL */}
{showWritingForm && (
  <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(8px)',
    zIndex: 9999,
    display: 'flex',
    alignItems: 'flex-end',
    animation: 'fadeIn 0.3s ease-out',
  }}>
    <div style={{
      width: '100%',
      height: '90vh',
      background: 'white',
      borderRadius: '24px 24px 0 0',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      animation: 'slideUp 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      position: 'relative', // ✅ Required for success overlay
    }}>
      {/* Header */}
      <div style={{
        padding: '20px 20px 16px 20px',
        borderBottom: '1px solid #e5e7eb',
        background: '#fafafa',
        opacity: showSaveSuccess ? 0 : 1,
        pointerEvents: showSaveSuccess ? 'none' : 'auto',
        transition: 'opacity 0.2s ease',
         animation: 'slideUp 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
      }}>
        <button
          onClick={resetModal}
          style={{
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '50%',
            width: '30px',
            height: '30px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            marginBottom: '16px',
          }}
        >
          <X size={17} color="#807f7e" />
        </button>

        <div style={{ textAlign: 'left', animation: 'slideUp 0.4s cubic-bezier(0.4, 0, 0.2, 1)' }}>
          <h2 style={{
            fontSize: '18px',
            fontWeight: '500',
            color: '#1f2937',
            marginBottom: '6px',
            margin: 0,
          }}>
            Write Your Entry
          </h2>
          <p style={{
            fontSize: '14px',
            color: '#9ca3af',
            margin: '4px 0 0 0',
            lineHeight: '1.5'
          }}>
            Express your thoughts and feelings
          </p>
        </div>
      </div>

      {/* Scrollable Content */}
      <div style={{ 
        flex: 1, 
        overflowY: 'auto', 
        padding: '24px',
        opacity: showSaveSuccess ? 0 : 1,
        pointerEvents: showSaveSuccess ? 'none' : 'auto',
        transition: 'opacity 0.2s ease',
              animation: 'slideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)'

      }}>

        {/* Selected Images Display */}
        <div className="form-group">
          <label className="form-label">
            Selected Images ({newEntry.selectedImages?.length || 0}/3)
          </label>
          
          <div 
            style={{
              minHeight: newEntry.selectedImages?.length > 0 ? 'auto' : '100px',
              border: newEntry.selectedImages?.length > 0 ? 'none' : '2px dashed #d1d5db',
              borderRadius: '16px',
              padding: newEntry.selectedImages?.length > 0 ? '0' : '20px',
              background: newEntry.selectedImages?.length > 0 ? 'transparent' : '#f9fafb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '16px'
            }}
          >
            {newEntry.selectedImages && newEntry.selectedImages.length > 0 ? (
              <div 
                style={{
                  display: 'grid',
                  gap: '8px',
                  width: '100%',
                  gridTemplateColumns: newEntry.selectedImages.length === 1 ? '1fr' : 
                                     newEntry.selectedImages.length === 2 ? 'repeat(2, 1fr)' : 
                                     'repeat(2, 1fr)',
                  gridAutoRows: '120px'
                }}
              >
                {newEntry.selectedImages.map((image, idx) => (
                  <div 
                    key={image.id || idx}
                    style={{
                      position: 'relative',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      background: '#f3f4f6',
                      border: '2px solid #e5e7eb',
                      gridColumn: newEntry.selectedImages.length === 3 && idx === 0 ? '1' : 'auto',
                      gridRow: newEntry.selectedImages.length === 3 && idx === 0 ? 'span 2' : 'span 1'
                    }}
                  >
                    <img 
                      src={image.data || image} 
                      alt={`Selected ${idx + 1}`}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                    <button
                      onClick={() => {
                        setNewEntry({
                          ...newEntry,
                          selectedImages: newEntry.selectedImages.filter((_, i) => i !== idx)
                        });
                      }}
                      type="button"
                      style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        width: '28px',
                        height: '28px',
                        background: 'rgba(0, 0, 0, 0.6)',
                        border: 'none',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        zIndex: 2,
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.8)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.6)'}
                    >
                      <X size={14} color="white" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', color: '#9ca3af' }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ margin: '0 auto 8px' }}>
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                  <circle cx="8.5" cy="8.5" r="1.5"></circle>
                  <polyline points="21 15 16 10 5 21"></polyline>
                </svg>
                <p style={{ fontSize: '13px', margin: 0 }}>
                  No images selected yet
                </p>
              </div>
            )}
          </div>
          
          {newEntry.selectedImages && newEntry.selectedImages.length > 0 && (
            <button
              onClick={() => setShowWritingForm(false)}
              style={{
                width: '100%',
                padding: '12px',
                background: '#f3f4f6',
                border: '1px solid #e5e7eb',
                borderRadius: '16px',
                fontSize: '13px',
                fontWeight: '500',
                color: '#374151',
                cursor: 'pointer',
                transition: 'all 0.2s',
                marginBottom: '16px'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#e5e7eb'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#f3f4f6'}
            >
              Change Background Images
            </button>
          )}
        </div>

        {/* Title Input */}
        <div className="form-group">
          <label className="form-label">Title</label>
          <input
            style={{ borderRadius: '22px' }}
            type="text"
            className="form-input"
            placeholder="Give your entry a title..."
            value={newEntry.title}
            onChange={(e) => setNewEntry({ ...newEntry, title: e.target.value })}
          />
        </div>

        {/* Content Textarea */}
        <div className="form-group">
          <label className="form-label">Your Story</label>
          <div style={{ position: 'relative' }}>
            <textarea
              className="form-input form-textarea"
              placeholder="Write your thoughts and feelings here..."
              value={newEntry.content}
              onChange={(e) => setNewEntry({ ...newEntry, content: e.target.value })}
              style={{ 
                paddingBottom: '60px', 
                minHeight: '200px',   
                borderRadius: '22px'
              }}
            />
          </div>
        </div>

        {/* Tags Input */}
        <div className="form-group">
          <label className="form-label">Tags</label>
          <div className="tag-input-container">
            <input
              style={{ borderRadius: '22px' }}
              type="text"
              className="form-input tag-input"
              placeholder="Add tags..."
              value={newEntry.tagInput}
              onChange={(e) => setNewEntry({ ...newEntry, tagInput: e.target.value })}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addTag(e.target.value);
                }
              }}
            />
            <button className="tag-add-button" onClick={() => addTag(newEntry.tagInput)}>
              Add
            </button>
          </div>
          {newEntry.tags.length > 0 && (
            <div className="selected-tags">
              {newEntry.tags.map(tag => (
                <div key={tag} className="selected-tag">
                  #{tag}
                  <button className="tag-remove" onClick={() => removeTag(tag)}>
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Spacer */}
        <div style={{ height: '80px' }} />
      </div>

      {/* Fixed Bottom Actions */}
      <div style={{
        padding: '16px 24px',
        borderTop: '1px solid #e5e7eb',
        background: 'white',
        display: 'flex',
        gap: '12px',
        opacity: showSaveSuccess ? 0 : 1,
        pointerEvents: showSaveSuccess ? 'none' : 'auto',
        transition: 'opacity 0.2s ease'
      }}>
        <button 
          style={{
            flex: 1,
            padding: '10px',
            background: 'white',
            color: '#374151',
            border: '2px solid #e5e7eb',
            borderRadius: '22px',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px'
          }}
          onClick={() => setShowWritingForm(false)}
          onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
        >
          Previous
        </button>
        <button 
          style={{
            flex: 1,
            padding: '10px',
            background: '#0c0c0cff',
            color: 'white',
            border: 'none',
            borderRadius: '22px',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
          onClick={handleAddEntry}
          onMouseEnter={(e) => e.currentTarget.style.background = '#000000'}
          onMouseLeave={(e) => e.currentTarget.style.background = '#0c0c0cff'}
        >
          Save Entry
        </button>
      </div>

      {/* ✅ SUCCESS OVERLAY - Covers entire modal */}
      {showSaveSuccess && (
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '24px 24px 0 0',
          zIndex: 100,
          animation: 'fadeIn 0.2s ease'
        }}>
          {/* Animated Checkmark */}
          <svg 
            width="80" 
            height="80" 
            viewBox="0 0 100 100" 
            fill="none"
            style={{ marginBottom: '20px' }}
          >
            <circle
              cx="50"
              cy="50"
              r="40"
              stroke="black"
              strokeWidth="2"
              fill="none"
              style={{
                animation: 'drawCircle 0.5s ease-out forwards',
                strokeDasharray: 251,
                strokeDashoffset: 251
              }}
            />
            <path
              d="M 30 50 L 42 62 L 70 35"
              stroke="black"
              strokeWidth="3"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{
                animation: 'drawCheck 0.4s ease-out 0.4s forwards',
                strokeDasharray: 80,
                strokeDashoffset: 80
              }}
            />
          </svg>

          <p style={{
            fontSize: '14px',
            fontWeight: '600',
            color: '#1f2937',
            margin: 0,
            animation: 'fadeInText 0.3s ease 0.8s forwards',
            opacity: 0
          }}>
            Entry Saved
          </p>

          <style>{`
            @keyframes drawCircle {
              to { stroke-dashoffset: 0; }
            }
            @keyframes drawCheck {
              to { stroke-dashoffset: 0; }
            }
            @keyframes fadeInText {
              to { opacity: 1; }
            }
            @keyframes fadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
          `}</style>
        </div>
      )}
    </div>
  </div>
)}
{showEditModal && selectedEntry && (
  <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(8px)',
    zIndex: 10000,
    display: 'flex',
    alignItems: 'flex-end',
    animation: 'fadeIn 0.3s ease-out'
  }}>
    <div style={{
      width: '100%',
      height: '88vh',
      background: 'white',
      borderRadius: '24px 24px 0 0',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      animation: 'slideUp 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      position: 'relative' // ✅ ONLY ADDED THIS
    }}>
      {/* Header */}
      <div style={{
        padding: '20px',
        borderBottom: '1px solid #e5e7eb',
        background: '#fafafa',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <button
          onClick={() => setShowEditModal(false)}
          style={{
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '50%',
            width: '30px',
            height: '30px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
          }}
        >
          <X size={17} color="#807f7e" />
        </button>

        <div style={{ display: 'flex', gap: '8px' }}>
          {/* Lock/Unlock Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleLock(selectedEntry.id);
            }}
            style={{
              background: isEntryLocked(selectedEntry.id) ? '#ffffffff' : 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '20px',
              padding: '8px 16px',
              cursor: 'pointer',
              color: isEntryLocked(selectedEntry.id) ? '#000000ff' : '#000000ff',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '13px',
              fontWeight: '500',
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
            }}
            title={isEntryLocked(selectedEntry.id) ? 'Unlock entry' : 'Lock entry'}
          >
            {isEntryLocked(selectedEntry.id) ? <Lock size={16} /> : <Unlock size={16} />}
            {isEntryLocked(selectedEntry.id) ? 'Locked' : 'Lock'}
          </button>

          {/* Save Button */}
          <button
            onClick={handleUpdateEntry}
            disabled={isSavingChanges}
            style={{
              background: '#000',
              border: 'none',
              borderRadius: '20px',
              padding: '8px 20px',
              color: 'white',
              fontSize: '13px',
              fontWeight: '600',
              cursor: isSavingChanges ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
              opacity: isSavingChanges ? 0.6 : 1
            }}
          >
<svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11.4669 3.72684C11.7558 3.91574 11.8369 4.30308 11.648 4.59198L7.39799 11.092C7.29783 11.2452 7.13556 11.3467 6.95402 11.3699C6.77247 11.3931 6.58989 11.3355 6.45446 11.2124L3.70446 8.71241C3.44905 8.48022 3.43023 8.08494 3.66242 7.82953C3.89461 7.57412 4.28989 7.55529 4.5453 7.78749L6.75292 9.79441L10.6018 3.90792C10.7907 3.61902 11.178 3.53795 11.4669 3.72684Z" fill="currentColor" fill-rule="evenodd" clip-rule="evenodd"></path></svg>            Save
          </button>
        </div>
      </div>

      {/* Scrollable Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
        
        {/* Display Entry Images with Delete */}
        {(() => {
          const images = loadEntryImages(selectedEntry);
          return images.length > 0 ? (
            <div className="edit-modal-images">
              <label style={{
                display: 'block',
                fontSize: '13px',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '8px',
                textAlign:'left'
              }}>
                Entry Images ({images.length})
              </label>
              
              <div className={`edit-modal-images-grid ${getImageGridClass(images.length)}`}>
                {images.map((image, idx) => (
                  <div key={image.id || idx} className="edit-image-wrapper">
                    <img src={image.data} alt="Entry" className="edit-image" />
                    <button
                      className="edit-remove-btn"
                      onClick={() => {
                        const updatedImages = images.filter((_, i) => i !== idx);
                        setSelectedEntry({
                          ...selectedEntry,
                          selectedImages: updatedImages,
                          background: updatedImages[0]?.data || ''
                        });
                      }}
                      type="button"
                    >
                      <X size={12} color="white" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ) : null;
        })()}

        {/* Add More Images Button */}
        {(!selectedEntry.selectedImages || selectedEntry.selectedImages.length < 3) && (
          <label style={{
            display: 'block',
            padding: '12px',
            border: '2px dashed #d1d5db',
            borderRadius: '24px',
            cursor: 'pointer',
            textAlign: 'center',
            marginBottom: '24px',
            background: '#f9fafb',
            transition: 'all 0.2s ease'
          }}>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => {
                const files = Array.from(e.target.files);
                const currentCount = selectedEntry.selectedImages?.length || 0;
                const remaining = 3 - currentCount;
                const filesToAdd = files.slice(0, remaining);

                const promises = filesToAdd.map(file => {
                  return new Promise((resolve) => {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                      const img = new Image();
                      img.onload = () => {
                        resolve({
                          data: reader.result,
                          id: Date.now() + Math.random(),
                          width: img.width,
                          height: img.height,
                          aspectRatio: img.width / img.height
                        });
                      };
                      img.src = reader.result;
                    };
                    reader.readAsDataURL(file);
                  });
                });

                Promise.all(promises).then(images => {
                  setSelectedEntry({
                    ...selectedEntry,
                    selectedImages: [...(selectedEntry.selectedImages || []), ...images]
                  });
                });
              }}
              style={{ display: 'none' }}
            />
            <div style={{ fontSize: '13px', color: '#202c35ff' }}>
              + Add {selectedEntry.selectedImages?.length > 0 ? 'More ' : ''}Images ({3 - (selectedEntry.selectedImages?.length || 0)} slots remaining)
            </div>
          </label>
        )}

        <div style={{ 
          marginBottom: '24px', 
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          fontSize: '13px',
          color: '#6b7280',
          paddingBottom: '16px',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Calendar size={14} />
            {new Date(selectedEntry.date).toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Clock size={14} />
            {new Date(selectedEntry.date).toLocaleTimeString('en-US', { 
              hour: '2-digit', 
              minute: '2-digit' 
            })}
          </div>
        </div>

        {/* Delete Button */}
        <div style={{ marginTop: '32px' }}>
          <button
            onClick={() => {
              setEntryToDelete(selectedEntry.id);
              setShowDeleteConfirm(true);
            }}
            style={{
              width: '100%',
              padding: '12px',
              background: '#b1b1b127',
              border: '1px solid #b6b7b7ff',
              borderRadius: '24px',
              color: '#000000ff',
              fontSize: '13px',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'all 0.2s'
            }}
          >
            <Trash2 size={14} />
            Delete Entry
          </button>
        </div>

        <div style={{ marginBottom: '24px', marginTop: '32px', paddingTop: '24px', borderTop: '1px solid #e5e7eb' }}>
          <label style={{
            display: 'block',
            fontSize: '13px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px',
            textAlign:'left'
          }}>
            Title
          </label>
          <input
            type="text"
            value={selectedEntry.title}
            onChange={(e) => setSelectedEntry({ ...selectedEntry, title: e.target.value })}
            placeholder="Enter title..."
            style={{
              width: '100%',
              padding: '12px 16px',
              fontSize: '15px',
              fontWeight: '600',
              border: '1px solid #e5e7eb',
              borderRadius: '24px',
              outline: 'none',
              transition: 'all 0.2s',
              background: 'white'
            }}
          />
        </div>

        {/* Content */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{
            display: 'block',
            fontSize: '13px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px',
            textAlign:'left'
          }}>
            Content
          </label>
          <textarea
            value={selectedEntry.content}
            onChange={(e) => setSelectedEntry({ ...selectedEntry, content: e.target.value })}
            placeholder="Write your thoughts..."
            style={{
              width: '100%',
              minHeight: '200px',
              padding: '16px',
              fontSize: '15px',
              lineHeight: '1.8',
              border: '1px solid #e5e7eb',
              borderRadius: '24px',
              outline: 'none',
              resize: 'vertical',
              transition: 'all 0.2s',
              fontFamily: 'inherit',
              background: 'white'
            }}
          />
        </div>

        {/* Background Image Selector */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{
            display: 'block',
            fontSize: '13px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px',
            textAlign:'left'
          }}>
            Cover Image
          </label>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '8px',
            maxHeight: '180px',
            overflowY: 'auto',
            padding: '8px',
            background: '#f9fafb',
            borderRadius: '12px',
            border: '1px solid #e5e7eb'
          }}>
            {/* Upload Custom Photo */}
            <label style={{
              aspectRatio: '16/11',
              border: '2px dashed #d1d5db',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px',
              background: '#fff',
              transition: 'all 0.2s ease'
            }}>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  
                  const reader = new FileReader();
                  reader.onloadend = () => {
                    setSelectedEntry({
                      ...selectedEntry,
                      background: reader.result
                    });
                  };
                  reader.readAsDataURL(file);
                }}
                style={{ display: 'none' }}
              />
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <circle cx="8.5" cy="8.5" r="1.5"></circle>
                <polyline points="21 15 16 10 5 21"></polyline>
              </svg>
              <span style={{ fontSize: '9px', color: '#6b7280', fontWeight: '500' }}>
                Upload
              </span>
            </label>

            {/* Show uploaded photos + default backgrounds */}
            {[...uploadedPhotos, ...backgroundImages].map((bg) => (
              <div
                key={bg.id || bg.value}
                onClick={() => setSelectedEntry({
                  ...selectedEntry,
                  background: bg.value
                })}
                style={{
                  aspectRatio: '16/11',
                  border: selectedEntry.background === bg.value ? '3px solid #272727' : '2px solid #e5e7eb',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  overflow: 'hidden',
                  position: 'relative',
                  transition: 'all 0.2s ease'
                }}
              >
                <img 
                  src={bg.preview || bg.value} 
                  alt="Background" 
                  style={{ 
                    width: '100%', 
                    height: '100%', 
                    objectFit: 'cover' 
                  }} 
                />
                {selectedEntry.background === bg.value && (
                  <div style={{
                    position: 'absolute',
                    top: '4px',
                    right: '4px',
                    width: '20px',
                    height: '20px',
                    background: '#272727',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  </div>
                )}
                
                {/* Delete button for custom photos */}
                {bg.isCustom && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteCustomPhoto(bg.id, e);
                    }}
                    style={{
                      position: 'absolute',
                      top: '4px',
                      left: '4px',
                      width: '20px',
                      height: '20px',
                      background: 'rgba(255, 255, 255, 0.4)',
                      border: 'none',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      zIndex: 2
                    }}
                  >
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

{/* Tags */}
<div style={{ marginBottom: '24px' }}>
  <label style={{
    display: 'block',
    fontSize: '13px',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '8px',
    textAlign:'left'
  }}>
    Tags
  </label>
  
  {/* ✅ ADD: Tag input field */}
  <div style={{ 
    display: 'flex', 
    gap: '8px', 
    marginBottom: '12px' 
  }}>
    <input
      type="text"
      placeholder="Add a tag..."
      value={selectedEntry.tagInput || ''}
      onChange={(e) => setSelectedEntry({
        ...selectedEntry,
        tagInput: e.target.value
      })}
      onKeyPress={(e) => {
        if (e.key === 'Enter' && selectedEntry.tagInput?.trim()) {
          const newTag = selectedEntry.tagInput.trim().toLowerCase();
          if (!selectedEntry.tags.includes(newTag)) {
            setSelectedEntry({
              ...selectedEntry,
              tags: [...selectedEntry.tags, newTag],
              tagInput: ''
            });
          }
        }
      }}
      style={{
        flex: 1,
        padding: '10px 14px',
        fontSize: '13px',
        border: '1px solid #e5e7eb',
        borderRadius: '20px',
        outline: 'none',
        transition: 'all 0.2s'
      }}
    />
    <button
      onClick={() => {
        if (selectedEntry.tagInput?.trim()) {
          const newTag = selectedEntry.tagInput.trim().toLowerCase();
          if (!selectedEntry.tags.includes(newTag)) {
            setSelectedEntry({
              ...selectedEntry,
              tags: [...selectedEntry.tags, newTag],
              tagInput: ''
            });
          }
        }
      }}
      style={{
        padding: '10px 20px',
        background: '#272727',
        color: 'white',
        border: 'none',
        borderRadius: '20px',
        fontSize: '13px',
        fontWeight: '500',
        cursor: 'pointer',
        transition: 'all 0.2s'
      }}
    >
      Add
    </button>
  </div>

  {/* Tag display */}
  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
    {selectedEntry?.tags && selectedEntry.tags.length > 0 && (
      selectedEntry.tags.map((tag, index) => {
        const colorIndex = index % 3;
        const tagColor = tagColors[colorIndex];
        return (
          <div
            key={index}
            style={{
              padding: '6px 10px',
              background: tagColor.bg,
              color: tagColor.color,
              borderRadius: '12px',
              fontSize: '11px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            {tag}
            <button
              onClick={() => {
                setSelectedEntry({
                  ...selectedEntry,
                  tags: selectedEntry.tags.filter((_, i) => i !== index)
                });
              }}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: tagColor.color,
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                opacity: 0.7,
                transition: 'opacity 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = 1}
              onMouseLeave={(e) => e.currentTarget.style.opacity = 0.7}
            >
              <X size={14} />
            </button>
          </div>
        );
      })
    )}
  </div>
</div>
      </div>

{/* ✅ CHECKMARK ONLY - No loading spinner */}
{showSuccessCheck && (
  <div style={{
    position: 'absolute',
    inset: 0,
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '24px 24px 0 0',
    zIndex: 100,
    animation: 'fadeIn 0.2s ease'
  }}>
    {/* Animated Checkmark */}
    <svg 
      width="80" 
      height="80" 
      viewBox="0 0 100 100" 
      fill="none"
      style={{
        marginBottom: '20px'
      }}
    >
      {/* Circle - draws first */}
      <circle
        cx="50"
        cy="50"
        r="40"
        stroke="black"
        strokeWidth="2"
        fill="none"
        style={{
          animation: 'drawCircle 0.5s ease-out forwards',
          strokeDasharray: 251,
          strokeDashoffset: 251
        }}
      />
      
      {/* Checkmark - draws second */}
      <path
        d="M 30 50 L 42 62 L 70 35"
        stroke="black"
        strokeWidth="3"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{
          animation: 'drawCheck 0.4s ease-out 0.4s forwards',
          strokeDasharray: 80,
          strokeDashoffset: 80
        }}
      />
    </svg>

    {/* Text */}
    <p style={{
      fontSize: '14px',
      fontWeight: '600',
      color: '#1f2937',
      margin: 0,
      animation: 'fadeInText 0.3s ease 0.8s forwards',
      opacity: 0
    }}>
      Saved Successfully
    </p>

    <style>{`
      @keyframes drawCircle {
        to {
          stroke-dashoffset: 0;
        }
      }

      @keyframes drawCheck {
        to {
          stroke-dashoffset: 0;
        }
      }

      @keyframes fadeInText {
        to {
          opacity: 1;
        }
      }

      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
    `}</style>
  </div>
)}

    </div>
  </div>
)}

<style>{`
  @keyframes scaleIn {
    from { transform: scale(0); opacity: 0; }
    to { transform: scale(1); opacity: 1; }
  }
`}</style>
{/* VIEW ALL ENTRIES MODAL - COMPLETE REDESIGN */}
{showViewAllModal && (
  <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(8px)',
    zIndex: 9999,
    display: 'flex',
    alignItems: 'flex-end',
    animation: 'fadeIn 0.3s ease-out'
  }}>
    <div style={{
      width: '100%',
      height: '88vh',
      background: 'white',
      borderRadius: '24px 24px 0 0',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      animation: 'slideUp 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
    }}>
      {/* Fixed Header with Search */}
      <div style={{
        padding: '20px',
        borderBottom: '1px solid #e5e7eb',
        background: 'white',
        flexShrink: 0
      }}>
        {/* Top Row: Close Button */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <button
            onClick={() => setShowViewAllModal(false)}
            style={{
              background: '#f3f4f6',
              border: 'none',
              borderRadius: '50%',
              width: '36px',
              height: '36px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#e5e7eb'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#f3f4f6'}
          >
            <X size={20} color="#374151" />
          </button>

          <div style={{
            fontSize: '13px',
            fontWeight: '500',
            color: '#6b7280',
            background: '#f9fafb',
            padding: '6px 12px',
            borderRadius: '20px',
                  animation: 'slideUp 0.4s cubic-bezier(0.4, 0, 0.2, 1)'

          }}>
            {filteredEntries.length} {filteredEntries.length === 1 ? 'Entry' : 'Entries'}
          </div>
        </div>

        {/* Title */}
        <h2 style={{
          fontSize: '16px',
          fontWeight: '600',
          color: '#111827',
          margin: '0 0 4px 0',
          textAlign:'left'
        }}>
          Journal Entries
        </h2>
        
        {/* Description */}
        <p style={{
          fontSize: '14px',
          color: '#9ca3af',
          margin: '0 0 16px 0',
          textAlign:'left'
        }}>
          Your personal collection of memories and reflections
        </p>

        {/* Search Bar */}
<div style={{ position: 'relative', marginBottom: '12px' }}>
  <Search
    size={15}
    style={{
      position: 'absolute',
      left: '16px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#9ca3af',
      pointerEvents: 'none'
    }}
  />
  <input
    type="text"
    placeholder="Search by title, content, or tags..."
    value={searchQuery}
    onChange={(e) => {
      const newQuery = e.target.value;
      setSearchQuery(newQuery);
      
      // Apply filters immediately
      let filtered = [...entries];
      
      if (newQuery.trim()) {
        const query = newQuery.toLowerCase();
        filtered = filtered.filter(entry => {
          const titleMatch = entry.title?.toLowerCase().includes(query);
          const contentMatch = entry.content?.toLowerCase().includes(query);
          const tagsMatch = entry.tags?.some(tag => tag.toLowerCase().includes(query));
          return titleMatch || contentMatch || tagsMatch;
        });
      }
      
      if (filterMood !== 'all') {
        filtered = filtered.filter(entry => {
          return entry.mood?.toLowerCase() === filterMood.toLowerCase() || 
                 entry.tags?.some(tag => tag.toLowerCase() === filterMood.toLowerCase());
        });
      }
      
      setFilteredEntries(filtered);
    }}
    style={{
      width: '100%',
      padding: '12px 48px 12px 44px',
      fontSize: '13px',
      border: '1.5px solid #e5e7eb',
      borderRadius: '14px',
      outline: 'none',
      transition: 'all 0.2s',
      background: '#fafafa'
    }}
  />
  <button
    onClick={() => setShowFilters(!showFilters)}
    style={{
      position: 'absolute',
      right: '8px',
      top: '50%',
      transform: 'translateY(-50%)',
      background: showFilters ? '#000' : 'transparent',
      border: 'none',
      borderRadius: '10px',
      padding: '8px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.2s'
    }}
  >
    <Filter size={15} color={showFilters ? '#fff' : '#6b7280'} />
  </button>
</div>

{/* ✅ NEW: Smart Dynamic Filter Pills */}
{showFilters && (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginBottom: '12px'
  }}>
    {/* Mood Filters */}
    <div>
      <div style={{
        fontSize: '10px',
        fontWeight: '600',
        color: '#4091f5',
        marginBottom: '8px',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        textAlign:'left'
      }}>
        Moods
      </div>
      <div style={{
        display: 'flex',
        gap: '8px',
        overflowX: 'auto',
        paddingBottom: '4px',
        msOverflowStyle: 'none',
        scrollbarWidth: 'none'
      }}>
        <button
          onClick={() => {
            setFilterMood('all');
            applyFilters();
          }}
          style={{
            padding: '8px 16px',
            borderRadius: '20px',
            border: filterMood === 'all' ? '2px solid #000' : '2px solid #e5e7eb',
            background: filterMood === 'all' ? '#000' : '#fff',
            color: filterMood === 'all' ? '#fff' : '#6b7280',
            fontSize: '12px',
            fontWeight: '500',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            transition: 'all 0.2s'
          }}
        >
          All
        </button>
        {moodOptions.map(mood => (
          <button
            key={mood.value}
            onClick={() => {
              setFilterMood(mood.value);
              let filtered = [...entries];
              
              if (searchQuery.trim()) {
                const query = searchQuery.toLowerCase();
                filtered = filtered.filter(entry => {
                  const titleMatch = entry.title?.toLowerCase().includes(query);
                  const contentMatch = entry.content?.toLowerCase().includes(query);
                  const tagsMatch = entry.tags?.some(tag => tag.toLowerCase().includes(query));
                  return titleMatch || contentMatch || tagsMatch;
                });
              }
              
              filtered = filtered.filter(entry => {
                return entry.mood?.toLowerCase() === mood.value.toLowerCase() || 
                       entry.tags?.some(tag => tag.toLowerCase() === mood.value.toLowerCase());
              });
              
              setFilteredEntries(filtered);
            }}
            style={{
              padding: '8px 16px',
              borderRadius: '20px',
              border: filterMood === mood.value ? `2px solid ${moodColors[mood.value].border}` : '2px solid #e5e7eb',
              background: filterMood === mood.value ? moodColors[mood.value].bg : '#fff',
              color: filterMood === mood.value ? moodColors[mood.value].text : '#6b7280',
              fontSize: '12px',
              fontWeight: '500',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'all 0.2s'
            }}
          >
            {mood.label}
          </button>
        ))}
      </div>
    </div>

    {/* ✅ NEW: Dynamic Custom Tags Filter */}
    {(() => {
      // Get all unique custom tags from entries (excluding mood tags)
      const moodValues = moodOptions.map(m => m.value.toLowerCase());
      const allCustomTags = [...new Set(
        entries.flatMap(entry => 
          (entry.tags || []).filter(tag => 
            !moodValues.includes(tag.toLowerCase())
          )
        )
      )];

      // Get tag counts for sorting by popularity
      const tagCounts = {};
      entries.forEach(entry => {
        (entry.tags || []).forEach(tag => {
          if (!moodValues.includes(tag.toLowerCase())) {
            tagCounts[tag] = (tagCounts[tag] || 0) + 1;
          }
        });
      });

      // Sort by popularity (most used first)
      const sortedCustomTags = allCustomTags.sort((a, b) => 
        (tagCounts[b] || 0) - (tagCounts[a] || 0)
      );

      if (sortedCustomTags.length === 0) return null;

      return (
        <div>
          <div style={{
            fontSize: '10px',
            fontWeight: '600',
            color: '#4091f5',
            marginBottom: '8px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            textAlign:'left'
          }}>
            Your Tags
          </div>
          <div style={{
            display: 'flex',
            gap: '8px',
            flexWrap: 'wrap',
            maxHeight: '120px',
            overflowY: 'auto',
            paddingRight: '4px'
          }}>
            {sortedCustomTags.map(tag => (
              <button
                key={tag}
                onClick={() => {
                  // Filter by this custom tag
                  let filtered = entries.filter(entry => 
                    entry.tags?.some(t => t.toLowerCase() === tag.toLowerCase())
                  );

                  if (searchQuery.trim()) {
                    const query = searchQuery.toLowerCase();
                    filtered = filtered.filter(entry => {
                      const titleMatch = entry.title?.toLowerCase().includes(query);
                      const contentMatch = entry.content?.toLowerCase().includes(query);
                      return titleMatch || contentMatch;
                    });
                  }

                  setFilteredEntries(filtered);
                  setSearchQuery(tag); // Update search to show the tag
                }}
                style={{
                  padding: '6px 12px',
                  borderRadius: '16px',
                  border: '2px solid #e5e7eb',
                  background: searchQuery.toLowerCase() === tag.toLowerCase() ? '#f3f4f6' : '#fff',
                  color: '#374151',
                  fontSize: '11px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f9fafb';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = searchQuery.toLowerCase() === tag.toLowerCase() ? '#f3f4f6' : '#fff';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                #{tag}
                <span style={{
                  background: '#e5e7eb',
                  color: '#6b7280',
                  padding: '2px 6px',
                  borderRadius: '8px',
                  fontSize: '9px',
                  fontWeight: '600'
                }}>
                  {tagCounts[tag]}
                </span>
              </button>
            ))}
          </div>
        </div>
      );
    })()}

    {/* ✅ NEW: Clear All Filters Button */}
    {(searchQuery || filterMood !== 'all') && (
      <button
        onClick={() => {
          setSearchQuery('');
          setFilterMood('all');
          setFilteredEntries(entries);
        }}
        style={{
          padding: '8px 16px',
          background: '#e6e6e659',
          border: '1px solid #5c5c5c',
          borderRadius: '24px',
          color: 'black',
          fontSize: '12px',
          fontWeight: '600',
          cursor: 'pointer',
          transition: 'all 0.2s',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '6px'
        }}
      >
        <X size={14} />
        Clear All Filters
      </button>
    )}
  </div>
)}

      </div>

      {/* Scrollable Content */}
      <div style={{ 
        flex: 1, 
        overflowY: 'auto', 
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch',
        padding: '20px',
              animation: 'slideUp 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
      }}>
        {filteredEntries.length === 0 ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '60px 20px',
            textAlign: 'center',
            minHeight: '400px'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: '#f3f4f6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '20px'
            }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2">
                <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path>
              </svg>
            </div>
            <h3 style={{
              fontSize: '16px',
              fontWeight: '600',
              color: '#111827',
              marginBottom: '8px'
            }}>
              {searchQuery || filterMood !== 'all' ? 'No entries found' : 'No entries yet'}
            </h3>
            <p style={{
              fontSize: '14px',
              color: '#6b7280',
              lineHeight: '1.6',
              maxWidth: '300px'
            }}>
              {searchQuery || filterMood !== 'all' 
                ? 'Try adjusting your search or filters'
                : 'Start writing to capture your thoughts and memories'
              }
            </p>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '16px'
          }}>
  {filteredEntries.map((entry) => (
  <div
    key={entry.id}
 onClick={() => {
  if (isEntryLocked(entry.id)) {
    setCurrentLockAction({ entryId: entry.id, action: 'view' });
    setShowPinModal(true);
    setPinInput('');
  } else {
    setSelectedEntry(entry);
    setShowViewAllModal(false);
    setShowSingleEntry(true);
  }
}}
    style={{
      background: 'white',
      borderRadius: '16px',
      border: '1px solid #e5e7eb',
      overflow: 'hidden',
      cursor: 'pointer',
      transition: 'all 0.2s',
      position: 'relative'
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = 'translateY(-4px)';
      e.currentTarget.style.boxShadow = '0 12px 24px rgba(0, 0, 0, 0.1)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = 'none';
    }}
  >
    {/* Lock Button */}
    <button
      onClick={(e) => {
        e.stopPropagation();
        toggleLock(entry.id);
      }}
      className={`entry-lock-toggle ${isEntryLocked(entry.id) ? 'locked' : ''}`}
      style={{
        position: 'absolute',
        top: '12px',
        left: '12px',
        zIndex: 2
      }}
      title={isEntryLocked(entry.id) ? 'Unlock entry' : 'Lock entry'}
    >
      {isEntryLocked(entry.id) ? <Lock size={16} /> : <Unlock size={16} />}
    </button>

    {/* Lock Badge */}
    {isEntryLocked(entry.id) && (
      <div className="entry-lock-badge" style={{
        position: 'absolute',
        top: '12px',
        right: '12px',
        zIndex: 2
      }}>
        <Lock size={12} />
        Locked
      </div>
    )}

    {/* ✅ FIX: Display images in grid */}
    {(() => {
      const images = loadEntryImages(entry);
      if (images.length === 0) return null;
      
      return (
        <div style={{
          width: '100%',
          height: '140px',
          overflow: 'hidden',
          position: 'relative'
        }}>
          {images.length === 1 ? (
            <img 
              src={images[0].data} 
              alt={entry.title}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                filter: isEntryLocked(entry.id) ? 'blur(8px)' : 'none'
              }}
            />
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: images.length === 2 ? '1fr 1fr' : '2fr 1fr',
              gap: '2px',
              height: '100%'
            }}>
              {images.slice(0, 3).map((img, idx) => (
                <img 
                  key={idx}
                  src={img.data} 
                  alt={`${entry.title} ${idx + 1}`}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    filter: isEntryLocked(entry.id) ? 'blur(8px)' : 'none',
                    gridColumn: images.length === 3 && idx === 0 ? '1 / 2' : 'auto',
                    gridRow: images.length === 3 && idx === 0 ? '1 / 3' : 'auto'
                  }}
                />
              ))}
            </div>
          )}
        </div>
      );
    })()}

                {/* Content */}
                <div style={{ padding: '16px' }}>
                  {/* Date */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '12px',
                    color: '#6b7280',
                    marginBottom: '8px',
                  }}>
                    <Calendar size={12} />
                    {formatDate(entry.date)}
                  </div>

                  {/* Content Preview */}
                  {entry.content && (
                    <p style={{
                      fontSize: '13px',
                      color: '#6b7280',
                      lineHeight: '1.5',
                      marginBottom: '12px',
                                 textAlign:'left',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                                        animation: 'slideUp 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      overflow: 'hidden',
                      filter: entry.isLocked ? 'blur(4px)' : 'none'
                                          }}>
                      {entry.content}
                    </p>
                  )}

                  {/* Tags */}
                  {entry.tags && entry.tags.length > 0 && (
                    <div style={{
                      display: 'flex',
                      gap: '6px',
                      flexWrap: 'wrap'
                    }}>
                      {entry.tags.slice(0, 3).map((tag, index) => (
                        <span 
                          key={index}
                          style={{
                            padding: '4px 10px',
                            background: index === 0 ? '#fef3c7' : index === 1 ? '#dbeafe' : '#d1fae5',
                            color: index === 0 ? '#92400e' : index === 1 ? '#1e40af' : '#065f46',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: '500',
                          }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
    {/* Floating Write Button - Only shows in All Entries Modal */}
      {showViewAllModal && (
        <button
          className="floating-write-button"
          onClick={(e) => {
            e.stopPropagation();
            setShowViewAllModal(false);
            setShowAddModal(true);
          }}
          title="Write new entry"
        >
          <svg width="20" height="20" fill="none" viewBox="0 0 24 24">
            <path 
              stroke="white" 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth="2" 
              d="m14.304 4.844 2.852 2.852M7 7H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1v-4.5m2.409-9.91a2.017 2.017 0 0 1 0 2.853l-6.844 6.844L8 14l.713-3.565 6.844-6.844a2.015 2.015 0 0 1 2.852 0Z"
            />
          </svg>
        </button>
      )}
  </div>
)}

{/* SINGLE ENTRY VIEW MODAL */}
{showSingleEntry && selectedEntry && (
  <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(8px)',
    zIndex: 10000,
    display: 'flex',
    alignItems: 'flex-end',
    animation: 'fadeIn 0.3s ease-out'
  }}>
    <div style={{
      width: '100%',
      height: '88vh',
      background: 'white',
      borderRadius: '24px 24px 0 0',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      animation: 'slideUp 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
    }}>
      
      {/* Header with Close Button */}
      <div style={{
        padding: '20px',
        borderBottom: '1px solid #e5e7eb',
        background: '#fafafa',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <button
          onClick={() => {
            setShowSingleEntry(false);
            setSelectedEntry(null);
          }}
          style={{
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '50%',
            width: '30px',
            height: '30px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#f9fafb';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'white';
          }}
        >
          <X size={17} color="#807f7e" />
        </button>

        {/* Action Buttons - Delete and Edit on Same Line */}
        <div style={{
          display: 'flex',
          gap: '8px',
          alignItems: 'center'
        }}>
          {/* Delete Button */}
          <button
            onClick={() => {
              setEntryToDelete(selectedEntry.id);
              setShowDeleteConfirm(true);
            }}
            style={{
              padding: '9px 16px',
              background: 'white',
              border: '1px solid #b6b7b7ff',
              borderRadius: '20px',
              color: '#000000ff',
              fontSize: '13px',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#f87171';
              e.currentTarget.style.color = 'white';
              e.currentTarget.style.borderColor = '#f87171';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#b1b1b127';
              e.currentTarget.style.color = '#000000ff';
              e.currentTarget.style.borderColor = '#b6b7b7ff';
            }}
          >
            <Trash2 size={14} />
            Delete
          </button>

          {/* Edit Button */}
          <button
            onClick={() => {
              setSelectedEntry({
                ...selectedEntry,
                tagInput: '',
                tags: selectedEntry.tags || [],
                selectedImages: selectedEntry.selectedImages || [],
                images: selectedEntry.images || []
              });
              setShowSingleEntry(false);
              setShowEditModal(true);
            }}
            style={{
              background: 'black',
              border: 'none',
              borderRadius: '20px',
              padding: '9px 16px',
              color: 'white',
              fontSize: '13px',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#1a1a1a';
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'black';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
        <svg width="17" height="17" fill="none" viewBox="0 0 24 24">
          <path stroke="white" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="m14.304 4.844 2.852 2.852M7 7H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1v-4.5m2.409-9.91a2.017 2.017 0 0 1 0 2.853l-6.844 6.844L8 14l.713-3.565 6.844-6.844a2.015 2.015 0 0 1 2.852 0Z"/>
        </svg>
                    Edit
          </button>
        </div>
      </div>

      {/* Scrollable Content */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '24px'
      }}>
        
        {/* Images Grid */}
        {(() => {
          const images = loadEntryImages(selectedEntry);
          return images.length > 0 ? (
            <div 
              className={`edit-modal-images-grid ${getImageGridClass(images.length)}`}
              style={{ 
                marginBottom: '20px',
                display: 'grid',
                gap: '8px',
                gridTemplateColumns: images.length === 1 ? '1fr' : 
                                   images.length === 2 ? 'repeat(2, 1fr)' : 
                                   'repeat(2, 1fr)',
                gridAutoRows: '120px'
              }}
            >
              {images.map((image, idx) => (
                <div 
                  key={image.id || idx} 
                  className="entry-image-wrapper"
                  style={{
                    gridColumn: images.length === 3 && idx === 0 ? '1' : 'auto',
                    gridRow: images.length === 3 && idx === 0 ? 'span 2' : 'span 1'
                  }}
                >
                  <img src={image.data} alt="Entry" className="entry-image" />
                </div>
              ))}
            </div>
          ) : null;
        })()}

        {/* Title */}
        <h1 style={{
          fontSize: '24px',
          fontWeight: '600',
          color: '#1f2937',
          marginBottom: '16px',
          lineHeight: '1.3',
          textAlign: 'left'
        }}>
          {selectedEntry.title}
        </h1>

        {/* Date and Time */}
        <div style={{
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          fontSize: '13px',
          color: '#6b7280',
          paddingBottom: '16px',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Calendar size={14} />
            {new Date(selectedEntry.date).toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Clock size={14} />
            {new Date(selectedEntry.date).toLocaleTimeString('en-US', {
              hour: '2-digit',
              minute: '2-digit'
            })}
          </div>
        </div>

        {/* Content */}
        <div style={{
          fontSize: '15px',
          lineHeight: '1.8',
          color: '#374151',
          whiteSpace: 'pre-wrap',
          marginBottom: '24px',
          textAlign: 'left'
        }}>
          {selectedEntry.content}
        </div>

        {/* Tags */}
        {selectedEntry?.tags && selectedEntry.tags.length > 0 && (
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px'
          }}>
            {selectedEntry.tags.map((tag, index) => {
              const colorIndex = index % 3;
              const tagColor = tagColors[colorIndex];
              return (
                <div
                  key={index}
                  style={{
                    padding: '6px 12px',
                    background: tagColor.bg,
                    color: tagColor.color,
                    borderRadius: '12px',
                    fontSize: '11px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  {tag}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  </div>
)}

{/* PIN SETUP MODAL */}
{showPinSetup && (
  <div className="modal-overlay" onClick={() => { setShowPinSetup(false); setPinInput(''); }}>
    <div className="modal pin-modal-wrapper" onClick={(e) => e.stopPropagation()}>
      <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '12px', color: '#1f2937' }}>
       Set Up PIN
      </h3>
      <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '24px', lineHeight: '1.6' }}>
        Create a 4-digit PIN to protect your private entries
      </p>
      <input
        type="password"
        inputMode="numeric"
        maxLength="4"
        placeholder="• • • •"
        className="pin-modal-input"
        value={pinInput}
        onChange={(e) => setPinInput(e.target.value.replace(/\D/g, ''))}
        onKeyPress={(e) => {
          if (e.key === 'Enter' && pinInput.length === 4) {
            setupPin(pinInput);
          }
        }}
      />
      <div style={{ display: 'flex', gap: '12px' }}>
        <button onClick={() => { setShowPinSetup(false); setPinInput(''); }} className="cancel-button1">
          Cancel
        </button>
        <button
          onClick={() => setupPin(pinInput)}
          className="submit-button1"
          disabled={pinInput.length !== 4}
        >
          Set PIN
        </button>
      </div>
      
{/* In PIN Setup Modal - Only show if PIN already exists */}
{appPin && (
<button
        onClick={() => {
          setShowPinModal(false);
          setPinInput('');
          setCurrentLockAction(null);
          setShowEmergencyReset(true);
        }}
        style={{
          marginTop: '16px',
          width: '100%',
          padding: '10px',
          background: 'transparent',
          border: 'none',
          color: '#ef4444',
          fontSize: '12px',
          fontWeight: '500',
          cursor: 'pointer',
          textDecoration: 'underline',
          transition: 'color 0.2s ease'
        }}
        onMouseEnter={(e) => e.target.style.color = '#dc2626'}
        onMouseLeave={(e) => e.target.style.color = '#ef4444'}
      >
        Forgot PIN? Emergency Reset
      </button>
)}
    </div>
  </div>
)}
{showPinModal && (
  <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(8px)',
    zIndex: 99999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px'
  }} onClick={() => { setShowPinModal(false); setPinInput(''); setCurrentLockAction(null); }}>
    <div style={{
      background: 'white',
      borderRadius: '28px',
      padding: '24px',
      width: '100%',
      maxWidth: '400px',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
    }} onClick={(e) => e.stopPropagation()}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
        <Lock size={20} color="#272727" />
        <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937', margin: 0 }}>
          Enter PIN
        </h3>
      </div>
      <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '24px', lineHeight: '1.6' }}>
        Enter your 4-digit PIN to {currentLockAction?.action === 'lock' ? 'lock' : currentLockAction?.action === 'unlock' ? 'unlock' : 'view'} this entry
      </p>
      <input
        type="password"
        inputMode="numeric"
        maxLength="4"
        placeholder="• • • •"
        className="pin-modal-input"
        value={pinInput}
        onChange={(e) => setPinInput(e.target.value.replace(/\D/g, ''))}
        onKeyPress={(e) => {
          if (e.key === 'Enter' && pinInput.length === 4) {
            handlePinSubmit();
          }
        }}
      />
      <div style={{ display: 'flex', gap: '12px' }}>
        <button onClick={() => { setShowPinModal(false); setPinInput(''); setCurrentLockAction(null); }} className="cancel-button1">
          Cancel
        </button>
        <button onClick={handlePinSubmit} className="submit-button1" disabled={pinInput.length !== 4}>
          {currentLockAction?.action === 'lock' ? 'Lock' : currentLockAction?.action === 'unlock' ? 'Unlock' : 'View'}
        </button>
      </div>
      
<button
        onClick={() => {
          setShowPinModal(false);
          setPinInput('');
          setCurrentLockAction(null);
          setShowEmergencyReset(true);
        }}
        style={{
          marginTop: '16px',
          width: '100%',
          padding: '10px',
          background: 'transparent',
          border: 'none',
          color: '#ef4444',
          fontSize: '12px',
          fontWeight: '500',
          cursor: 'pointer',
          textDecoration: 'underline',
          transition: 'color 0.2s ease'
        }}
        onMouseEnter={(e) => e.target.style.color = '#dc2626'}
        onMouseLeave={(e) => e.target.style.color = '#ef4444'}
      >
        Forgot PIN? Emergency Reset
      </button>
    </div>
  </div>
)}
{showResetPin && appPin && ( 
  <div className="modal-overlay" onClick={cancelResetPin}>
    <div className="modal pin-modal-wrapper" onClick={(e) => e.stopPropagation()}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
        <RefreshCw size={20} color="#272727" />
        <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937', margin: 0 }}>
          Reset PIN
        </h3>
      </div>
      <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '24px', lineHeight: '1.6' }}>
        {resetPinStep === 1 && 'Enter your current PIN to continue'}
        {resetPinStep === 2 && 'Enter your new 4-digit PIN'}
        {resetPinStep === 3 && 'Confirm your new PIN'}
      </p>
      {resetPinStep === 1 && (
        <input
          type="password"
          inputMode="numeric"
          maxLength="4"
          placeholder="• • • •"
          className="pin-modal-input"
          value={oldPinInput}
          onChange={(e) => setOldPinInput(e.target.value.replace(/\D/g, ''))}
          onKeyPress={(e) => {
            if (e.key === 'Enter' && oldPinInput.length === 4) {
              handleResetPin();
            }
          }}
        />
      )}
      {resetPinStep === 2 && (
        <input
          type="password"
          inputMode="numeric"
          maxLength="4"
          placeholder="• • • •"
          className="pin-modal-input"
          value={newPinInput}
          onChange={(e) => setNewPinInput(e.target.value.replace(/\D/g, ''))}
          onKeyPress={(e) => {
            if (e.key === 'Enter' && newPinInput.length === 4) {
              handleResetPin();
            }
          }}
        />
      )}
      {resetPinStep === 3 && (
        <input
          type="password"
          inputMode="numeric"
          maxLength="4"
          placeholder="• • • •"
          className="pin-modal-input"
          value={confirmPinInput}
          onChange={(e) => setConfirmPinInput(e.target.value.replace(/\D/g, ''))}
          onKeyPress={(e) => {
            if (e.key === 'Enter' && confirmPinInput.length === 4) {
              handleResetPin();
            }
          }}
        />
      )}
      <div style={{ display: 'flex', gap: '12px' }}>
        <button onClick={cancelResetPin} className="cancel-button1">
          Cancel
        </button>
        <button
          onClick={handleResetPin}
          className="submit-button1"
          disabled={
            (resetPinStep === 1 && oldPinInput.length !== 4) ||
            (resetPinStep === 2 && newPinInput.length !== 4) ||
            (resetPinStep === 3 && confirmPinInput.length !== 4)
          }
        >
          {resetPinStep === 3 ? 'Confirm' : 'Next'}
        </button>
      </div>
      <div style={{ marginTop: '16px', fontSize: '11px', color: '#9ca3af', textAlign: 'center' }}>
        Step {resetPinStep} of 3
      </div>
    </div>
  </div>
)}

        </div>
      </div>
{showConfetti && (
  <Confetti
    width={windowSize.width}
    height={windowSize.height}
    numberOfPieces={200}
    recycle={false}
    gravity={0.3}
    colors={['#fbbf24', '#ef4444', '#3b82f6', '#10b981', '#8b5cf6', '#f59e0b']}
  />
)}
{/* Congratulations Modal for First Entry */}
{showCongratulationsModal && (
  <CongratulationsModal
    onClose={() => {
      setShowCongratulationsModal(false);
      if (entries.length > 1) {
        checkMilestones(entries.length);
      }
    }}
  />
)}

{/* ✅ NEW - Delete Confirmation Modal */}
{showDeleteConfirm && (
  <DeleteConfirmationModal
    onConfirm={() => {
      handleDeleteEntry(entryToDelete);
      setShowDeleteConfirm(false);
      setEntryToDelete(null);
    }}
    onCancel={() => {
      setShowDeleteConfirm(false);
      setEntryToDelete(null);
    }}
  />
)}

{/* ✅ NEW - Emergency PIN Reset Modal */}
{showEmergencyReset && (
  <EmergencyPINResetModal
    onReset={() => {
      localStorage.removeItem('journal_app_pin');
      localStorage.removeItem('journal_locked_entries');
      setAppPin('');
      setLockedEntries([]);
      setShowEmergencyReset(false);
      
      setTimeout(() => {
        const successDiv = document.createElement('div');
        successDiv.style.cssText = `
          position: fixed;
          top: 20px;
          left: 50%;
          transform: translateX(-50%);
          background: black;
          color: white;
          padding: 14px 24px;
          border-radius: 24px;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
          z-index: 999999;
          font-size: 14px;
          font-weight: 500;
        `;
        successDiv.textContent = 'PIN removed.';
        document.body.appendChild(successDiv);
        setTimeout(() => document.body.removeChild(successDiv), 3000);
      }, 100);
    }}
    onCancel={() => setShowEmergencyReset(false)}
  />
)}
    </>
  );
}
      